import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import supabase from '../config/supabase';
import { formatErrorMessage } from '../utils/formatErrorMessage';
import CycleSelector, { cycleToFriendlyLabel } from './CycleSelector';
import VendorAutocompleteInput from './VendorAutocompleteInput';
import { assignVendorToItem, clearVendorDirectoryCache } from '../utils/vendorResolution';
import {
  buildPendingFromSupport,
  buildPendingFromSetup,
  buildPendingTemplateClarify,
  buildPendingTemplateConvert,
  convertModeChips,
  convertModeFromReply,
  buildNextVendorPending,
  vendorChoiceChips,
  clarifyScopeChoices,
  resolveFollowup,
  isExpired as isPendingExpired,
  isCancel as isSupportCancel,
  isPlainShortAnswer,
  buildSupportFollowupPending,
} from '../utils/ciFollowups';
import {
  PRETTY_CATEGORY,
  proposeParsForItems,
  describeCounts,
  matchSetParsIntent,
  matchTemplatesToCommand,
  buildSetParMoqOps,
} from '../utils/alcoholParRules.mjs';
import {
  IMPORT_ACCEPT,
  ACCEPTED_FILES_SENTENCE,
  isAcceptedFilesQuestion,
  isImportAffirmative,
  isImportDecline,
  buildImportOfferPending,
  importOfferChips,
  IMPORT_OFFER_TEXT,
  buildTemplatesPending,
  templateOfferChips,
  mapImportedRow,
  newImportSession,
  summarizeImport,
  validateImportFiles,
  pastedImportOfferText,
} from '../utils/ciItemImport.mjs';
import {
  detectPastedTableData,
  buildPastedSource,
  validatePastedTable,
  extractTemplateDirective,
  collapseTableForClassify,
} from '../utils/ciPasteImport.mjs';
import { partitionNewFiles } from '../utils/fileDedupe';
import { launchTemplateSetup } from '../utils/templateSetupLaunch';
import {
  PRICE_UPDATE_ACCEPT,
  PRICE_UPDATE_FILES_SENTENCE,
  PRICE_UPDATE_OFFER_TEXT,
  priceUpdateOfferChips,
  buildPriceUpdateOfferPending,
  buildPriceUpdateRows,
  summarizeRowCounts,
  actionableRows,
  summarizePriceUpdate,
  describePriceChange,
} from '../utils/ciPriceUpdate.mjs';
import {
  SHEETJS_SRC,
  isSpreadsheetFile,
  workbookToCsvText,
  spreadsheetReaderUnavailableError,
  spreadsheetUnreadableError,
  spreadsheetEmptyError,
  describeAttachedFiles,
  describeFailedFiles,
} from '../utils/ciSpreadsheet.mjs';
import {
  isDedupAffirmative,
  isDedupDecline,
  matchDedupIntent,
  buildDedupOfferPending,
  dedupOfferChips,
  DEDUP_OFFER_TEXT,
} from '../utils/ciDedup.mjs';
import {
  matchScopedItemDelete,
  matchAttributeScopedItemDelete,
  matchLatestImportDelete,
  matchesLatestImportWording,
  splitScopePhrases,
  attributeScopeListLabel,
  scopeFromDatePhrase,
  ambiguousDateMessage,
  assumedYearNote,
  formatCreatedDate,
  formatCreatedInstant,
  describeInstantWindow,
  buildDeleteScopePending,
  deleteScopeChips,
  intentConfirmMessage,
  resolveDeleteScopeReply,
  SCOPED_DELETE_HINT,
  ATTRIBUTE_SCOPED_DELETE_HINT,
} from '../utils/ciItemDelete.mjs';
import { getCandidates, callPriceExtractWithMeta, callPriceMatch, summarizeInvoices, formatInvoiceDateLabel } from '../utils/priceUpdateMatching';
import { downloadReportCsv, emailReportSummary, printReportDocument } from './reports/ReportDriver';
import {
  isReportCancel,
  buildReportPending,
  isReportPendingExpired,
  inventoryCountChips,
  resolveInventoryCountReply,
  generateInventoryValueReport,
  generateInventoryTotalsReport,
  generateCountReviewReport,
  generateEopCogsReport,
  generateInventoryValueComparison,
  generateEopComparisonReport,
  purchaseHistoryChips,
  resolvePurchasePeriodReply,
  generatePurchaseHistoryReport,
  generatePurchaseItemHistoryReport,
  priceRangeChips,
  resolvePriceRangeReply,
  generatePriceChangeReport,
  getReportStoreName,
  getReportCreatedBy,
  formatCurrency,
} from '../utils/ciReports.mjs';
import {
  parseReportQuestion,
  parseDateRanges,
  listReportSessionsForAI,
  filterSessionsByNameMention,
  listEopReportsForAI,
  eopReportChips,
  resolveEopReportReply,
  reportKindChips,
  resolveReportKindReply,
  reportTypeChips,
  resolveReportTypeReply,
  prepareReportRunForAI,
} from '../utils/ciReportQuery.mjs';
import { buildCatalogLookup, classifyItem } from '../utils/importDedup';
import { recordPriceHistory } from '../utils/priceHistory';
import {
  extractPriceChangeRequest,
  findMatchingCatalogItem,
  buildPriceChangePreview,
  formatPriceChangeSummary,
  priceChangeDateChips,
  parsePastDate,
} from '../utils/ciPriceChangeChat.mjs';
import {
  normalizeItemFields,
  validateItemCreate,
  buildItemCreatePreview,
  itemCreateMissingMessage,
  itemCreateOptionalPrompt,
  itemCreateOptionalChips,
  ITEM_CREATE_FORM_FIELDS,
  buildItemCreateFormValues,
  findExistingLabel,
  dedupeLabels,
} from '../utils/ciItemCreate.mjs';
import {
  isSetupSurfacePath,
  suppressChatLink,
  SETUP_EMPTY_COPY,
  SETUP_TRY_PROMPTS,
} from '../utils/ciSetupSurface.mjs';
import {
  SIGNUP_REVIEW_MODE,
  SIGNUP_REVIEW_MESSAGE_KIND,
  SIGNUP_REVIEW_PROMPT,
  SIGNUP_REVIEW_INTRO,
  SIGNUP_REVIEW_DISMISS_REPLY,
  signupReviewChoices,
  signupReviewChoiceAction,
  drawerOpeningOffset,
} from '../utils/ciSignupReview.mjs';
import { surfaceForApi, viewportForApi, shouldOfferAction } from '../utils/ciPageContext';
import { directQuery, directUpdate } from '../utils/directFetch';
import { propagateGlobalCountFieldsToParents } from '../services/sessionFieldPropagation';
import {
  createOptionForItem,
  findInventoryItemAfterDuplicateMerge,
} from '../services/itemCrossReferenceService';

// Endpoints for the three brains: a lightweight classifier, plus the two
// battle-tested executors it routes to.
const classifyEndpoint = () => `${window.SUPABASE_URL}/functions/v1/counter-intelligence`;
const templatesEndpoint = () => `${window.SUPABASE_URL}/functions/v1/templates-ai-edit`;
const itemsEndpoint = () => `${window.SUPABASE_URL}/functions/v1/items-ai-bulk-edit`;
const itemsCleanupEndpoint = () => `${window.SUPABASE_URL}/functions/v1/items-ai-cleanup`;
const vendorsEndpoint = () => `${window.SUPABASE_URL}/functions/v1/vendor-ai-edit`;
const purchaseOrdersEndpoint = () => `${window.SUPABASE_URL}/functions/v1/purchase-order-ai-edit`;
const setupProgressEndpoint = () => `${window.SUPABASE_URL}/functions/v1/setup-progress`;
const invoiceProcessorEndpoint = () => `${window.SUPABASE_URL}/functions/v1/store-invoice-processor`;
// Changes a template's FORMAT between ordering and End of Period — convert in
// place or copy into the other format. Deterministic (no LLM): preview → apply.
const templatesConvertEndpoint = () => `${window.SUPABASE_URL}/functions/v1/templates-convert`;
// The same document-import engine the Items page uses: upload → AI extracts items
// into the catalog → preview → save. We drive it directly so the whole flow stays
// in the chat.
const menuStarterEndpoint = () => `${window.SUPABASE_URL}/functions/v1/menu-starter`;
// Same naming protocol the Count & Order setup flow uses (count.html →
// callAiSessionName): turns the template's vendors (ordering) or categories
// (End of Period) into a clean, human label.
const namingEndpoint = () => `${window.SUPABASE_URL}/functions/v1/price-update-ai`;

// The Vendors page publishes the vendor currently open in its editor to
// window.__ciActiveVendor so a bare "set the phone to …" / "rename this vendor"
// resolves to that record without the user having to name it.
const getActiveVendor = () => {
  try {
    const v = window.__ciActiveVendor;
    if (v && (v.id || v.display_name || v.name)) {
      return { id: v.id ?? null, display_name: v.display_name || v.name || null };
    }
  } catch (_) {}
  return null;
};

// The Count & Order template editor (count.html?view=count-creation) publishes
// the open template's live rows and an apply hook on `window` (see
// ItemLocationFolders: getCountCreationAiRows / applyAiPlanToCountCreation). When
// both are present we know a template is genuinely open, so an edit request like
// "copy the bar items to the upstairs bar" can be previewed and applied in place
// here — instead of bouncing the user to Count & Order → Manage groups, which is
// exactly where they already are. Mirrors the Vendors page's __ciActiveVendor
// surface. Returns null on every other page so template edits fall back to
// guidance rather than acting blindly.
const getActiveTemplate = () => {
  try {
    if (typeof window.getCountCreationAiRows !== 'function') return null;
    const path = window.location?.pathname || '';
    const params = new URLSearchParams(window.location?.search || '');
    if (!/count\.html$/.test(path) || params.get('view') !== 'count-creation') return null;
    const rows = window.getCountCreationAiRows() || [];
    const rawType = (params.get('type') || '').toLowerCase();
    const typeLabel = rawType === 'order'
      ? 'Ordering'
      : (rawType === 'end-of-period' || rawType === 'eop' || rawType === 'inventory')
        ? 'End of Period'
        : 'Count';
    return {
      // The session id of the open template (set by count.html on save). May be
      // null for a brand-new, never-saved template — edits still apply in-memory.
      id: window.currentSessionId ?? null,
      name: (params.get('sessionName') || '').trim() || 'this template',
      typeLabel,
      rows,
    };
  } catch (_) {
    return null;
  }
};

const buildAuthHeaders = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) {
      return {
        Authorization: `Bearer ${data.session.access_token}`,
        apikey: window.SUPABASE_KEY,
        'Content-Type': 'application/json',
      };
    }
  } catch (_) {}
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed?.access_token) {
        return {
          Authorization: `Bearer ${parsed.access_token}`,
          apikey: window.SUPABASE_KEY,
          'Content-Type': 'application/json',
        };
      }
    } catch (_) {}
  }
  throw new Error('Not authenticated. Please sign in and try again.');
};

const getStoreId = () => {
  const raw = Number.parseInt(localStorage.getItem('selectedStoreId'), 10);
  if (Number.isFinite(raw)) return raw;
  try {
    const stored = JSON.parse(localStorage.getItem('currentStore') || 'null');
    if (stored?.id) return Number(stored.id);
  } catch (_) {}
  return null;
};

// Distinct, trimmed, de-duplicated list (case-insensitive), capped — used to
// feed the naming protocol a tidy set of vendors / categories.
const distinctValues = (list, limit = 20) => {
  const seen = new Set();
  const out = [];
  (list || []).forEach((raw) => {
    const v = String(raw || '').trim();
    if (!v) return;
    const key = v.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(v);
  });
  return out.slice(0, limit);
};

// Run a freshly-built template through the shared naming protocol and return a
// proposed label. Ordering templates are named from their vendors; End of
// Period templates from their categories. Best-effort: any failure (or the
// function's own internal fallback) resolves to `fallback` so a name is always
// proposed and the user can still edit it.
const proposeTemplateName = async ({ typeLabel, items, fallback }) => {
  const safeFallback = (fallback && String(fallback).trim()) || 'New Template';
  try {
    const isEop = typeLabel === 'End of Period';
    const body = isEop
      ? { action: 'generate_session_name', kind: 'eop', categories: distinctValues((items || []).map((it) => it.category)) }
      : { action: 'generate_session_name', kind: 'order', vendors: distinctValues((items || []).map((it) => it.vendor)) };
    const headers = await buildAuthHeaders();
    const res = await fetch(namingEndpoint(), { method: 'POST', headers, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    const name = (data && typeof data.name === 'string') ? data.name.trim() : '';
    return name || safeFallback;
  } catch (_) {
    return safeFallback;
  }
};

// ── In-chat item import: on-demand engine + workflow plumbing ────────────────
// The drawer's launcher is on every page, but the import engine script
// (menu-starter-ui.js) only ships with the Items/Add-Items/Update-Prices pages.
// Load it on demand so we can reuse its file reader and its starter-template
// builder (createStarterCounts) from anywhere. Cached so repeated imports don't
// re-inject the tag.
const scriptLoadPromises = {};
const loadScriptOnce = (src) => {
  if (typeof document === 'undefined') return Promise.reject(new Error('No document.'));
  if (scriptLoadPromises[src]) return scriptLoadPromises[src];
  scriptLoadPromises[src] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing && existing.dataset.ciLoaded === 'true') { resolve(); return; }
    const el = existing || document.createElement('script');
    el.src = src;
    el.async = true;
    el.addEventListener('load', () => { el.dataset.ciLoaded = 'true'; resolve(); });
    el.addEventListener('error', () => { delete scriptLoadPromises[src]; reject(new Error(`Failed to load ${src}`)); });
    if (!existing) document.head.appendChild(el);
  });
  return scriptLoadPromises[src];
};

// Extract text from a PDF for the in-chat price update, loading pdf.js from the
// same CDN the Items page uses if it isn't already on the page. Returns '' when
// the library can't be loaded (the caller then surfaces a friendly error).
const PDFJS_SRC = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js';
const PDFJS_WORKER = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
const extractPdfTextForPrice = async (file) => {
  try {
    if (typeof window.pdfjsLib === 'undefined') {
      await loadScriptOnce(PDFJS_SRC);
      for (let i = 0; i < 40 && typeof window.pdfjsLib === 'undefined'; i += 1) {
        await new Promise((r) => setTimeout(r, 50));
      }
    }
    if (typeof window.pdfjsLib === 'undefined') return '';
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    const buf = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument(new Uint8Array(buf)).promise;
    let allText = '';
    for (let p = 1; p <= pdf.numPages; p += 1) {
      // eslint-disable-next-line no-await-in-loop
      const page = await pdf.getPage(p);
      // eslint-disable-next-line no-await-in-loop
      const tc = await page.getTextContent();
      allText += tc.items.map((it) => it.str).join(' ') + '\n';
    }
    return allText;
  } catch (_) {
    return '';
  }
};

// SheetJS, loaded the same on-demand way as pdf.js above. The import pages
// (add-items.html / items.html / invoice-inbox.html) ship it in a <script> tag;
// the chat lives on EVERY page, so it pulls the same build in only when an
// Excel file actually shows up.
const ensureXlsxLib = async () => {
  if (typeof window.XLSX !== 'undefined') return window.XLSX;
  try {
    await loadScriptOnce(SHEETJS_SRC);
    for (let i = 0; i < 40 && typeof window.XLSX === 'undefined'; i += 1) {
      await new Promise((r) => setTimeout(r, 50));
    }
  } catch (_) { /* fall through — caller surfaces a friendly error */ }
  return typeof window.XLSX !== 'undefined' ? window.XLSX : null;
};

// Read one uploaded Excel file into the same "uploaded source" shape the import
// engine produces for text files — rows as CSV, exactly what the Add Items
// page's importer sends. Throws named, user-facing errors so the upload card
// can say WHICH file failed.
const readSpreadsheetSource = async (file) => {
  const fileName = (file && file.name) || 'spreadsheet.xlsx';
  const lib = await ensureXlsxLib();
  if (!lib) throw spreadsheetReaderUnavailableError(fileName);
  let text = '';
  try {
    const workbook = lib.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });
    text = workbookToCsvText(workbook, lib);
  } catch (_) {
    throw spreadsheetUnreadableError(fileName);
  }
  if (!text.trim()) throw spreadsheetEmptyError(fileName);
  return { file_name: fileName, content_type: 'text/csv', text };
};

// menu-starter-ui.js reuses the host page's shared Supabase client via
// window.__MT_SHARED_SUPABASE__; on pages that never set one, hand it the
// drawer's authenticated client so its DB writes and price-update-ai calls run
// as the signed-in user.
const ensureSharedSupabase = () => {
  try { if (!window.__MT_SHARED_SUPABASE__) window.__MT_SHARED_SUPABASE__ = supabase; } catch (_) {}
};

// Returns window.MenuStarterUI (loading the script first if needed), or null if
// it can't be loaded — callers degrade gracefully (e.g. deep-link instead of
// auto-building templates).
const loadImportEngine = async () => {
  try {
    if (!(window.MenuStarterUI && typeof window.MenuStarterUI.readUploadedMenuFiles === 'function')) {
      await loadScriptOnce('/js/menu-starter-ui.js');
      for (let i = 0; i < 40 && !window.MenuStarterUI; i += 1) {
        await new Promise((r) => setTimeout(r, 50));
      }
    }
    ensureSharedSupabase();
    return window.MenuStarterUI || null;
  } catch (_) {
    return null;
  }
};

// Fallback file reader (used only when the engine script can't load). Mirrors the
// engine's "uploaded source" shape: images/PDFs as base64, everything else as
// text. No resize — the engine's reader does that; this is a safety net.
const IMPORT_IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'];
const fileToBase64 = async (file) => {
  const buf = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    binary += String.fromCharCode.apply(null, buf.subarray(i, i + chunk));
  }
  return btoa(binary);
};
const inlineReadImportFiles = async (files) => {
  const out = [];
  for (const file of Array.from(files || [])) {
    const name = file.name || 'upload';
    const lower = name.toLowerCase();
    const type = String(file.type || '').toLowerCase();
    const isImage = type.startsWith('image/') || IMPORT_IMAGE_EXTS.some((e) => lower.endsWith(e));
    if (isImage) {
      out.push({ file_name: name, content_type: type || 'image/jpeg', pdf_base64: await fileToBase64(file) });
    } else if (lower.endsWith('.pdf') || type.includes('pdf')) {
      out.push({ file_name: name, content_type: 'application/pdf', pdf_base64: await fileToBase64(file) });
    } else if (isSpreadsheetFile(file)) {
      out.push(await readSpreadsheetSource(file));
    } else {
      out.push({ file_name: name, content_type: type || 'text/plain', text: await file.text() });
    }
  }
  return out;
};
// Spreadsheets are converted to CSV rows here in the drawer, BEFORE anything
// reaches the engine — file.text() on an .xlsx is binary garbage, and the chat
// must not depend on which engine version the browser has cached. Everything
// else keeps the engine's richer reader (image resize, batch caps) with the
// inline reader as the no-engine fallback, in the original pick order.
const readImportFiles = async (files, engine) => {
  const list = Array.from(files || []);
  const sources = new Array(list.length).fill(null);
  const passthrough = [];
  const passthroughSlots = [];
  for (let i = 0; i < list.length; i += 1) {
    if (isSpreadsheetFile(list[i])) {
      // eslint-disable-next-line no-await-in-loop
      sources[i] = await readSpreadsheetSource(list[i]);
    } else {
      passthrough.push(list[i]);
      passthroughSlots.push(i);
    }
  }
  if (passthrough.length) {
    const read = (engine && typeof engine.readUploadedMenuFiles === 'function')
      ? await engine.readUploadedMenuFiles(passthrough)
      : await inlineReadImportFiles(passthrough);
    (read || []).forEach((source, j) => { sources[passthroughSlots[j]] = source; });
  }
  return sources.filter(Boolean);
};

// Start the document-import workflow for one source and poll until it finishes.
// Exact contract the Items page uses (start_discovery + skip_discovery + poll
// get_workflow). The edge function inserts the extracted items straight into
// inventory_items, so we read them back for the preview afterward.
const startAndPollImport = async (source, storeId, onProgress) => {
  const headers = await buildAuthHeaders();
  const startRes = await fetch(menuStarterEndpoint(), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      action: 'start_discovery',
      skip_discovery: true,
      source_type: 'document_import',
      uploaded_menu_sources: [source],
      store_id: storeId,
      source: 'import',
    }),
  });
  const startData = await startRes.json().catch(() => ({}));
  if (!startRes.ok || startData.ok === false || !startData.workflow_id) {
    throw new Error(formatErrorMessage(startData?.error, `The import couldn’t start (${startRes.status}).`));
  }
  const workflowId = startData.workflow_id;
  const maxPollMs = 6 * 60 * 1000;
  const intervalMs = 3000;
  const started = Date.now();
  let lastExtracted = 0;
  while (Date.now() - started < maxPollMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const pollRes = await fetch(menuStarterEndpoint(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ action: 'get_workflow', workflow_id: workflowId }),
    });
    if (!pollRes.ok) continue;
    const pollData = await pollRes.json().catch(() => ({}));
    const wf = pollData.workflow;
    if (!wf) continue;
    const prog = wf.metadata && wf.metadata.document_import_progress;
    if (prog) {
      lastExtracted = Number(prog.extracted_items_so_far) || lastExtracted;
      if (typeof onProgress === 'function') onProgress(lastExtracted);
    }
    if (wf.status === 'completed') {
      const items = wf.generated_items_preview || [];
      // generated_items_preview is capped (~150); the real inserted count comes
      // from the progress counter, so the readback limit can cover everything.
      return { items, extracted: Math.max(lastExtracted, items.length) };
    }
    if (wf.status === 'failed') throw new Error(wf.last_error_public || 'The AI couldn’t read that file.');
  }
  throw new Error('That file took too long to process. Try a smaller or clearer file.');
};

// Read back the rows the import just inserted (created since we started), in the
// compact preview shape. Same approach as add-items.html's review fetch.
const fetchImportedRows = async (storeId, startedAtIso, expected = 200) => {
  const limit = Math.max((expected || 0) + 30, 150);
  const { data, error } = await supabase
    .from('inventory_items')
    .select('id, description, brand, category, location, vendor, pack_size, unit, case_quantity, split_quantity, unit_price, case_price, manufacturer_code, created_at')
    .eq('store_id', storeId)
    .gte('created_at', startedAtIso)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message || 'Could not load the imported items for review.');
  return (data || []).map(mapImportedRow);
};

// Human labels for a template's format, used in convert preview cards.
const FORMAT_LABEL = { ordering: 'Ordering', end_of_period: 'End of Period', count: 'Count' };
const formatPhrase = (format) =>
  format === 'ordering' ? 'an ordering template'
    : format === 'end_of_period' ? 'an End of Period template'
      : 'the other format';
// The current template's format from the editor's URL type label (getActiveTemplate).
const formatFromTypeLabel = (typeLabel) =>
  typeLabel === 'Ordering' ? 'ordering'
    : typeLabel === 'End of Period' ? 'end_of_period'
      : null; // a plain count template — let the backend pick the target

let messageSeq = 0;
const nextId = () => { messageSeq += 1; return `m${messageSeq}`; };

// ── Per-user chat persistence ────────────────────────────────────────────────
// The drawer is destroyed on every full page navigation, so its React state is
// lost — reopening it on the next page showed an empty chat. Persist the
// conversation to localStorage keyed by user id so it follows the user across
// pages and windows and they can come back to ask follow-up questions.
const getChatUserId = () => {
  try {
    const cu = JSON.parse(localStorage.getItem('currentUser') || 'null');
    if (cu?.id) return String(cu.id);
  } catch (_) {}
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) {
      try {
        const p = JSON.parse(localStorage.getItem(k));
        if (p?.user?.id) return String(p.user.id);
      } catch (_) {}
    }
  }
  return 'anon';
};
const chatStorageKey = () => `ci_chat_v1_${getChatUserId()}`;
const MAX_PERSISTED_MESSAGES = 60;

const loadPersistedMessages = () => {
  try {
    const raw = localStorage.getItem(chatStorageKey());
    const saved = raw ? JSON.parse(raw) : null;
    if (Array.isArray(saved) && saved.length) {
      // Keep messageSeq ahead of restored ids so new messages get unique keys.
      let maxN = 0;
      saved.forEach((m) => {
        const n = parseInt(String(m?.id || '').replace(/^m/, ''), 10);
        if (Number.isFinite(n) && n > maxN) maxN = n;
      });
      if (maxN > messageSeq) messageSeq = maxN;
      return saved;
    }
  } catch (_) {}
  return [];
};

// ── Pending follow-up context ────────────────────────────────────────────────
// When the assistant asks a clarifying question ("…one combined template or
// separate by vendor?"), we stash a small structured context so the user's next
// short reply ("yes", "order template", "combined") resolves against it instead
// of being re-classified from scratch. Keyed by user + store so it doesn't leak
// across logins or stores; expires after 30 minutes (handled in ciFollowups).
const pendingStorageKey = () => `ci_pending_v1_${getChatUserId()}_${getStoreId() ?? 'nostore'}`;

const loadPendingContext = () => {
  try {
    const raw = localStorage.getItem(pendingStorageKey());
    const saved = raw ? JSON.parse(raw) : null;
    if (saved && typeof saved === 'object' && !isPendingExpired(saved)) return saved;
  } catch (_) {}
  return null;
};

// The starter prompts shown behind an empty chat — one for each of the jobs
// people reach for first: review the setup, update prices, build a template.
// Clicking one drops it into the input. Kept deliberately short — the copy above
// summarizes the full range, so this is a nudge to start, not a catalog.
const TRY_PROMPTS = [
  'Review my setup — what should I fix next?',
  'Update my prices from this invoice',
  'Create an order template for Sysco',
];

const isVendorSurface = () => /vendor-editor/i.test(window.location?.pathname || '');

// The Add Items onboarding page (add-items.html) — a brand-new account's final
// setup step. The rest of the app redirects back there until real items exist
// (or setup is skipped), so the drawer adapts: setup-flavored empty state, no
// dead link buttons, and the support answerer is told where the user is.
const isSetupSurface = () => isSetupSurfacePath(window.location?.pathname || '');

// Where the user is asking from, and whether an answer should carry an action
// offer. The surface string is the whole basis of the assistant's page
// awareness: the server looks it up in the shared page registry
// (supabase/functions/_shared/pageContext.mjs) to describe what is on screen.
// See utils/ciPageContext.js and docs/ai-page-context.md.

// ── Support / navigation intent layer ───────────────────────────────────────
// Known how-to / "where do I" questions are answered directly with the app path
// (and a deep link) BEFORE the classifier runs, so Counter Intelligence never
// loops on clarification for a clear support intent (e.g. "How do I add new
// users?").
//
// Guard: topics with requireNav:true only match question-style phrasing (NAV_CUE),
// so imperative action commands ("create a template for Sysco") still reach the
// build/edit executors, and readiness questions ("why can't I run EOP?") still
// reach Setup Review. User/team has no Counter Intelligence write action, so it
// is answered for any phrasing.
const NAV_CUE = /\b(how (?:do|can|would|to)|where (?:do|can|is|are))\b/i;

const SUPPORT_TOPICS = [
  {
    id: 'billing',
    requireNav: false,
    triggers: ['subscription', 'subscribe', 'cancel my plan', 'cancel plan', 'cancel my subscription',
      'cancel subscription', 'how much', 'pricing', 'price of', 'billing', 'free trial',
      'trial end', 'trial ends', 'days left', 'renew', 'renewal', 'upgrade', 'per store', 'per user'],
    answer: 'Your subscription is managed on the Subscription tab — it shows your plan, price, and trial days left or next renewal date, and is where you subscribe, switch monthly/yearly, update your card, or cancel. A subscription covers the whole store and all its users.',
    href: '/store-settings.html#subscription',
    label: 'Open Subscription settings →',
  },
  {
    id: 'team',
    requireNav: false,
    // No Counter Intelligence write action exists for team management, so the
    // canned path answer IS the full answer — serve it instantly (no model
    // round-trip) when no classification has run yet.
    instant: true,
    triggers: ['add user', 'add users', 'add a user', 'add new user', 'add a new user',
      'new user', 'new users', 'invite user', 'invite a user', 'invite someone',
      'invite teammate', 'invite a teammate', 'add teammate', 'add a teammate', 'invite staff',
      'manage team', 'team management', 'manage users', 'user management', 'store users',
      'remove user', 'remove a user', 'remove users', 'delete user', 'delete a user',
      'remove a team member', 'remove team member', 'remove teammate', 'remove a teammate',
      'remove member', 'remove staff', 'edit user', 'edit a user', 'edit users',
      'edit a team member', 'edit team member', 'user role', 'user roles', 'change role',
      'change their role', 'user permission', 'user permissions', 'team permission',
      'reset password', 'reset a password', 'reset their password', 'reset my password',
      'password reset', 'forgot password', 'forgot their password', 'change password'],
    answer: 'Go to Admin → Manage Stores and open the Team tab. Invite a new user by email with a role and permissions, or click a member to edit their role and permissions, send them a password reset email, or remove them from the store.',
    href: '/store-settings.html#team',
    label: 'Open Team settings →',
  },
  {
    id: 'relationships',
    requireNav: true,
    triggers: ['related product', 'product relationship', 'relationship suggestion',
      'suggested relationship', 'add item to a relationship', 'edit relationship',
      'create relationship', 'lowest comparable price'],
    answer: 'Go to Inventory → Items and open Related products above the Catalog. Search the alphabetical cards, use Edit to add or remove members, or open the Relationships workspace to review suggestions and create a relationship.',
    href: '/items.html',
    label: 'Open Related products →',
  },
  {
    id: 'ordering',
    requireNav: true,
    triggers: ['purchase planner', 'purchase plan', 'savings review', 'shopping guide', 'shopping list on my phone',
      'shopping list from my phone', 'scan shopping receipt', 'scan the receipt',
      'pickup shopping list'],
    answer: 'Compare vendors and finish each one in the Purchase Planner, reached from Purchasing → Purchase Planner. On a phone, open the shopping list itself — from the “list ready” notification or from Purchasing → Orders → Shopping Lists — to get the Shopping Guide you carry through the store; use Scan receipt there to reconcile what you actually bought.',
    href: '/ordering.html?view=savings-review',
    label: 'Open Purchase Planner →',
  },
  {
    id: 'reports',
    requireNav: true,
    triggers: ['what reports', 'which report', 'available reports', 'report is for',
      'inventory detail report', 'inventory value report', 'order report',
      'theoretical food cost', 'prep report'],
    answer: 'Go to Reports and open the card for the question you need to answer. Configure its date or completed-count inputs, generate it, then Download, Print, or Email it.',
    href: '/reports.html',
    label: 'Open Reports →',
  },
  {
    id: 'recipes',
    requireNav: true,
    triggers: ['menu and recipes', 'recipe creator', 'recipe costing', 'prepared item',
      'sub-recipe', 'sub recipe', 'print cookbook', 'build recipe'],
    answer: 'Open Recipes from the desktop sidebar or the phone\'s Recipes bottom-nav domain, then choose Menu and Recipes. Use Recipe Creator for dishes, Prepared items for countable batches, and Cookbook for printable recipes.',
    href: '/menu-costing-lab.html',
    label: 'Open Menu and Recipes →',
  },
  {
    id: 'templates',
    requireNav: true,
    triggers: ['create template', 'create a template', 'make a template', 'new template',
      'edit template', 'count template', 'order template', 'ordering template',
      'end of period template', 'eop template'],
    answer: 'Go to Inventory → Counts, then use New Count Template or New Order Template to build one, or Edit Template to change one.',
    href: '/count.html?view=overview',
    label: 'Open Counts →',
  },
  {
    id: 'prices',
    requireNav: true,
    triggers: ['update price', 'update prices', 'add price', 'add prices', 'edit price',
      'edit prices', 'change price', 'change prices', 'missing price', 'missing prices',
      'price list', 'prices from', 'track price', 'track my price', 'price history',
      'see how my price', 'how my prices changed', 'prices changed'],
    answer: 'Import a vendor invoice or price list and Countertop updates the prices that changed (matched items only), adds anything new, and leaves the rest — you review before it saves. Go to Purchasing → Invoices to import, or Inventory → Items to edit a price by hand or see an item’s price history. I can also do it right here — just upload an invoice.',
    href: '/items.html',
    label: 'Open Item Catalog →',
  },
  {
    id: 'pnl',
    requireNav: true,
    triggers: ['map p&l', 'map pnl', 'map p and l', 'p&l categor', 'pnl categor',
      'p and l categor', 'category mapping', 'map categor', 'map my categor'],
    answer: 'Go to Admin → Manage Stores (Store Settings) and open the P&L Categories tab.',
    href: '/store-settings.html#pnl',
    label: 'Open P&L Categories →',
  },
  {
    id: 'eop',
    requireNav: true,
    triggers: ['end of period', 'eop', 'close the month', 'close month',
      'see my cogs', 'see cogs', 'view cogs', 'run cogs'],
    answer: 'Go to Reports → End of Period.',
    href: '/reports.html',
    label: 'Open Reports →',
  },
  {
    id: 'vendors',
    requireNav: true,
    triggers: ['add a vendor', 'add vendor', 'new vendor', 'manage vendor', 'vendor contact',
      'vendor email', 'edit a vendor', 'find vendor'],
    answer: 'Go to Purchasing → Vendors to add or edit a vendor and their contact details.',
    href: '/vendor-editor.html',
    label: 'Open Vendors →',
  },
  {
    id: 'items',
    requireNav: true,
    triggers: ['import item', 'import items', 'add an item', 'upload item', 'item catalog',
      'import my item', 'import invoice', 'import order guide', 'add my items',
      'item list', 'my items', 'list of items', 'see my items', 'view my items',
      'find my items', 'my inventory list'],
    answer: 'Go to Inventory → Items and use the add-items panel above the table — “Import a file”, “Build from your menu”, or “Type them in”. First-time setup offers the same choices on Add Items.',
    href: '/items.html',
    label: 'Open Item Catalog →',
  },
];

const matchSupportTopic = (raw) => {
  const t = (raw || '').toLowerCase();
  const isNav = NAV_CUE.test(t);
  for (const topic of SUPPORT_TOPICS) {
    if (topic.requireNav && !isNav) continue;
    if (topic.triggers.some((kw) => t.includes(kw))) return topic;
  }
  return null;
};

// ── Template create card body (the in-chat mini-editor) ─────────────────────
// A scoped-down version of the setup-flow template editor (GroupEditor): it
// shows EVERY item the AI selected and lets the user fine-tune before creating —
// remove items, retarget a location, and set PAR (ordering only) — then review/
// edit the proposed name above the schedule selector. The item table scrolls
// horizontally so it stays usable inside the narrow chat drawer on mobile.
//
// The editable item table shared by the template-create card and the AI
// PAR-suggestion card — "the same review console" for PARs. Each row
// shows the item (with an optional sub-label / reason tooltip), an editable
// location, and — for ordering — an editable PAR with its unit. Purely
// presentational: the parent owns the items array and the update/remove handlers.
// (Vendor minimums are not edited here: they live on purchase_options.)
const ParMoqItemsTable = ({ items, editable, isOrdering, onUpdateItem, onRemoveItem, editableLocation = true }) => {
  const itemCount = items.length;
  const canEditLocation = editable && editableLocation;
  return (
    <>
      <div className="ci-tpl-editor" role="group" aria-label="Items (editable)">
        {/* table-layout:fixed + an explicit colgroup pins every column to a
            tab stop, so a long item name (truncated with an ellipsis) can
            never shove Location / PAR out of alignment. */}
        <table className="ci-tpl-table">
          <colgroup>
            {editable && <col style={{ width: 26 }} />}
            <col style={{ width: 124 }} />
            <col style={{ width: 108 }} />
            {isOrdering && <col style={{ width: 102 }} />}
          </colgroup>
          <thead>
            <tr>
              {editable && <th className="ci-tpl-th-rm" aria-label="Remove" />}
              <th>Item</th>
              <th>Location</th>
              {isOrdering && <th className="ci-tpl-th-num">PAR</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const sub = it.sub_label || it.vendor || it.category;
              return (
                <tr key={it.inventory_item_id ?? i}>
                  {editable && (
                    <td className="ci-tpl-rm-cell">
                      <button
                        type="button"
                        className="ci-tpl-rm"
                        title={`Remove ${it.item_name}`}
                        aria-label={`Remove ${it.item_name}`}
                        onClick={() => onRemoveItem(i)}
                      >×</button>
                    </td>
                  )}
                  <td className="ci-tpl-item-cell">
                    <div className="ci-tpl-name" title={it.item_name}>{it.item_name}</div>
                    {sub && (
                      <div className="ci-tpl-sub" title={it.reason || sub}>{sub}</div>
                    )}
                  </td>
                  <td>
                    {canEditLocation ? (
                      <input
                        className="ci-tpl-loc"
                        value={it.location}
                        onChange={(e) => onUpdateItem(i, { location: e.target.value })}
                        aria-label={`Location for ${it.item_name}`}
                      />
                    ) : (
                      <span className="ci-tpl-static" title={it.location}>{it.location}</span>
                    )}
                  </td>
                  {isOrdering && (
                    <td className="ci-tpl-num">
                      {editable ? (
                        <span className="ci-tpl-par-wrap">
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="ci-tpl-par"
                            value={it.par_level}
                            placeholder="—"
                            onChange={(e) => onUpdateItem(i, { par_level: e.target.value })}
                            aria-label={`PAR for ${it.item_name}`}
                          />
                          <span className="ci-tpl-unit" title={it.par_unit}>{it.par_unit}</span>
                        </span>
                      ) : (
                        <span className="ci-tpl-static">{it.par_level === '' ? '—' : `${it.par_level} ${it.par_unit}`}</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="ci-tpl-hint">
        All {itemCount} item{itemCount === 1 ? '' : 's'} shown · scroll sideways for {isOrdering ? 'location & PAR' : 'location'}{editable ? ' · edit inline, remove with ×' : ''}
      </p>
    </>
  );
};

// ── AI PAR-suggestion card body ─────────────────────────────────────────────
// Proposes starter PARs for the alcohol items in the open ordering
// template, in the same review console used for template creation. The user can
// edit any value, remove a row, then apply — which writes set_par into
// the live editor (no name or schedule here; the template already exists).
const ParSuggestCardBody = React.memo(({ card: c, messageId, onPatch }) => {
  const editable = c.status !== 'applying' && c.status !== 'done';
  const items = Array.isArray(c.items) ? c.items : [];
  const itemCount = items.length;
  const lowConf = items.filter((it) => (it.confidence ?? 1) < 0.7).length;

  const updateItem = (idx, patch) =>
    onPatch(messageId, { card: { ...c, items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) } });
  const removeItem = (idx) =>
    onPatch(messageId, { card: { ...c, items: items.filter((_, i) => i !== idx) } });

  return (
    <>
      <div className="ci-card-title">Suggested PARs · {c.templateName}</div>
      <div className="ci-card-meta">
        {itemCount} alcohol item{itemCount === 1 ? '' : 's'}{c.status !== 'done' ? ' · review & edit, then apply' : ''}
      </div>
      {itemCount === 0 ? (
        c.status !== 'done' && (
          <div className="ci-tpl-empty">No items left to set — every row was removed. Re-ask to start over.</div>
        )
      ) : (
        <ParMoqItemsTable
          items={items}
          editable={editable}
          isOrdering
          editableLocation={false}
          onUpdateItem={updateItem}
          onRemoveItem={removeItem}
        />
      )}
      {c.status !== 'done' && itemCount > 0 && (
        <p className="ci-tpl-hint">
          Starter values from category rules — replace them with usage-based PARs once you have order or count history.
          {lowConf ? ` ${lowConf} item${lowConf === 1 ? '' : 's'} marked low-confidence (under 70%) — double-check ${lowConf === 1 ? 'it' : 'them'}.` : ''}
        </p>
      )}
    </>
  );
});

// Memoized + driven by a stable (messageId, onPatch) pair so typing in one field
// only re-renders this card, not the whole conversation. All edits live on the
// card object; applyCard sends them to the server, which re-validates every id.
const TemplateCreateCardBody = React.memo(({ card: c, messageId, onPatch }) => {
  const patchCard = (next) => onPatch(messageId, { card: next });
  const editable = c.status !== 'applying' && c.status !== 'done';
  const isOrdering = c.typeLabel !== 'End of Period';
  const items = Array.isArray(c.items) ? c.items : [];
  const itemCount = items.length;

  const updateItem = (idx, patch) =>
    patchCard({ ...c, items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const removeItem = (idx) =>
    patchCard({ ...c, items: items.filter((_, i) => i !== idx) });

  return (
    <>
      <div className="ci-card-title">{(c.name && c.name.trim()) || 'New template'}</div>
      <div className="ci-card-meta">
        {c.typeLabel} · {itemCount} item{itemCount === 1 ? '' : 's'}
        {c.scheduleDescription ? ` · ${c.scheduleDescription}` : ''}
      </div>

      {c.status !== 'done' && (
        itemCount === 0 ? (
          <div className="ci-tpl-empty">No items left — every item was removed. Add at least one back (re-ask) before creating.</div>
        ) : (
          <ParMoqItemsTable
            items={items}
            editable={editable}
            isOrdering={isOrdering}
            onUpdateItem={updateItem}
            onRemoveItem={removeItem}
          />
        )
      )}

      {c.status !== 'done' && (
        <>
          <div className="ci-name-edit">
            <div className="ci-name-label" id={`ci-name-label-${messageId}`}>Template name</div>
            <input
              className="ci-name-input"
              value={c.name || ''}
              maxLength={80}
              disabled={!editable}
              placeholder="Name this template"
              aria-labelledby={`ci-name-label-${messageId}`}
              onChange={(e) => patchCard({ ...c, name: e.target.value })}
            />
          </div>
          <div className="ci-sched">
            <div className="ci-sched-label">Schedule <span className="ci-sched-opt">(optional)</span></div>
            <CycleSelector
              value={c.cycle || { preset: '' }}
              disabled={c.status === 'applying'}
              includeEopPresets={!isOrdering}
              onChange={(cycle) => patchCard({ ...c, cycle })}
            />
            {c.cycle && c.cycle.preset
              ? <div className="ci-sched-when">Repeats: <b>{cycleToFriendlyLabel(c.cycle)}</b></div>
              : <div className="ci-sched-hint">Pick when this {isOrdering ? 'order' : 'count'} should recur, or create without a schedule.</div>}
          </div>
        </>
      )}
      {c.status === 'done' && c.scheduleLabel && (
        <div className="ci-card-meta">Repeats: {c.scheduleLabel}</div>
      )}
    </>
  );
});

// ── Item import preview card body ───────────────────────────────────────────
// After a file is imported, the extracted items are already in the catalog. This
// card lets the user review them and remove anything they don't want before
// saving (saving deletes the removed rows). Memoized so removing one row doesn't
// re-render the whole conversation.
const ItemImportCardBody = React.memo(({ card: c, messageId, onPatch }) => {
  const items = Array.isArray(c.items) ? c.items : [];
  const total = Array.isArray(c.allIds) ? c.allIds.length : items.length;
  const removed = Math.max(0, total - items.length);
  const removeItem = (idx) =>
    onPatch(messageId, { card: { ...c, items: items.filter((_, i) => i !== idx) } });

  if (c.status === 'done') {
    return (
      <>
        <div className="ci-card-title">Imported items</div>
        <div className="ci-card-meta">{items.length} item{items.length === 1 ? '' : 's'} saved to your Item Catalog</div>
      </>
    );
  }

  return (
    <>
      <div className="ci-card-title">Review imported items</div>
      <div className="ci-card-meta">
        {items.length} item{items.length === 1 ? '' : 's'} to save{removed ? ` · ${removed} removed` : ''}
      </div>
      {items.length === 0 ? (
        <div className="ci-tpl-empty">You removed every item — nothing will be saved. Upload again to start over.</div>
      ) : (
        <div className="ci-import-list" role="group" aria-label="Imported items — remove any you don’t want">
          {items.map((it, i) => {
            const sub = [it.vendor, it.category, it.location].filter(Boolean).join(' · ');
            return (
              <div className="ci-import-row" key={it.id ?? i}>
                <button
                  type="button"
                  className="ci-tpl-rm"
                  title={`Remove ${it.item_name}`}
                  aria-label={`Remove ${it.item_name}`}
                  onClick={() => removeItem(i)}
                >×</button>
                <div className="ci-import-item">
                  <div className="ci-tpl-name" title={it.item_name}>{it.item_name}</div>
                  {sub && <div className="ci-tpl-sub" title={sub}>{sub}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {items.length > 0 && (
        <p className="ci-tpl-hint">
          These are in your Item Catalog now. Remove anything wrong with ×, then save — I’ll then offer to build count &amp; order templates from them.
        </p>
      )}
    </>
  );
});

// ── Deduplication / delete card body (review look-alike items, pick to archive) ─
// Shows groups of similar/variant items (same or near-identical names differing only
// by unit, pack, item number, spelling, or vendor) with a checkbox per item. Ticked
// items are archived (a pre-delete): removed from every template so future counts skip
// them, while counted history, orders, and reports keep them. Nothing until confirmed.
const DedupCardBody = React.memo(({ card: c, messageId, onPatch }) => {
  const groups = Array.isArray(c.groups) ? c.groups : [];
  const selected = new Set((c.selectedIds || []).map((v) => String(v)));
  const totalItems = groups.reduce((n, g) => n + ((g.items && g.items.length) || 0), 0);
  // 'scoped' = items selected by a hard filter (when they were added, or one
  // vendor/category/storage area); 'lookalike' = the fuzzy duplicate scan. The
  // card must never describe one as the other — a header reading "look-alike
  // items" over a date-filtered batch is exactly how a user ends up approving
  // 151 archives they didn't ask for.
  const scoped = c.mode === 'scoped';
  // 'attribute' = the hard filter was one vendor/category/storage area, so the
  // scope label is a full phrase ("from vendor Asia Imports") rather than a date.
  const attribute = scoped && c.scopeKind === 'attribute';
  const scopeLabel = String(c.scopeLabel || '').trim();
  const groupNoun = c.groupNoun === 'category'
    ? (groups.length === 1 ? 'category' : 'categories')
    : `vendor${groups.length === 1 ? '' : 's'}`;
  // Items whose delete will also break Related Products links (their active
  // purchase options / aliases / pending duplicate suggestions) — surfaced
  // BEFORE confirmation, per row and in the hint below.
  const relatedOf = (it) => (Number(it.related_products) || 0) + (Number(it.related_links) || 0);
  const relatedItems = groups.reduce(
    (n, g) => n + (g.items || []).filter((it) => relatedOf(it) > 0).length,
    0,
  );

  if (c.status === 'done') {
    const n = (c.selectedIds || []).length;
    return (
      <>
        <div className="ci-card-title">Archived items</div>
        <div className="ci-card-meta">{n} item{n === 1 ? '' : 's'} archived and removed from upcoming counts</div>
      </>
    );
  }

  const toggle = (id) => {
    const key = String(id);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    onPatch(messageId, { card: { ...c, selectedIds: [...next].map((v) => Number(v)) } });
  };

  // Every item id across all groups, so one control can tick or clear the whole
  // list instead of the user checking dozens of boxes one at a time.
  const allIds = groups.flatMap((g) => (g.items || []).map((it) => it.id));
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(String(id)));
  const toggleAll = () =>
    onPatch(messageId, { card: { ...c, selectedIds: allSelected ? [] : allIds.map((v) => Number(v)) } });
  const clearAll = () => onPatch(messageId, { card: { ...c, selectedIds: [] } });

  return (
    <>
      <div className="ci-card-title">
        {scoped
          ? (attribute ? `Review items ${scopeLabel}` : `Review items added ${scopeLabel || 'in that window'}`)
          : 'Review look-alike items'}
      </div>
      <div className="ci-card-meta">
        {scoped
          ? `${totalItems} item${totalItems === 1 ? '' : 's'} · ${groups.length} ${groupNoun} · ${selected.size} selected to delete`
          : `${groups.length} set${groups.length === 1 ? '' : 's'} · ${totalItems} item${totalItems === 1 ? '' : 's'} · ${selected.size} selected to delete`}
      </div>
      <div className="ci-dedup-toolbar">
        <label className="ci-dedup-selectall">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => { if (el) el.indeterminate = !allSelected && selected.size > 0; }}
            onChange={toggleAll}
            aria-label={scoped
              ? (attribute
                ? `Select all ${allIds.length} item${allIds.length === 1 ? '' : 's'} ${scopeLabel}`
                : `Select all ${allIds.length} item${allIds.length === 1 ? '' : 's'} added ${scopeLabel || 'in that window'}`)
              : `Select all ${allIds.length} look-alike item${allIds.length === 1 ? '' : 's'}`}
          />
          Select all
        </label>
        {selected.size > 0 && (
          <button type="button" className="ci-dedup-clear" onClick={clearAll}>Clear</button>
        )}
      </div>
      <div
        className="ci-dedup-groups"
        role="group"
        aria-label={scoped
          ? (attribute
            ? `Items ${scopeLabel} — tick the ones to delete`
            : `Items added ${scopeLabel || 'in that window'} — tick the ones to delete`)
          : 'Look-alike items — tick the ones to delete'}
      >
        {groups.map((g) => (
          <div className="ci-dedup-group" key={g.group_id}>
            <div className="ci-dedup-group-label" title={g.label}>
              {scoped ? g.label : `Looks like: ${g.label}`}
            </div>
            {(g.items || []).map((it) => {
              // Sub-day windows show the clock — on an hour-scoped review every
              // row's bare date is identical and says nothing.
              const added = scoped && !attribute
                ? (c.timeScoped ? formatCreatedInstant(it.created_at) : formatCreatedDate(it.created_at))
                : '';
              const sub = [
                it.unit && `unit: ${it.unit}`,
                scoped ? null : it.vendor,
                it.item_number && `#${it.item_number}`,
                added && `added ${added}`,
              ].filter(Boolean).join(' · ');
              const related = relatedOf(it);
              const usage = [
                it.in_templates ? `in ${it.in_templates} template${it.in_templates === 1 ? '' : 's'}` : null,
                it.counted ? `counted ${it.counted}×` : null,
                it.ordered ? `ordered ${it.ordered}×` : null,
                related ? `${related} related link${related === 1 ? '' : 's'} to remove` : null,
              ].filter(Boolean).join(' · ');
              const isSel = selected.has(String(it.id));
              return (
                <label className={`ci-dedup-row${isSel ? ' selected' : ''}`} key={it.id}>
                  <input type="checkbox" checked={isSel} onChange={() => toggle(it.id)} />
                  <div className="ci-import-item">
                    <div className="ci-tpl-name" title={it.item_name}>{it.item_name}</div>
                    {sub && <div className="ci-tpl-sub" title={sub}>{sub}</div>}
                    {usage && <div className="ci-dedup-usage" title={usage}>{usage}</div>}
                  </div>
                </label>
              );
            })}
          </div>
        ))}
      </div>
      <p className="ci-tpl-hint">
        {scoped ? (attribute ? ATTRIBUTE_SCOPED_DELETE_HINT : SCOPED_DELETE_HINT) : <>Tick the items to delete. Deleting <b>archives</b> them and removes them from your templates so future counts skip them — your past counts, orders, and reports still keep them, and they can be restored. Nothing changes until you confirm.</>}
        {relatedItems > 0 && <> {relatedItems} of these items {relatedItems === 1 ? 'is' : 'are'} linked in Related Products — those links will be removed (the other items and all history stay).</>}
      </p>
    </>
  );
});

// ── Price update card body (review proposed price changes before saving) ─────
// Shows the proposed changes grouped: price changes (old → new), new items, and
// unchanged items (for transparency). The user can remove any actionable row
// before approving. Mirrors the Update Prices page's approval screen, in chat.
const PriceUpdateCardBody = React.memo(({ card: c, messageId, onPatch }) => {
  const rows = Array.isArray(c.rows) ? c.rows : [];
  const counts = summarizeRowCounts(rows);
  const removeRow = (id) =>
    onPatch(messageId, { card: { ...c, rows: rows.filter((r) => r.id !== id) } });

  if (c.status === 'done') {
    return (
      <>
        <div className="ci-card-title">Prices updated</div>
        <div className="ci-card-meta">
          {c.applied?.updated || 0} updated · {c.applied?.added || 0} added · {c.applied?.skipped || 0} unchanged
        </div>
      </>
    );
  }

  const priceChanges = rows.filter((r) => r._classification === 'price_change');
  const newItems = rows.filter((r) => r._classification === 'new');
  const unchanged = rows.filter((r) => r._classification === 'unchanged');
  const actionable = priceChanges.length + newItems.length;

  const invoices = summarizeInvoices(rows);

  return (
    <>
      <div className="ci-card-title">Review price changes</div>
      {invoices.length === 1 ? (
        <div className="ci-card-meta">
          {[
            invoices[0].vendor,
            invoices[0].invoiceNumber && `Invoice #${invoices[0].invoiceNumber}`,
            invoices[0].invoiceDate ? formatInvoiceDateLabel(invoices[0].invoiceDate) : 'no invoice date',
          ].filter(Boolean).join(' · ')}
        </div>
      ) : invoices.length > 1 ? (
        <div className="ci-card-meta">{invoices.length} invoices</div>
      ) : null}
      <div className="ci-card-meta">
        {counts.priceChanges} price change{counts.priceChanges === 1 ? '' : 's'} · {counts.newItems} new · {counts.unchanged} unchanged
      </div>

      {priceChanges.length > 0 && (
        <div className="ci-price-group" role="group" aria-label="Price changes — remove any you don’t want">
          <div className="ci-price-group-label">Price changes</div>
          {priceChanges.map((r) => {
            const sub = [r.vendor, r.unit].filter(Boolean).join(' · ');
            return (
              <div className="ci-import-row" key={r.id}>
                <button
                  type="button"
                  className="ci-tpl-rm"
                  title={`Skip ${r.description}`}
                  aria-label={`Skip ${r.description}`}
                  onClick={() => removeRow(r.id)}
                >×</button>
                <div className="ci-import-item">
                  <div className="ci-tpl-name" title={r.description}>
                    {r.description}{r.fuzzyMatched ? ' ·' : ''}
                    {r.fuzzyMatched && <span className="ci-price-fuzzy">fuzzy match</span>}
                  </div>
                  {sub && <div className="ci-tpl-sub" title={sub}>{sub}</div>}
                </div>
                <div className="ci-price-delta">{describePriceChange(r)}</div>
              </div>
            );
          })}
        </div>
      )}

      {newItems.length > 0 && (
        <div className="ci-price-group" role="group" aria-label="New items — remove any you don’t want">
          <div className="ci-price-group-label">New items</div>
          {newItems.map((r) => {
            const sub = [r.vendor, r.unit].filter(Boolean).join(' · ');
            return (
              <div className="ci-import-row" key={r.id}>
                <button
                  type="button"
                  className="ci-tpl-rm"
                  title={`Skip ${r.description}`}
                  aria-label={`Skip ${r.description}`}
                  onClick={() => removeRow(r.id)}
                >×</button>
                <div className="ci-import-item">
                  <div className="ci-tpl-name" title={r.description}>{r.description}</div>
                  {sub && <div className="ci-tpl-sub" title={sub}>{sub}</div>}
                </div>
                <div className="ci-price-delta">{r.newPrice != null ? `$${Number(r.newPrice).toFixed(2)}` : '—'}</div>
              </div>
            );
          })}
        </div>
      )}

      {unchanged.length > 0 && (
        <div className="ci-price-group">
          <div className="ci-price-group-label">Unchanged ({unchanged.length}) — already at this price, nothing to do</div>
        </div>
      )}

      {actionable === 0 ? (
        <div className="ci-tpl-empty">No price changes to apply — everything already matches your catalog. Upload another invoice to try again.</div>
      ) : (
        <p className="ci-tpl-hint">
          Remove anything you don’t want with ×, then approve. I’ll update the changed prices and add any new items — completed counts and past reports aren’t affected.
        </p>
      )}
    </>
  );
});

// ── Rendered report card body ────────────────────────────────────────────────
// A finished report, in chat: the same descriptor the Reports page builds, shown
// as a compact summary (stats + a short preview of the first section) with the
// download / email / print actions wired to the shared ReportDriver. The full
// table is intentionally truncated here — the buttons (and the Reports link)
// hand off the complete data.
const REPORT_PREVIEW_ROWS = 6;
const ReportCardBody = React.memo(({ card: c }) => {
  const d = c.descriptor || {};
  const report = d.report || {};
  const section = (d.sections && d.sections[0]) || { columns: [], rows: [] };
  const columns = section.columns || [];
  const rows = section.rows || [];
  const previewRows = rows.slice(0, REPORT_PREVIEW_ROWS);
  const hiddenCount = Math.max(0, rows.length - previewRows.length);

  return (
    <>
      <div className="ci-card-title">{report.title}</div>
      <div className="ci-card-meta">{report.subtitle}</div>
      {c.summary && <div className="ci-report-summary">{c.summary}</div>}

      {Array.isArray(d.stats) && d.stats.length > 0 && (
        <div className="ci-report-stats">
          {d.stats.map((s) => (
            <div className="ci-report-stat" key={s.label}>
              <span className="ci-report-stat-label">{s.label}</span>
              <span className="ci-report-stat-value">{s.value}</span>
            </div>
          ))}
        </div>
      )}

      {!c.empty && previewRows.length > 0 && (
        <div className="ci-report-table-wrap" role="group" aria-label={`${report.title} preview`}>
          <table className="ci-report-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className={col.align !== 'text' ? 'ci-report-num' : undefined}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={col.align !== 'text' ? 'ci-report-num' : undefined}
                      style={row._style || undefined}
                      title={String(row[col.key] ?? '')}
                    >
                      {row[col.key] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hiddenCount > 0 && (
        <div className="ci-report-more">+{hiddenCount} more row{hiddenCount === 1 ? '' : 's'} — download or email for the full report</div>
      )}

      {!c.empty && (
        <div className="ci-card-actions ci-report-actions">
          <button type="button" className="ci-btn ci-btn-primary" onClick={() => downloadReportCsv(c.descriptor, c.csvFilename)}>Download CSV</button>
          <button type="button" className="ci-btn ci-btn-secondary" onClick={() => emailReportSummary(c.descriptor, c.emailLines || [])}>Email</button>
          <button type="button" className="ci-btn ci-btn-secondary" onClick={() => printReportDocument(c.descriptor)}>Print</button>
        </div>
      )}
    </>
  );
});

// One category/location field in the item-create form. It shows the store's
// EXISTING values as a dropdown (so the assistant's choice can be corrected to a
// value already in use) with an "Add new…" escape hatch — the point is to reuse
// "Beer" rather than mint "Beers". When the store has no existing values yet, or
// the user is adding a new one, it falls back to a plain text input.
function CatalogFieldControl({ label, required, value, options, busy, onChange }) {
  const opts = useMemo(() => dedupeLabels(options), [options]);
  const hasOptions = opts.length > 0;
  const matchedExisting = useMemo(() => findExistingLabel(value, opts), [value, opts]);
  // Start on the dropdown when the proposed value is one the store already uses
  // (or is blank); start on the text input when it's a genuinely new value, or
  // when there are no existing values to pick from.
  const [addingNew, setAddingNew] = useState(() => (hasOptions ? !!(value && !matchedExisting) : true));
  const showSelect = hasOptions && !addingNew;
  const lower = label.toLowerCase();

  return (
    <label className={`ci-form-field ${required ? 'required' : ''}`}>
      <span className="ci-form-label">
        {label}
        {required && <span className="ci-form-required">*</span>}
      </span>
      {showSelect ? (
        <select
          className="ci-form-input"
          value={matchedExisting || ''}
          required={required}
          disabled={busy}
          onChange={(e) => {
            if (e.target.value === '__add_new__') { setAddingNew(true); onChange(''); }
            else onChange(e.target.value);
          }}
        >
          <option value="" disabled>{`Select ${lower}…`}</option>
          {opts.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
          <option value="__add_new__">{`＋ Add new ${lower}…`}</option>
        </select>
      ) : (
        <>
          <input
            className="ci-form-input"
            type="text"
            value={value}
            placeholder={`New ${lower}`}
            required={required}
            disabled={busy}
            onChange={(e) => onChange(e.target.value)}
          />
          {hasOptions && (
            <button
              type="button"
              className="ci-form-link"
              disabled={busy}
              onClick={() => { setAddingNew(false); onChange(''); }}
            >
              Choose an existing {lower} instead
            </button>
          )}
        </>
      )}
    </label>
  );
}

// The in-chat "add one item" form. Category/location reuse existing values via a
// dropdown (CatalogFieldControl); vendor uses the alias-aware autocomplete; the
// rest are plain inputs. Controlled so the assistant's pre-filled values can be
// corrected before saving. Calls onSubmit with the assembled values object.
function ItemCreateForm({ initialValues, facets, storeId, busy, onSubmit, onCancel }) {
  const [values, setValues] = useState(() => ({ ...initialValues }));
  const setField = useCallback((key, val) => setValues((prev) => ({ ...prev, [key]: val })), []);

  const renderField = (def) => {
    if (def.key === 'category' || def.key === 'location') {
      return (
        <CatalogFieldControl
          key={def.key}
          label={def.label}
          required={def.required}
          value={values[def.key] ?? ''}
          options={def.key === 'category' ? facets?.category : facets?.location}
          busy={busy}
          onChange={(v) => setField(def.key, v)}
        />
      );
    }
    if (def.key === 'vendor') {
      return (
        <label key={def.key} className="ci-form-field">
          <span className="ci-form-label">{def.label}</span>
          <div className="vendor-cell">
            <VendorAutocompleteInput
              value={values.vendor ?? ''}
              onChange={(v) => setField('vendor', v)}
              storeId={storeId}
              className="ci-form-input"
              placeholder="Vendor"
            />
          </div>
        </label>
      );
    }
    return (
      <label key={def.key} className={`ci-form-field ${def.required ? 'required' : ''}`}>
        <span className="ci-form-label">
          {def.label}
          {def.required && <span className="ci-form-required">*</span>}
        </span>
        <input
          className="ci-form-input"
          type={def.type}
          value={values[def.key] ?? ''}
          placeholder={def.label}
          required={def.required}
          disabled={busy}
          step={def.type === 'number' ? '0.01' : undefined}
          onChange={(e) => setField(def.key, e.target.value)}
        />
      </label>
    );
  };

  return (
    <form
      className="ci-form"
      onSubmit={(e) => { e.preventDefault(); onSubmit(values); }}
    >
      <div className="ci-form-grid">
        {ITEM_CREATE_FORM_FIELDS.map(renderField)}
      </div>
      <div className="ci-form-actions">
        <button type="submit" className="ci-btn ci-btn-primary" disabled={busy}>Add item</button>
        <button type="button" className="ci-btn" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

const CounterIntelligenceDrawer = () => {
  const [open, setOpen] = useState(false);
  const [signupOpening, setSignupOpening] = useState(false);
  const [introVisible, setIntroVisible] = useState(false);
  const [messages, setMessages] = useState(loadPersistedMessages);
  const [command, setCommand] = useState('');
  const [busy, setBusy] = useState(false);
  const pendingRef = useRef(undefined);
  if (pendingRef.current === undefined) pendingRef.current = loadPendingContext();
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const drawerRef = useRef(null);
  const launcherRef = useRef(null);
  const signupOpeningTimerRef = useRef(null);
  const signupHandoffFocusRef = useRef(false);
  // Hidden file <input> driving the in-chat import, plus the id of the upload
  // card so file-picker progress updates land on the right message.
  const fileInputRef = useRef(null);
  const uploadCardIdRef = useRef(null);
  // The in-chat price update has its own hidden <input> + upload card so a price
  // import and an item import can't collide on the same picker / progress card.
  const priceFileInputRef = useRef(null);
  const priceUploadCardIdRef = useRef(null);
  // On the setup surface (Add Items onboarding), link buttons to other app pages
  // are dead ends until real items exist — the onboarding gate bounces them back
  // here. Flips true the moment this chat creates real items (import save /
  // single add), which is exactly when the gate opens and links start working.
  const setupUnlockedRef = useRef(false);
  // Always points at the latest startTemplateConvert so the
  // 'counter-intelligence:convert-template' event (fired by count.html's
  // "Transition to order template" header button) can start the flow without a
  // stale closure.
  const startConvertRef = useRef(null);
  // Always points at the latest handleSend so the 'counter-intelligence:ask'
  // event (fired by the AI Command Center's hero input) can submit without a
  // stale closure.
  const handleSendRef = useRef(null);
  // Structured help context carried by a ui_help_tooltip ask (see
  // utils/helpTopics.js): consumed by the support answer payload of the turn
  // that carries it, then cleared so later turns don't inherit it.
  const helpContextRef = useRef(null);

  // While the post-import template-setup overlay (public/js/template-setup-chat.js)
  // is live on the Count page, it owns the single CI star badge — this generic
  // drawer must hide its own fab and refuse to open so the two assistants never
  // stack. The overlay sets window.__templateSetupActive + body.tsc-setup-active
  // and fires counter-intelligence:suppress / :release.
  const [suppressed, setSuppressed] = useState(
    typeof window !== 'undefined' && !!window.__templateSetupActive,
  );

  // Docked mode: a host surface (the AI Command Center) adopts this chat into
  // its layout as the page's ONLY chat window. Docked means permanently open —
  // no overlay, no close button, no Escape-to-dismiss and no launcher fab —
  // because there is nothing to dismiss back to. Off everywhere else.
  const [docked, setDocked] = useState(
    typeof window !== 'undefined' && !!window.__ciDocked,
  );

  const finishSignupOpening = useCallback(() => {
    if (signupOpeningTimerRef.current) clearTimeout(signupOpeningTimerRef.current);
    signupOpeningTimerRef.current = null;
    drawerRef.current?.style.removeProperty('--ci-signup-open-x');
    drawerRef.current?.style.removeProperty('--ci-signup-open-y');
    setSignupOpening(false);
  }, []);

  // The template-setup drawer already collapses into the CI launcher when it
  // closes. This is that motion in reverse for the completed-signup handoff:
  // begin at the visible launcher's center, then expand into the real drawer.
  // The ordinary open/close path keeps its existing side-slide transition.
  const openFromSignupHandoff = useCallback(() => {
    signupHandoffFocusRef.current = true;
    let reduced = false;
    try {
      reduced = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    } catch (_) { /* default to motion */ }

    const drawer = drawerRef.current;
    const launcher = launcherRef.current;
    if (!reduced && drawer && launcher) {
      const offset = drawerOpeningOffset({
        drawerWidth: drawer.offsetWidth,
        drawerHeight: drawer.offsetHeight,
        launcherRect: launcher.getBoundingClientRect(),
        viewportWidth: window.innerWidth || document.documentElement.clientWidth,
      });
      if (offset) {
        drawer.style.setProperty('--ci-signup-open-x', `${offset.x}px`);
        drawer.style.setProperty('--ci-signup-open-y', `${offset.y}px`);
        setSignupOpening(true);
        if (signupOpeningTimerRef.current) clearTimeout(signupOpeningTimerRef.current);
        signupOpeningTimerRef.current = setTimeout(finishSignupOpening, 400);
      }
    }
    setOpen(true);
  }, [finishSignupOpening]);

  const closeDrawer = useCallback(() => {
    signupHandoffFocusRef.current = false;
    finishSignupOpening();
    setOpen(false);
  }, [finishSignupOpening]);

  useEffect(() => () => {
    if (signupOpeningTimerRef.current) clearTimeout(signupOpeningTimerRef.current);
  }, []);

  useEffect(() => {
    const isSuppressed = () => (typeof window !== 'undefined' && !!window.__templateSetupActive);
    const onOpen = () => { if (isSuppressed()) return; setOpen(true); };
    const onToggle = () => {
      if (isSuppressed()) return;
      signupHandoffFocusRef.current = false;
      finishSignupOpening();
      setOpen((v) => !v);
    };
    // Programmatic close, used when a chat link launches the fly-to animation
    // (the drawer gets out of the way). Ignored while docked — the docked chat
    // is the page itself, so it stays put and only the tool animates.
    const onCloseEvt = () => { if (!window.__ciDocked) closeDrawer(); };
    // Setup coaching: the AI Command Center opens the chat on sign-in with a
    // progress card (until setup completes). The card is fully built by the
    // caller (utils/setupCoach.js); any earlier coach card is replaced so
    // progress never shows stale or duplicated.
    const onSetupCoach = (e) => {
      if (isSuppressed()) return;
      const card = e?.detail?.card;
      if (!card || card.kind !== 'setup_coach') return;
      setOpen(true);
      setMessages((prev) => [
        ...prev.filter((m) => !(m.card && m.card.kind === 'setup_coach')),
        { id: nextId(), role: 'assistant', card },
      ]);
    };
    // Open + optionally prefill the input from anywhere via:
    //   window.dispatchEvent(new CustomEvent('counter-intelligence:prefill', { detail: { text } }))
    // Never auto-submits — the user reviews/sends.
    const onPrefill = (e) => {
      if (isSuppressed()) return;
      setOpen(true);
      const text = e?.detail?.text;
      if (typeof text === 'string' && text) setCommand(text);
    };
    // Open + submit in one step via:
    //   window.dispatchEvent(new CustomEvent('counter-intelligence:ask', { detail: { text } }))
    // Unlike prefill, this DOES send — it exists for surfaces where the user
    // already typed and submitted the text themselves (e.g. the AI Command
    // Center hero input), so re-confirming in the drawer would be a dead click.
    // Help tooltips (utils/helpTopics.js) add structured detail fields:
    //   { text, source, helpTopicId, sourceLabel, helpContext }
    // The sourceLabel renders above the user message ("Asked from Templates
    // help"), the help-topic ID is preserved on the message, and helpContext
    // is forwarded to the support answerer as help_context.
    const onAsk = (e) => {
      if (isSuppressed()) return;
      setOpen(true);
      const d = e?.detail || {};
      const text = typeof d.text === 'string' ? d.text.trim() : '';
      if (text && typeof handleSendRef.current === 'function') {
        handleSendRef.current(text, {
          source: d.source,
          helpTopicId: d.helpTopicId,
          sourceLabel: d.sourceLabel,
          helpContext: d.helpContext,
        });
      }
    };
    // The editor's "Transition to order template" header button fires this with
    // the open template's id/name/format so we can start the convert-or-copy flow
    // immediately (no typing required).
    const onConvert = (e) => {
      if (isSuppressed()) return;
      setOpen(true);
      const detail = (e && e.detail) || {};
      if (typeof startConvertRef.current === 'function') startConvertRef.current(detail);
    };
    // Template-setup overlay coordination: hide + close while it's active.
    const onSuppress = () => { setSuppressed(true); closeDrawer(); };
    const onRelease = () => { setSuppressed(false); };
    // Docking coordination with a host surface that renders this chat inline.
    const onDock = () => { setDocked(true); setOpen(true); };
    const onUndock = () => setDocked(false);
    window.addEventListener('counter-intelligence:open', onOpen);
    window.addEventListener('counter-intelligence:toggle', onToggle);
    window.addEventListener('counter-intelligence:prefill', onPrefill);
    window.addEventListener('counter-intelligence:ask', onAsk);
    window.addEventListener('counter-intelligence:close', onCloseEvt);
    window.addEventListener('counter-intelligence:setup-coach', onSetupCoach);
    window.addEventListener('counter-intelligence:convert-template', onConvert);
    window.addEventListener('counter-intelligence:suppress', onSuppress);
    window.addEventListener('counter-intelligence:release', onRelease);
    window.addEventListener('counter-intelligence:dock', onDock);
    window.addEventListener('counter-intelligence:undock', onUndock);
    // The host may have docked before this drawer mounted (script order).
    if (window.__ciDocked) { setDocked(true); setOpen(true); }
    // Announce readiness for static launchers — add-items.html ships one in
    // plain HTML so the badge is visible before this bundle arrives; it hides
    // itself on this signal — and honor a click that landed before mount.
    window.__ciDrawerReady = true;
    try { window.dispatchEvent(new CustomEvent('counter-intelligence:ready')); } catch (_) { /* non-fatal */ }
    if (window.__ciOpenQueued && !isSuppressed()) {
      window.__ciOpenQueued = false;
      setOpen(true);
    }
    return () => {
      window.removeEventListener('counter-intelligence:open', onOpen);
      window.removeEventListener('counter-intelligence:toggle', onToggle);
      window.removeEventListener('counter-intelligence:prefill', onPrefill);
      window.removeEventListener('counter-intelligence:ask', onAsk);
      window.removeEventListener('counter-intelligence:close', onCloseEvt);
      window.removeEventListener('counter-intelligence:setup-coach', onSetupCoach);
      window.removeEventListener('counter-intelligence:convert-template', onConvert);
      window.removeEventListener('counter-intelligence:suppress', onSuppress);
      window.removeEventListener('counter-intelligence:release', onRelease);
      window.removeEventListener('counter-intelligence:dock', onDock);
      window.removeEventListener('counter-intelligence:undock', onUndock);
    };
  }, [closeDrawer, finishSignupOpening]);

  // Deep link: a link or email can open Counter Intelligence on page load with
  //   ?openCounterIntelligence=setup-review   (or =1)
  // The automatic new-signup template handoff uses =signup-review. It opens
  // with an introduction and an explicit opt-in; choosing Yes calls the same
  // read-only Setup Review used everywhere else. The param is stripped so a
  // refresh or back-button cannot replay either handoff.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('openCounterIntelligence')) return;
      const mode = params.get('openCounterIntelligence');
      if (mode === SIGNUP_REVIEW_MODE) {
        setMessages((prev) => [
          ...prev.filter((message) => message.kind !== SIGNUP_REVIEW_MESSAGE_KIND),
          {
            id: nextId(),
            role: 'assistant',
            kind: SIGNUP_REVIEW_MESSAGE_KIND,
            text: SIGNUP_REVIEW_INTRO,
            choices: signupReviewChoices(),
          },
        ]);
        openFromSignupHandoff();
      } else {
        setOpen(true);
      }
      if (mode === 'setup-review' || mode === '1' || mode === '' || mode === 'true') {
        setCommand(SIGNUP_REVIEW_PROMPT);
      }
      params.delete('openCounterIntelligence');
      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    } catch (_) { /* non-fatal */ }
  }, [openFromSignupHandoff]);

  // First-time discovery: briefly reveal the launcher's label so new users
  // (especially on touch, where there's no hover) notice the corner button and
  // learn what it is. Shown once per browser, then it stays a quiet badge.
  useEffect(() => {
    let timer;
    try {
      if (!localStorage.getItem('ci_fab_intro_seen_v1')) {
        setIntroVisible(true);
        localStorage.setItem('ci_fab_intro_seen_v1', '1');
        timer = setTimeout(() => setIntroVisible(false), 4500);
      }
    } catch (_) { /* storage unavailable — skip the intro */ }
    return () => { if (timer) clearTimeout(timer); };
  }, []);

  useEffect(() => {
    if (!open) return;
    // This invitation opens without a user gesture. Keep mobile keyboards
    // closed and screen-reader focus at the dialog instead of skipping the
    // introduction to land in the composer. Wait until the expansion settles;
    // reduced-motion opens have no expansion and focus immediately.
    if (signupHandoffFocusRef.current) {
      if (signupOpening) return;
      try { drawerRef.current?.focus({ preventScroll: true }); } catch (_) {}
      signupHandoffFocusRef.current = false;
      return;
    }
    // preventScroll: docked, the chat opens with the page, and scrolling a
    // freshly focused input into view would drop the reader past the greeting
    // and the chat's own header on short (landscape phone) viewports.
    if (inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [open, signupOpening]);

  useEffect(() => {
    // Docked, the chat is the page — Escape has nothing to dismiss it to.
    const onKey = (e) => { if (e.key === 'Escape' && open && !docked) closeDrawer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, docked, closeDrawer]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  // Persist the conversation (per user) so it survives page navigation / reopen.
  useEffect(() => {
    try {
      const key = chatStorageKey();
      if (!messages.length) { localStorage.removeItem(key); return; }
      localStorage.setItem(key, JSON.stringify(messages.slice(-MAX_PERSISTED_MESSAGES)));
    } catch (_) { /* storage full or unavailable — non-fatal */ }
  }, [messages]);

  const pushMessage = useCallback((msg) => {
    const id = nextId();
    let next = msg;
    // Single funnel for the setup-surface link rule: while the user is still
    // locked into the Add Items step, drop link buttons that would only bounce
    // them back here (see ciSetupSurface.mjs).
    if (next?.link && suppressChatLink({
      onSetupSurface: isSetupSurface(),
      unlocked: setupUnlockedRef.current,
      href: next.link.href,
    })) {
      const { link: _dropped, ...rest } = next;
      next = rest;
    }
    // On the AI Command Center surface, tapping a chat link flies the user to
    // the destination (the matching orbit button animates open, then the page
    // loads) — make the answer offer that explicitly.
    if (next?.link?.href && next.role === 'assistant'
        && typeof window !== 'undefined' && window.__aiCommandCenterActive
        && /^\//.test(next.link.href)
        && typeof next.text === 'string' && next.text
        && !/open it for you/i.test(next.text)) {
      // The trailing newline drops the link button onto its own line
      // (.ci-msg is white-space: pre-wrap).
      next = { ...next, text: `${next.text}\n\nWant me to open it for you? Tap the button below and I’ll take you there.\n` };
    }
    setMessages((prev) => [...prev, { id, ...next }]);
    return id;
  }, []);

  const updateMessage = useCallback((id, patch) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  const setPending = useCallback((pending) => {
    pendingRef.current = pending || null;
    try {
      if (pending) localStorage.setItem(pendingStorageKey(), JSON.stringify(pending));
      else localStorage.removeItem(pendingStorageKey());
    } catch (_) { /* storage unavailable — non-fatal */ }
  }, []);

  const clearPending = useCallback(() => setPending(null), [setPending]);

  const clearChat = useCallback(() => {
    setMessages([]);
    clearPending();
    try { localStorage.removeItem(chatStorageKey()); } catch (_) {}
  }, [clearPending]);

  // A compact transcript of the recent user/assistant turns, sent with each
  // classify call so the assistant can follow a longer discussion — short replies
  // like "yes, do that" or "delete those" are read against what was just said
  // instead of being classified cold. This is the PRIOR conversation only; the
  // current message rides along separately as `command`. The edge function trims
  // it further (last few turns), so ~12 here is a safe upper bound.
  const recentConversationForClassify = () => messages
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && typeof m.text === 'string' && m.text.trim())
    .slice(-12)
    // A pasted table stays in the transcript, but history hands the router its
    // collapsed form (prose + first rows + a row-count note) so every later
    // turn isn't re-reading 30 rows of vendor data.
    .map((m) => ({ role: m.role, text: collapseTableForClassify(m.text, detectPastedTableData(m.text)) }));

  // A compact description of the currently-open thread (an offer the assistant made,
  // or a question it asked) for the model-first router, so the model — not brittle
  // client heuristics — decides whether the next message continues it or starts a new
  // request. Offer pendings are yes/no; the rest are open questions.
  const OFFER_PENDING_KINDS = new Set(['pending_item_import', 'pending_price_update', 'pending_dedup', 'pending_par_suggest']);
  const describePendingForRouter = (pending) => {
    if (!pending || typeof pending !== 'object') return null;
    const offer = OFFER_PENDING_KINDS.has(pending.kind) || pending.awaiting === 'offer';
    const question = pending.assistant_question || pending.question || pending.assistant_message || '';
    const intent = pending.intent || pending.support_intent || pending.template_type || null;
    return { kind: pending.kind || null, offer: !!offer, intent, question: String(question || '') };
  };

  // One classify call = the model-first router: it returns the turn-taking decision
  // (dialog: continue|new, offer_response) AND the underlying intent/route, given the
  // recent conversation and the open thread. Used both to gate follow-ups and to route
  // a fresh request (the same result is reused, so a new request costs one call).
  const classifyMessage = async (text, pending) => {
    const activeVendor = getActiveVendor();
    const activeTemplate = getActiveTemplate();
    const res = await post(classifyEndpoint(), {
      action: 'classify',
      // The router reads intent, not rows: a pasted table travels collapsed
      // (prose + first rows + "…about N rows" note). The import itself always
      // uses the full original text.
      command: collapseTableForClassify(text, detectPastedTableData(text)),
      has_open_template: !!activeTemplate,
      surface: surfaceForApi(),
      viewport: viewportForApi(),
      // When the ask came from a ⓘ help button, the topic id names the exact
      // control — that is a question ABOUT the app, never a command, and it is
      // what keeps "What is an inventory item?" out of the file-import route.
      help_topic_id: helpContextRef.current?.help_topic_id || null,
      active_vendor: activeVendor?.display_name || null,
      history: recentConversationForClassify(),
      pending: describePendingForRouter(pending),
    });
    return res.classification;
  };

  const post = async (url, body) => {
    const headers = await buildAuthHeaders();
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok || payload?.ok === false || payload?.error) {
      throw new Error(formatErrorMessage(payload?.error, `Request failed (${res.status})`));
    }
    return payload;
  };

  // ── In-chat item import ─────────────────────────────────────────────────────
  // Offer → upload → run the document-import engine → preview → save → build
  // starter templates → recap. We keep a small per-run "session" record (files,
  // item count, templates) so the closing recap reflects the whole multi-step run.
  const offerItemImport = (text) => {
    setPending(buildImportOfferPending({ text }));
    pushMessage({ role: 'assistant', text: IMPORT_OFFER_TEXT, choices: importOfferChips() });
  };

  const promptFileUpload = () => {
    const id = pushMessage({ role: 'assistant', card: { kind: 'item_import_upload', status: 'idle' } });
    uploadCardIdRef.current = id;
  };

  const onImportFilesChosen = (e) => {
    const picked = e?.target?.files ? Array.from(e.target.files) : [];
    if (e?.target) e.target.value = ''; // let the user re-pick the same file later
    if (picked.length) runItemImport(picked);
  };

  const runItemImport = async (files) => {
    // A drag-and-drop can carry the same file twice (a file dialog cannot) —
    // read each document once.
    const list = partitionNewFiles([], Array.from(files || [])).added;
    // Every early exit must say something — a silently dropped pick leaves the
    // card stuck on "Choose another file" with no sign the file ever attached.
    if (busy) {
      pushMessage({ role: 'assistant', text: 'One moment — I’m still finishing the previous step. Tap “Choose a file” again when it’s done.' });
      return;
    }
    const check = validateImportFiles(list);
    if (!check.ok) {
      pushMessage({ role: 'assistant', text: check.error, tone: 'error' });
      return;
    }
    const storeId = getStoreId();
    if (!storeId) {
      pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store, then re-upload.' });
      return;
    }
    // Progress lands on the picker card the user clicked; if that card is gone
    // (page reload, cleared chat), create one on the fly so reading/error states
    // are never invisible.
    let cardId = uploadCardIdRef.current;
    const setUpload = (patch) => {
      const card = { kind: 'item_import_upload', ...patch };
      if (cardId) { updateMessage(cardId, { card }); return; }
      cardId = pushMessage({ role: 'assistant', card });
      uploadCardIdRef.current = cardId;
    };
    const attached = describeAttachedFiles(list);
    setBusy(true);
    try {
      // Confirm the attach immediately — before any engine/network work — so a
      // retry after a rejection visibly took the new file.
      setUpload({ status: 'processing', note: `Attached ${attached} — reading…` });
      const engine = await loadImportEngine();
      let sources;
      try {
        sources = await readImportFiles(list, engine);
      } catch (readErr) {
        throw new Error(formatErrorMessage(readErr, `I couldn’t read ${attached}. Try a PDF, spreadsheet, or photo.`));
      }
      const fileNames = list.map((f) => f.name).filter(Boolean);
      await importSourcesToPreview({
        sources,
        fileNames,
        storeId,
        setUpload,
        emptyNote: `I didn’t find any items in ${attached}. Try a clearer product list, invoice, or spreadsheet.`,
        failNote: (failed) => `I couldn’t read ${describeFailedFiles(failed)}. Try a clearer product list, invoice, or spreadsheet.`,
      });
    } catch (err) {
      setUpload({ status: 'error', note: formatErrorMessage(err, 'Something went wrong while importing.') });
    } finally {
      setBusy(false);
    }
  };

  // Shared tail of both import paths — uploaded files and the pasted list:
  // run every source through the document-import workflow, read back the rows
  // it inserted, and post the review card. `emptyNote` / `failNote` keep the
  // error wording path-specific ("that file" vs "what you pasted"), and a
  // template `directive` carried in the original message rides the session so
  // the save step can honor it.
  const importSourcesToPreview = async ({ sources, fileNames, storeId, setUpload, emptyNote, failNote, directive }) => {
    const startedAt = new Date().toISOString();
    // Per-source: a failing source must not abort the batch (its predecessors
    // have already inserted rows into the catalog). Accumulate the real
    // extracted count so the readback limit covers every source, and after the
    // loop ALWAYS review what landed — mirroring add-items.html's importFile().
    let totalExtracted = 0;
    const failedFiles = [];
    for (let i = 0; i < sources.length; i += 1) {
      const fileLabel = sources[i].file_name ? `“${sources[i].file_name}”` : `file ${i + 1}`;
      const label = sources.length > 1 ? `${fileLabel} (${i + 1} of ${sources.length})` : fileLabel;
      const soFar = totalExtracted;
      setUpload({ status: 'processing', note: `Reading ${label}…` });
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await startAndPollImport(sources[i], storeId, (n) => {
          const seen = soFar + n;
          setUpload({ status: 'processing', note: `Reading ${label} — found ${seen} item${seen === 1 ? '' : 's'} so far…` });
        });
        totalExtracted += (res && Number.isFinite(res.extracted)) ? res.extracted : 0;
      } catch (fileErr) {
        failedFiles.push(sources[i].file_name || `file ${i + 1}`);
      }
    }
    const rows = await fetchImportedRows(storeId, startedAt, totalExtracted);
    if (!rows.length) {
      setUpload({
        status: 'error',
        note: failedFiles.length ? failNote(failedFiles) : emptyNote,
      });
      return false;
    }
    setUpload({ status: 'done', note: `Found ${rows.length} item${rows.length === 1 ? '' : 's'}.` });
    const okNames = (fileNames || []).filter((n) => !failedFiles.includes(n));
    const session = newImportSession({ fileNames: okNames.length ? okNames : (fileNames || []) });
    if (directive) session.directive = directive;
    const lead = `I found ${rows.length} item${rows.length === 1 ? '' : 's'}. Review them below, remove anything you don’t want, then save.`;
    pushMessage({
      role: 'assistant',
      text: failedFiles.length ? `I couldn’t read ${describeFailedFiles(failedFiles)}, but ${lead.charAt(0).toLowerCase()}${lead.slice(1)}` : lead,
      card: { kind: 'item_import', storeId, items: rows, allIds: rows.map((r) => r.id), session, status: 'pending' },
    });
    return true;
  };

  // The pasted-list variant of the import: the user's chat message IS the
  // document. Same engine, same preview → save → templates flow — only the
  // source and the progress copy differ. Runs inside handleSend's turn, which
  // already holds `busy`, so there is no busy guard of its own.
  const runItemImportFromText = async ({ detection, directive }) => {
    const check = validatePastedTable(detection);
    if (!check.ok) {
      pushMessage({ role: 'assistant', text: check.error, tone: 'error' });
      return;
    }
    const storeId = getStoreId();
    if (!storeId) {
      pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store, then paste your list again.' });
      return;
    }
    // Progress gets its own fresh card — there is no picker card on this path,
    // and the file flow's card ref must stay untouched.
    let cardId = null;
    const setUpload = (patch) => {
      const card = { kind: 'item_import_upload', ...patch };
      if (cardId) { updateMessage(cardId, { card }); return; }
      cardId = pushMessage({ role: 'assistant', card });
    };
    const rowsWord = `${detection.rowCount} row${detection.rowCount === 1 ? '' : 's'}`;
    try {
      setUpload({ status: 'processing', note: `Reading your pasted list (${rowsWord})…` });
      await importSourcesToPreview({
        sources: [buildPastedSource(detection.tableText)],
        fileNames: [],
        storeId,
        setUpload,
        emptyNote: 'I couldn’t find any items in what you pasted. Check it’s one item per row (vendor, description, pack, price work best) and paste it again — or upload the file instead.',
        failNote: () => 'I couldn’t read your pasted list. Check it’s one item per row and paste it again — or upload the file instead.',
        directive,
      });
    } catch (err) {
      setUpload({ status: 'error', note: formatErrorMessage(err, 'Something went wrong while importing.') });
    }
  };

  // Hand chat-imported items to the same conversational template setup used by
  // the Item Catalog and by setup itself (utils/templateSetupLaunch). That is
  // the source of truth for the graphical template cards, inline schedules,
  // custom re-grouping, and the graphical completion card; routing imports
  // there prevents the old text-only shortcut from silently creating a
  // different set of templates.
  const openTemplateSetupFromImport = (pending) => {
    const session = (pending && pending.session) || {};
    const keptIds = (pending && pending.kept_item_ids) || [];
    const storeId = getStoreId();
    if (!storeId || !keptIds.length) {
      pushMessage({ role: 'assistant', text: summarizeImport(session) });
      return;
    }
    const launched = launchTemplateSetup({
      storeId,
      itemIds: keptIds,
      source: isSetupSurface() ? 'setup' : 'add_items',
      // "…and make it all into one ordering template" from the import message
      // rides the handoff so the setup assistant applies it unprompted.
      directive: session.directive || undefined,
    });
    if (launched) {
      // The Count page takes over from here; the drawer would only flash.
      if (!docked) closeDrawer();
    } else {
      pushMessage({
        role: 'assistant',
        text: 'Your items are saved, but I couldn’t open the graphical template review in this browser. Open Counts and ask CounterIntelligence to organize the new items when you’re ready.',
        tone: 'error',
        link: { href: '/count.html?view=overview', label: 'Open Counts →' },
      });
    }
  };

  const finishImportWithoutTemplates = (session) => {
    pushMessage({
      role: 'assistant',
      text: summarizeImport(session || {}),
      link: { href: '/count.html?view=overview', label: 'Build templates anytime on Counts →' },
    });
  };

  // ── In-chat price update ────────────────────────────────────────────────────
  // Offer → upload an invoice/price list → run the SAME price-update processor the
  // Update Prices page uses (price-update-ai extract, shared candidate scoring,
  // deterministic dedup classification) → review the proposed price changes →
  // apply (update changed prices, add new items, record price history). The user
  // approves before anything is written; matched items only update when the price
  // is actually different.
  const offerPriceUpdate = (text) => {
    setPending(buildPriceUpdateOfferPending({ text }));
    pushMessage({ role: 'assistant', text: PRICE_UPDATE_OFFER_TEXT, choices: priceUpdateOfferChips() });
  };

  const promptPriceFileUpload = () => {
    const id = pushMessage({ role: 'assistant', card: { kind: 'price_update_upload', status: 'idle' } });
    priceUploadCardIdRef.current = id;
  };

  const onPriceFilesChosen = (e) => {
    const picked = e?.target?.files ? Array.from(e.target.files) : [];
    if (e?.target) e.target.value = '';
    if (picked.length) runPriceUpdate(picked);
  };

  // Read one uploaded file into the shape the price-update-ai extractor expects:
  // images as base64, PDFs as extracted text when pdf.js is available (else
  // base64 for server-side OCR), spreadsheets/CSV/text as plain text.
  const readPriceSource = async (file) => {
    const name = (file.name || 'upload').toLowerCase();
    const type = String(file.type || '').toLowerCase();
    const isImage = type.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'].some((e) => name.endsWith(e));
    if (isImage) {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(String(ev.target?.result || ''));
        reader.onerror = () => reject(new Error('Could not read that image.'));
        reader.readAsDataURL(file);
      });
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : '';
      return { fileType: 'image', mediaType: type || 'image/jpeg', imageData: base64, fileName: file.name };
    }
    if (name.endsWith('.pdf') || type.includes('pdf')) {
      // price-update-ai extracts from PDF *text* (it doesn't OCR PDFs), so pull the
      // text client-side with pdf.js — the same library the Update Prices page uses.
      const text = await extractPdfTextForPrice(file);
      if (!text.trim()) throw new Error('I couldn’t read text from that PDF — try a clearer PDF, a spreadsheet, or a photo of the invoice.');
      return { fileType: 'pdf', text, fileName: file.name };
    }
    if (isSpreadsheetFile(file)) {
      // Excel is binary — parse it to CSV rows with SheetJS (what the Invoices
      // page does) instead of handing the extractor unreadable bytes.
      const source = await readSpreadsheetSource(file);
      return { fileType: 'csv', text: source.text, fileName: file.name };
    }
    const text = await file.text().catch(() => '');
    if (!text.trim()) throw new Error(`I couldn’t read ${file.name ? `“${file.name}”` : 'that file'} as text — try a PDF, spreadsheet, or photo of the invoice.`);
    return { fileType: 'csv', text, fileName: file.name };
  };

  const runPriceUpdate = async (files) => {
    // Same within-batch dedupe as runItemImport.
    const list = partitionNewFiles([], Array.from(files || [])).added;
    // Same guarantees as runItemImport: every early exit says something, and
    // progress/errors always have a card to land on.
    if (busy) {
      pushMessage({ role: 'assistant', text: 'One moment — I’m still finishing the previous step. Tap “Choose a file” again when it’s done.' });
      return;
    }
    const check = validateImportFiles(list);
    if (!check.ok) {
      pushMessage({ role: 'assistant', text: check.error, tone: 'error' });
      return;
    }
    const storeId = getStoreId();
    if (!storeId) {
      pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store, then re-upload.' });
      return;
    }
    let cardId = priceUploadCardIdRef.current;
    const setUpload = (patch) => {
      const card = { kind: 'price_update_upload', ...patch };
      if (cardId) { updateMessage(cardId, { card }); return; }
      cardId = pushMessage({ role: 'assistant', card });
      priceUploadCardIdRef.current = cardId;
    };
    const attached = describeAttachedFiles(list);
    setBusy(true);
    try {
      setUpload({ status: 'processing', note: `Attached ${attached} — loading your catalog…` });
      const { data: catalog } = await directQuery('inventory_items', {
        select: 'id,description,vendor,unit,pack_size,case_price,unit_price,category,location,brand',
        eq: { store_id: storeId },
        fetchAll: true,
        pageSize: 500,
      });
      const allCatalog = catalog || [];
      const catalogLookup = buildCatalogLookup(allCatalog);

      // Extract line items from every file (a failed file shouldn't abort the batch).
      setUpload({ status: 'processing', note: `Reading ${attached}…` });
      const invoiceItems = [];
      const failedFiles = [];
      for (let i = 0; i < list.length; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const source = await readPriceSource(list[i]);
          // eslint-disable-next-line no-await-in-loop
          // Each file is one invoice: tag its lines with that invoice's date and
          // number so the resulting price events are dated by (and traceable to)
          // the right invoice, even when several are imported at once.
          const { items, invoiceVendor, invoiceDate, invoiceNumber } = await callPriceExtractWithMeta(buildAuthHeaders, source);
          invoiceItems.push(...items.map((it) => ({ ...it, _invoiceVendor: invoiceVendor, _invoiceDate: invoiceDate, _invoiceNumber: invoiceNumber })));
          setUpload({ status: 'processing', note: `Read ${invoiceItems.length} line item${invoiceItems.length === 1 ? '' : 's'} so far…` });
        } catch (_) {
          failedFiles.push(list[i].name || `file ${i + 1}`);
        }
      }

      if (!invoiceItems.length) {
        setUpload({
          status: 'error',
          note: failedFiles.length
            ? `I couldn’t read ${describeFailedFiles(failedFiles)}. Try a clearer invoice, price list, or spreadsheet.`
            : `I didn’t find any priced line items in ${attached}. Try a clearer invoice or price list.`,
        });
        return;
      }

      // Match each invoice item to a catalog candidate (same as the Update Prices
      // page). Classification stays deterministic; this only enriches the review.
      const matched = [];
      for (let i = 0; i < invoiceItems.length; i += 1) {
        setUpload({ status: 'processing', note: `Matching item ${i + 1} of ${invoiceItems.length}…` });
        const invoiceItem = invoiceItems[i];
        let match = { catalogId: null, confidence: 'none', reason: '' };
        try {
          const candidates = getCandidates(invoiceItem, allCatalog);
          // eslint-disable-next-line no-await-in-loop
          match = await callPriceMatch(buildAuthHeaders, invoiceItem, candidates);
        } catch (_) { /* deterministic dedup still classifies it below */ }
        matched.push({ invoiceItem, confidence: match.confidence || 'none', reason: match.reason || '' });
      }

      const rows = buildPriceUpdateRows({ matched, catalogLookup });
      const counts = summarizeRowCounts(rows);
      setUpload({ status: 'done', note: `${counts.priceChanges} price change${counts.priceChanges === 1 ? '' : 's'}, ${counts.newItems} new, ${counts.unchanged} unchanged.` });

      if (!actionableRows(rows).length) {
        pushMessage({
          role: 'assistant',
          text: counts.unchanged
            ? `I read ${invoiceItems.length} item${invoiceItems.length === 1 ? '' : 's'}, but every one already matches the price in your catalog — nothing to update.`
            : 'I couldn’t match those line items to your catalog or read any prices. You can add them as new items from Catalog & Prices, or try a clearer invoice.',
        });
        return;
      }

      const lead = failedFiles.length
        ? `I couldn’t read ${describeFailedFiles(failedFiles)}, but from the rest I found ${counts.priceChanges} price change${counts.priceChanges === 1 ? '' : 's'}${counts.newItems ? ` and ${counts.newItems} new item${counts.newItems === 1 ? '' : 's'}` : ''}. Review below, then approve.`
        : `I found ${counts.priceChanges} price change${counts.priceChanges === 1 ? '' : 's'}${counts.newItems ? ` and ${counts.newItems} new item${counts.newItems === 1 ? '' : 's'}` : ''}. Review below, then approve — I’ll only update prices that are different.`;
      pushMessage({
        role: 'assistant',
        text: lead,
        card: { kind: 'price_update', storeId, rows, status: 'pending' },
      });
    } catch (err) {
      setUpload({ status: 'error', note: formatErrorMessage(err, 'Something went wrong while reading prices.') });
    } finally {
      setBusy(false);
    }
  };

  // Apply the approved price changes. Re-classifies against the live catalog at
  // save time (the user may have removed rows, and prices may have moved), then
  // updates changed prices, inserts new items, and records every change to price
  // history — the same writes the Update Prices page performs.
  const applyPriceUpdate = async (messageId, card) => {
    const storeId = card.storeId || getStoreId();
    if (!storeId) throw new Error('I can’t tell which store you’re in — pick a store and try again.');
    const rows = actionableRows(card.rows || []);
    if (!rows.length) throw new Error('Nothing to apply — every row was removed.');

    // When this import ran. Recorded on each price event (import_date) for the
    // audit trail; the event itself is dated by the invoice date, not this.
    const importedAt = new Date().toISOString();

    // Re-run dedup on the current catalog so a stale price/classification can't
    // overwrite a value that changed since the preview.
    let liveRows = rows;
    try {
      const { data: catData } = await directQuery('inventory_items', {
        select: 'id,description,vendor,unit,case_price,unit_price',
        eq: { store_id: storeId },
        fetchAll: true,
        pageSize: 500,
      });
      const liveCatalog = catData || [];
      const liveById = new Map(liveCatalog.map((r) => [String(r.id), r]));
      const freshLookup = buildCatalogLookup(liveCatalog);
      liveRows = rows.map((item) => {
        const result = classifyItem({
          description: item.description, vendor: item.vendor, unit: item.unit, case_price: item.case_price,
        }, freshLookup);
        const freshExisting = result.existingItem
          ? (liveById.get(String(result.existingItem.id)) || result.existingItem)
          : null;
        return {
          ...item,
          _classification: result.classification,
          _existingItem: freshExisting,
          _priceDeltaAbsolute: result.priceDeltaAbsolute,
          _priceDeltaPct: result.priceDeltaPct,
        };
      });
    } catch (_) { /* fall back to the previewed values */ }

    let updated = 0;
    let added = 0;
    let skipped = 0;
    const errors = [];

    for (const item of liveRows) {
      try {
        const classification = item._classification || 'unchanged';
        if (classification === 'unchanged') { skipped += 1; continue; }

        if (classification === 'price_change') {
          const existing = item._existingItem;
          const newPrice = parseFloat(item.case_price);
          if (!existing?.id || !Number.isFinite(newPrice)) { skipped += 1; continue; }
          const updates = { case_price: newPrice };
          if (existing.unit_price != null) updates.unit_price = newPrice;
          // eslint-disable-next-line no-await-in-loop
          const { error } = await directUpdate('inventory_items', updates, { id: existing.id, store_id: storeId });
          if (error) throw error;
          // eslint-disable-next-line no-await-in-loop
          await recordPriceHistory({
            inventoryItemId: existing.id,
            storeId,
            oldName: existing.description,
            newName: item.description || existing.description,
            oldCasePrice: existing.case_price,
            newCasePrice: newPrice,
            oldUnitPrice: existing.unit_price,
            newUnitPrice: existing.unit_price != null ? newPrice : null,
            source: 'ai_price_import',
            invoiceVendor: item.vendor || null,
            invoiceUnit: item.unit || null,
            priceDeltaAbsolute: item._priceDeltaAbsolute,
            priceDeltaPct: item._priceDeltaPct,
            invoiceDate: item._invoiceDate || null,
            invoiceNumber: item._invoiceNumber || null,
            importDate: importedAt,
          });
          updated += 1;
        } else if (classification === 'new') {
          const insertPayload = {
            store_id: storeId,
            description: String(item.description || '').trim() || 'Unknown Item',
            vendor: item.vendor || null,
            unit: item.unit || null,
            case_price: parseFloat(item.case_price) || null,
            category: item.category || null,
            location: item.location || null,
          };
          // eslint-disable-next-line no-await-in-loop
          const { data: returnedItem, error } = await supabase
            .from('inventory_items')
            .insert([insertPayload])
            .select()
            .maybeSingle();
          if (error) throw error;
          const inserted = returnedItem || await findInventoryItemAfterDuplicateMerge(
            storeId,
            insertPayload,
            {},
            supabase,
          );
          if (!inserted?.id) throw new Error('The saved inventory item could not be resolved.');
          const mergedByDuplicateTrigger = !returnedItem?.id;

          // Default price handling for a newly discovered item: record the
          // price as a way to BUY it, not as a bare number on the item. A
          // later supplier then adds a vendor instead of overwriting this one.
          let firstOption = null;
          if (inserted?.id) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const mirrored = await createOptionForItem(storeId, {
                ...inserted,
                ...insertPayload,
                id: inserted.id,
              }, { channel: 'counter_intelligence' });
              firstOption = mirrored?.option || null;
            } catch (optionErr) {
              console.warn('[counter-intelligence] Purchase option mirror failed (non-fatal):', optionErr?.message);
            }
          }

          const insertedPrice = parseFloat(inserted?.case_price);
          if (!mergedByDuplicateTrigger && inserted?.id && Number.isFinite(insertedPrice) && insertedPrice > 0) {
            // eslint-disable-next-line no-await-in-loop
            await recordPriceHistory({
              inventoryItemId: inserted.id,
              storeId,
              purchaseOptionId: firstOption?.id || null,
              normalizedUnitCost: firstOption?.normalized_unit_cost ?? null,
              oldName: null,
              newName: inserted.description,
              oldCasePrice: null,
              newCasePrice: insertedPrice,
              oldUnitPrice: null,
              newUnitPrice: null,
              source: 'ai_price_import',
              invoiceVendor: item.vendor || null,
              invoiceUnit: item.unit || null,
              invoiceDate: item._invoiceDate || null,
              invoiceNumber: item._invoiceNumber || null,
              importDate: importedAt,
            });
          }
          if (mergedByDuplicateTrigger) updated += 1;
          else added += 1;
        }
      } catch (err) {
        errors.push(`${item.description || '?'}: ${formatErrorMessage(err, 'failed')}`);
      }
    }

    const applied = { updated, added, skipped };
    updateMessage(messageId, { card: { ...card, status: 'done', applied } });
    try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'price_update', storeId } })); } catch (_) {}
    let text = summarizePriceUpdate(applied);
    if (errors.length) text += `\n\nI couldn’t apply ${errors.length}: ${errors.slice(0, 5).join('; ')}${errors.length > 5 ? '…' : ''}`;
    pushMessage({
      role: 'assistant',
      text,
      link: { href: '/items.html', label: 'Open Item Catalog →' },
    });
  };

  // ── Single-item price change and direct item creation via chat ───────────────
  const fetchStoreCatalog = async (storeId) => {
    const { data, error } = await directQuery('inventory_items', {
      select: 'id,description,vendor,category,location,unit,case_price,unit_price,pack_size,brand',
      eq: { store_id: storeId },
      fetchAll: true,
      pageSize: 500,
    });
    if (error) throw error;
    return data || [];
  };

  // The store's existing category / location values, so a single-item add can
  // reuse them instead of coining a near-duplicate ("Beer" vs "Beers"). We union
  // the distinct values already on the catalog with the canonical lists the
  // manual Add-Items flow keeps on store_details, then de-dupe case/plural-
  // insensitively and sort. (Vendors are reconciled separately, alias-aware, by
  // the vendor autocomplete + assignVendorToItem.)
  const fetchItemFacets = async (storeId) => {
    const empty = { categories: [], locations: [] };
    if (!storeId) return empty;
    const catCategories = [];
    const catLocations = [];
    try {
      const catalog = await fetchStoreCatalog(storeId);
      (catalog || []).forEach((row) => {
        if (row?.category) catCategories.push(row.category);
        if (row?.location) catLocations.push(row.location);
      });
    } catch (_) { /* fall back to store_details only */ }
    let detailCategories = [];
    let detailLocations = [];
    try {
      const { data } = await directQuery('store_details', {
        select: 'accounting_categories,locations',
        eq: { store_id: storeId },
        limit: 1,
      });
      const row = data?.[0];
      if (Array.isArray(row?.accounting_categories)) detailCategories = row.accounting_categories;
      if (Array.isArray(row?.locations)) detailLocations = row.locations;
    } catch (_) { /* store_details is optional enrichment */ }
    const byName = (a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
    return {
      categories: dedupeLabels([...catCategories, ...detailCategories]).sort(byName),
      locations: dedupeLabels([...catLocations, ...detailLocations]).sort(byName),
    };
  };

  const applySinglePriceChange = async ({ itemId, newPrice, changedAt }) => {
    const storeId = getStoreId();
    if (!storeId) throw new Error('I can’t tell which store you’re in — pick a store and try again.');
    const { data: items, error: fetchError } = await directQuery('inventory_items', {
      select: 'id,description,case_price,unit_price',
      eq: { id: itemId, store_id: storeId },
      limit: 1,
    });
    if (fetchError) throw fetchError;
    const existing = items?.[0];
    if (!existing) throw new Error('I couldn’t find that item in your catalog.');
    const oldCasePrice = parseFloat(existing.case_price) || null;
    const oldUnitPrice = parseFloat(existing.unit_price) || null;
    const updates = { case_price: newPrice };
    if (oldUnitPrice != null) updates.unit_price = newPrice;
    const { error } = await directUpdate('inventory_items', updates, { id: existing.id, store_id: storeId });
    if (error) throw error;
    await recordPriceHistory({
      inventoryItemId: existing.id,
      storeId,
      oldName: existing.description,
      newName: existing.description,
      oldCasePrice,
      newCasePrice: newPrice,
      oldUnitPrice,
      newUnitPrice: oldUnitPrice != null ? newPrice : null,
      source: 'ci_chat',
      priceDeltaAbsolute: oldCasePrice != null ? newPrice - oldCasePrice : null,
      priceDeltaPct: oldCasePrice ? ((newPrice - oldCasePrice) / oldCasePrice) * 100 : null,
      changedAt: changedAt || null,
    });
    try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'price_change', storeId } })); } catch (_) {}
    return existing;
  };

  // Keep the store's canonical category / location lists (on store_details) in
  // step with a freshly-added value, mirroring the manual Add-Items save so the
  // value is offered next time instead of being retyped into a near-duplicate.
  // Best-effort: a failure here never blocks the item insert.
  const mergeStoreFacets = async (storeId, { category, location }) => {
    const cat = String(category || '').trim();
    const loc = String(location || '').trim();
    if (!cat && !loc) return;
    try {
      const { data } = await directQuery('store_details', {
        select: 'id,accounting_categories,locations',
        eq: { store_id: storeId },
        limit: 1,
      });
      const row = data?.[0] || null;
      const existingCats = Array.isArray(row?.accounting_categories) ? row.accounting_categories.filter(Boolean) : [];
      const existingLocs = Array.isArray(row?.locations) ? row.locations.filter(Boolean) : [];
      const nextCats = cat && !findExistingLabel(cat, existingCats) ? [...existingCats, cat] : existingCats;
      const nextLocs = loc && !findExistingLabel(loc, existingLocs) ? [...existingLocs, loc] : existingLocs;
      if (nextCats.length === existingCats.length && nextLocs.length === existingLocs.length) return;
      if (row?.id) {
        await supabase.from('store_details')
          .update({ accounting_categories: nextCats, locations: nextLocs, updated_at: new Date().toISOString() })
          .eq('id', row.id);
      } else {
        await supabase.from('store_details')
          .insert({ store_id: storeId, accounting_categories: nextCats, locations: nextLocs });
      }
    } catch (_) { /* keeping the canonical lists fresh is best-effort */ }
  };

  // Returns the effective fields actually saved (e.g. the vendor's canonical
  // display name) so the caller's confirmation reflects what landed in the catalog.
  const applySingleItemCreate = async (fields) => {
    const storeId = getStoreId();
    if (!storeId) throw new Error('I can’t tell which store you’re in — pick a store and try again.');
    const f = normalizeItemFields(fields);
    if (!f.name || !f.category || !f.unit) throw new Error('Name, category, and unit are required.');
    // Reconcile the vendor against the store's directory (alias-aware find-or-
    // create) — the same resolution the manual/import saves use — so "Sysco Los
    // Angeles" collapses onto the existing "Sysco" instead of spawning a second
    // vendor. Falls back to the raw text if resolution is unavailable.
    let vendorText = f.vendor || null;
    let vendorId = null;
    if (f.vendor) {
      try {
        const resolved = await assignVendorToItem({
          supabaseClient: supabase,
          storeId,
          vendorName: f.vendor,
          stubNote: 'Auto-created from Counter Intelligence chat',
        });
        vendorId = resolved?.vendorId || null;
        vendorText = resolved?.vendorDisplayName || f.vendor;
      } catch (_) { /* keep the typed vendor text */ }
    }
    const insert = {
      store_id: storeId,
      description: f.name,
      category: f.category,
      unit: f.unit,
      ...(f.location ? { location: f.location } : {}),
      ...(vendorText ? { vendor: vendorText } : {}),
      ...(vendorId ? { vendor_id: vendorId } : {}),
      ...(f.pack_size ? { pack_size: f.pack_size } : {}),
      ...(f.brand ? { brand: f.brand } : {}),
      ...(f.price != null ? { case_price: f.price, unit_price: f.price } : {}),
    };
    const { data: returnedItem, error } = await supabase
      .from('inventory_items')
      .insert([insert])
      .select()
      .maybeSingle();
    if (error) throw error;
    const inserted = returnedItem || await findInventoryItemAfterDuplicateMerge(
      storeId,
      insert,
      {},
      supabase,
    );
    if (!inserted?.id) throw new Error('The saved inventory item could not be resolved.');
    const mergedByDuplicateTrigger = !returnedItem?.id;
    await mergeStoreFacets(storeId, { category: f.category, location: f.location });
    let firstOption = null;
    if (inserted?.id) {
      try {
        const mirrored = await createOptionForItem(storeId, {
          ...inserted,
          ...insert,
          id: inserted.id,
        }, {
          channel: 'counter_intelligence',
        });
        firstOption = mirrored?.option || null;
      } catch (optionError) {
        // The inventory item already exists at this point. Match the adjacent
        // AI price-import recovery behavior so retrying chat cannot duplicate
        // the item merely because its vendor-product mirror needs healing.
        console.warn('[counter-intelligence] Purchase option mirror failed (non-fatal):', optionError?.message);
      }
    }
    if (!mergedByDuplicateTrigger && inserted?.id && f.price != null) {
      await recordPriceHistory({
        inventoryItemId: inserted.id,
        storeId,
        purchaseOptionId: firstOption?.id || null,
        normalizedUnitCost: firstOption?.normalized_unit_cost ?? null,
        oldName: null,
        newName: inserted.description,
        oldCasePrice: null,
        newCasePrice: f.price,
        oldUnitPrice: null,
        newUnitPrice: f.price,
        source: 'ci_chat',
        changedAt: null,
      });
    }
    if (vendorId) { try { clearVendorDirectoryCache(storeId); } catch (_) { /* cache clear is best-effort */ } }
    // A real (non-sample) item now exists, so the onboarding gate is open —
    // link buttons pushed from here on genuinely work (see setupUnlockedRef).
    setupUnlockedRef.current = true;
    try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'item_create', storeId } })); } catch (_) {}
    return { ...f, vendor: vendorText, _mergedExisting: mergedByDuplicateTrigger };
  };

  // The item-create form (ItemCreateForm) calls this with its assembled values.
  const submitItemCreateForm = async (values) => {
    const merged = normalizeItemFields(values);
    const { ok, missing } = validateItemCreate(merged);
    if (!ok) {
      // The form marks required fields, but guard anyway (e.g. whitespace-only).
      pushMessage({ role: 'assistant', text: itemCreateMissingMessage(missing) });
      return;
    }
    clearPending();
    setBusy(true);
    try {
      const saved = await applySingleItemCreate(merged);
      const preview = buildItemCreatePreview(saved);
      pushMessage({
        role: 'assistant',
        text: saved._mergedExisting
          ? `Updated the existing ${preview} and its vendor product.`
          : `Added ${preview} to your Item Catalog.`,
        link: { href: '/items.html', label: 'Open Item Catalog →' },
      });
    } catch (err) {
      pushMessage({ role: 'assistant', text: formatErrorMessage(err, 'I couldn’t add the item.'), tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const cancelItemCreateForm = () => {
    clearPending();
    pushMessage({ role: 'assistant', text: 'Okay — I didn’t add the item. Just ask when you’re ready.' });
  };

  const previewSinglePriceChange = async (classification, storeId) => {
    const req = extractPriceChangeRequest(classification);
    if (!req) {
      pushMessage({ role: 'assistant', text: 'I’m not sure which item or price you mean — say something like “change Golden Pier Blonde Ale to $12”.' });
      return;
    }
    let catalog = [];
    try { catalog = await fetchStoreCatalog(storeId); } catch (err) {
      pushMessage({ role: 'assistant', text: formatErrorMessage(err, 'I couldn’t load your catalog right now.'), tone: 'error' });
      return;
    }
    const { match, candidates } = findMatchingCatalogItem(req.itemName, catalog);
    if (!match) {
      if (candidates.length) {
        setPending({
          kind: 'pending_item_price_change',
          route: 'item_price_change',
          awaiting: 'candidate_choice',
          candidates,
          newPrice: req.newPrice,
          question: `I wasn’t sure which item you meant by “${req.itemName}”. Which one?`,
          created_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        });
        pushMessage({
          role: 'assistant',
          text: `I wasn’t sure which item you meant by “${req.itemName}”. Which one?`,
          choices: candidates.slice(0, 5).map((c) => ({ key: `pc_item:${c.id}`, label: `${c.description}${c.category ? ` · ${c.category}` : ''}` })),
        });
        return;
      }
      pushMessage({ role: 'assistant', text: `I couldn’t find “${req.itemName}” in your catalog. Check the name or add it as a new item.` });
      return;
    }
    const preview = buildPriceChangePreview(match, req.newPrice);
    setPending({
      kind: 'pending_item_price_change',
      route: 'item_price_change',
      awaiting: 'date_choice',
      itemId: match.id,
      itemName: preview.itemName,
      oldPrice: preview.oldPrice,
      newPrice: preview.newPrice,
      preview,
      question: `Change ${preview.itemName} from ${formatCurrency(preview.oldPrice || 0)} to ${formatCurrency(preview.newPrice)}?`,
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
    });
    pushMessage({
      role: 'assistant',
      text: `I’ll change ${preview.itemName} from ${formatCurrency(preview.oldPrice || 0)} to ${formatCurrency(preview.newPrice)}. When should this price change take effect?`,
      choices: priceChangeDateChips(),
    });
  };

  const previewSingleItemCreate = async (classification) => {
    const storeId = getStoreId();
    const rawFields = classification?.extracted_item_fields || null;
    const fields = normalizeItemFields(rawFields);
    // Reconcile the proposed category/location against the store's existing
    // values so the pre-fill (and the dropdown default) reuses their own
    // spelling — "Beers" becomes their existing "Beer" — instead of creating a
    // near-duplicate. Vendor is reconciled in the form + on save (alias-aware).
    let facets = { categories: [], locations: [] };
    try { facets = await fetchItemFacets(storeId); } catch (_) { /* preview still works without them */ }
    if (fields.category) fields.category = findExistingLabel(fields.category, facets.categories) || fields.category;
    if (fields.location) fields.location = findExistingLabel(fields.location, facets.locations) || fields.location;
    const { ok, missing } = validateItemCreate(fields);
    setPending({
      kind: 'pending_item_create',
      route: 'item_create_direct',
      awaiting: 'form',
      fields,
      question: ok ? itemCreateOptionalPrompt(fields) : itemCreateMissingMessage(missing),
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
    });
    // Recap the details we parsed so the user can review them before saving —
    // the form below is pre-filled with the same values. When a required field
    // (usually category) is missing, say so plainly instead of a generic nudge.
    const recap = buildItemCreatePreview(fields);
    const recapLine = recap ? `Here’s what I’ve got: ${recap}. ` : '';
    pushMessage({
      role: 'assistant',
      text: ok
        ? `${recapLine}Review the details — pick an existing category, area, or vendor if my guess is off — then tap Add item.`
        : `${recapLine}I just need a bit more to add it to your catalog — fill in the required fields below and tap Add item.`,
      form: {
        kind: 'item_create',
        fields: buildItemCreateFormValues(fields),
        facets: { category: facets.categories, location: facets.locations },
      },
    });
  };

  // ── In-chat reports ────────────────────────────────────────────────────────
  // A report question ("what was my inventory value for August?", "compare
  // August and September COGS", "review my purchase history") is parsed into a
  // report query (ciReportQuery.mjs), matched against the store's completed
  // counts / saved End of Period reports, and answered from that data — asking
  // which count/report to use when several match, and pointing at the report
  // to run when none exists. Replies are resolved in handleSend's pending
  // block, then renderReport drops the finished report into the chat.

  // The generator for one count-backed report type (value / totals / review).
  const generateCountReport = async (type, { session, query, storeId }) => {
    const storeName = getReportStoreName();
    const createdBy = getReportCreatedBy();
    const scope = query?.scope || null;
    if (type === 'inventory_totals') return generateInventoryTotalsReport({ supabase, storeId, session, scope, storeName, createdBy });
    if (type === 'count_review') return generateCountReviewReport({ supabase, storeId, session, scope, storeName, createdBy });
    return generateInventoryValueReport({ supabase, storeId, session, scope, storeName, createdBy });
  };

  const generatePurchaseReport = async ({ query, storeId, sinceDays = null, startDate = null, endDate = null, periodLabel = '' }) => {
    const storeName = getReportStoreName();
    const createdBy = getReportCreatedBy();
    const vendorScope = query?.vendorScope || null;
    // Line-item questions ("wine purchases") need line-level data; plain
    // "what did I buy" questions exclude drafts/canceled so the total is what
    // was actually ordered, while the browse-style report keeps everything and
    // shows each order's status.
    if (query?.scope) {
      return generatePurchaseItemHistoryReport({
        supabase, storeId, sinceDays, startDate, endDate, periodLabel,
        vendorScope, itemScope: query.scope, storeName, createdBy,
      });
    }
    return generatePurchaseHistoryReport({
      supabase, storeId, sinceDays, startDate, endDate, periodLabel,
      vendorScope, excludeInactive: query?.purchaseSpend === true || !!vendorScope, storeName, createdBy,
    });
  };

  // Offer/answer one count-backed report for a single period.
  const offerCountReport = async (type, query, storeId) => {
    const range = (query?.ranges && query.ranges[0]) || null;
    let sessions = [];
    try { sessions = await listReportSessionsForAI({ supabase, storeId, reportType: type, range }); } catch (_) { sessions = []; }
    if (!sessions.length) {
      const next = prepareReportRunForAI({ reportType: type, rangeLabel: range?.label || '' });
      pushMessage({ role: 'assistant', text: next.text, link: next.link });
      return;
    }
    // Prefer counts whose name/template the user actually mentioned
    // ("…for PoP Offsite Dallas"); fall back to all candidates.
    const named = filterSessionsByNameMention(sessions, query?.sourceText || '');
    const pool = named.length ? named : sessions;
    // "the latest"/"the oldest", a mentioned name, or a date that pins down
    // exactly one count answers directly — the summary states which count.
    let session = null;
    if (query?.recency === 'latest') session = pool[0];
    else if (query?.recency === 'oldest') session = pool[pool.length - 1];
    else if (pool.length === 1 && (range || named.length)) session = pool[0];
    if (session) {
      const result = await generateCountReport(type, { session, query, storeId });
      await renderReport(result, range?.assumed ? `I read “${range.label.split(' ')[0]}” as ${range.label}.` : '');
      return;
    }
    setPending(buildReportPending({ report: type, awaiting: 'count', sessions: pool, query, question: 'Which completed count should I use?' }));
    const what = type === 'inventory_totals' ? 'counted quantities' : (type === 'count_review' ? 'count review' : 'inventory value');
    pushMessage({
      role: 'assistant',
      text: range
        ? `I found ${pool.length} completed counts for ${range.label} — which one should I use for the ${what}?`
        : `Which completed count should I use for the ${what}? Tap one below, or say “the latest”.`,
      choices: inventoryCountChips(pool),
    });
  };

  // "Compare the last two" — the newest completed count vs the one before it,
  // honoring a mentioned count/template name.
  const offerLastTwoComparison = async (type, query, storeId) => {
    let sessions = [];
    try { sessions = await listReportSessionsForAI({ supabase, storeId, reportType: type }); } catch (_) { sessions = []; }
    const named = filterSessionsByNameMention(sessions, query?.sourceText || '');
    const pool = named.length ? named : sessions;
    if (!pool.length) {
      const next = prepareReportRunForAI({ reportType: type });
      pushMessage({ role: 'assistant', text: next.text, link: next.link });
      return;
    }
    if (pool.length < 2) {
      pushMessage({
        role: 'assistant',
        text: `I only found one completed count${named.length ? ' matching that name' : ''} — “${pool[0].name}” — so there’s nothing to compare it against yet.`,
      });
      return;
    }
    const [newest, previous] = pool;
    const result = await generateInventoryValueComparison({
      supabase, storeId, sessionA: previous, sessionB: newest,
      scope: query?.scope || null,
      storeName: getReportStoreName(), createdBy: getReportCreatedBy(),
    });
    await renderReport(result, type !== 'inventory_value'
      ? 'I compare periods by dollar value — counted quantities span mixed units, so value is the comparable measure.'
      : '');
  };

  // Compare a count-backed report across two periods.
  const offerCountComparison = async (type, query, storeId) => {
    const [rangeA, rangeB] = query.ranges;
    let sessionsA = []; let sessionsB = [];
    try {
      [sessionsA, sessionsB] = await Promise.all([
        listReportSessionsForAI({ supabase, storeId, reportType: type, range: rangeA }),
        listReportSessionsForAI({ supabase, storeId, reportType: type, range: rangeB }),
      ]);
    } catch (_) { /* fall through to the missing-report message */ }
    // A mentioned count/template name narrows both sides.
    const nameFilter = (list) => {
      const named = filterSessionsByNameMention(list, query?.sourceText || '');
      return named.length ? named : list;
    };
    sessionsA = nameFilter(sessionsA);
    sessionsB = nameFilter(sessionsB);
    const missing = [!sessionsA.length && rangeA.label, !sessionsB.length && rangeB.label].filter(Boolean);
    if (missing.length) {
      const next = prepareReportRunForAI({ reportType: type, rangeLabel: missing.join(' and ') });
      pushMessage({
        role: 'assistant',
        text: `I can compare those once both periods have a completed count. ${next.text}`,
        link: next.link,
      });
      return;
    }
    const generateComparison = async (sessionA, sessionB) => {
      const result = await generateInventoryValueComparison({
        supabase, storeId, sessionA, sessionB,
        labelA: rangeA.label, labelB: rangeB.label,
        scope: query?.scope || null,
        storeName: getReportStoreName(), createdBy: getReportCreatedBy(),
      });
      // A quantities/review question still compares by dollar value — mixed
      // units make quantity totals incomparable — so say so.
      await renderReport(result, type !== 'inventory_value'
        ? 'I compare periods by dollar value — counted quantities span mixed units, so value is the comparable measure.'
        : '');
    };
    if (sessionsA.length === 1 && sessionsB.length === 1) {
      await generateComparison(sessionsA[0], sessionsB[0]);
      return;
    }
    if (sessionsA.length !== 1) {
      setPending(buildReportPending({
        report: type, awaiting: 'compare_count', slot: 'a',
        sessions: sessionsA, sessionsB, rangeA, rangeB, picked: {}, query,
        question: `Which count should I use for ${rangeA.label}?`,
      }));
      pushMessage({
        role: 'assistant',
        text: `I found ${sessionsA.length} completed counts for ${rangeA.label} — which one should I compare?`,
        choices: inventoryCountChips(sessionsA),
      });
      return;
    }
    setPending(buildReportPending({
      report: type, awaiting: 'compare_count', slot: 'b',
      sessions: sessionsB, sessionsB, rangeA, rangeB, picked: { a: sessionsA[0] }, query,
      question: `Which count should I compare for ${rangeB.label}?`,
    }));
    pushMessage({
      role: 'assistant',
      text: `For ${rangeA.label} I’ll use “${sessionsA[0].name}”. I found ${sessionsB.length} counts for ${rangeB.label} — which one should I compare it to?`,
      choices: inventoryCountChips(sessionsB),
    });
  };

  // End of Period / actual COGS — answered ONLY from saved End of Period
  // reports (pnl_reports); never re-derived from raw counts.
  const offerEopReport = async (query, storeId) => {
    const range = (query?.ranges && query.ranges[0]) || null;
    const rangeB = (query?.ranges && query.ranges[1]) || null;
    const storeName = getReportStoreName();
    const createdBy = getReportCreatedBy();

    if (query?.comparison && range && rangeB) {
      let reportsA = []; let reportsB = [];
      try {
        [reportsA, reportsB] = await Promise.all([
          listEopReportsForAI({ supabase, storeId, range }),
          listEopReportsForAI({ supabase, storeId, range: rangeB }),
        ]);
      } catch (_) { /* fall through */ }
      const missing = [!reportsA.length && range.label, !reportsB.length && rangeB.label].filter(Boolean);
      if (missing.length) {
        const next = prepareReportRunForAI({ reportType: 'eop_cogs', rangeLabel: missing.join(' and ') });
        pushMessage({ role: 'assistant', text: `I can compare COGS once both periods have a saved End of Period report. ${next.text}`, link: next.link });
        return;
      }
      const result = generateEopComparisonReport({
        eopRowA: reportsA[0], eopRowB: reportsB[0],
        labelA: range.label, labelB: rangeB.label, storeName, createdBy,
      });
      await renderReport(result, (reportsA.length > 1 || reportsB.length > 1)
        ? 'I used the latest saved End of Period report for each period.' : '');
      return;
    }

    // "Compare the last two" = the two most recent saved End of Period reports.
    if (query?.lastTwo) {
      let reports = [];
      try { reports = await listEopReportsForAI({ supabase, storeId }); } catch (_) { reports = []; }
      if (!reports.length) {
        const next = prepareReportRunForAI({ reportType: 'eop_cogs' });
        pushMessage({ role: 'assistant', text: next.text, link: next.link });
        return;
      }
      if (reports.length < 2) {
        pushMessage({ role: 'assistant', text: `I only found one saved End of Period report — “${reports[0].period_label || 'End of Period'}” — so there’s nothing to compare it against yet.` });
        return;
      }
      const result = generateEopComparisonReport({ eopRowA: reports[1], eopRowB: reports[0], storeName, createdBy });
      await renderReport(result);
      return;
    }
    if (query?.comparison && !(range && rangeB)) {
      pushMessage({
        role: 'assistant',
        text: 'Tell me the two periods to compare — like “compare August and September COGS” — or say “compare the last two”.',
      });
      return;
    }

    let reports = [];
    try { reports = await listEopReportsForAI({ supabase, storeId, range }); } catch (_) { reports = []; }
    if (!reports.length) {
      const next = prepareReportRunForAI({ reportType: 'eop_cogs', rangeLabel: range?.label || '' });
      pushMessage({ role: 'assistant', text: next.text, link: next.link });
      return;
    }
    // "the latest / the oldest" or a single match answers directly.
    const picked = query?.recency === 'latest'
      ? reports[0]
      : (query?.recency === 'oldest' ? reports[reports.length - 1] : (reports.length === 1 ? reports[0] : null));
    if (picked) {
      const result = generateEopCogsReport({ eopRow: picked, storeName, createdBy });
      await renderReport(result, range?.assumed ? `I read “${range.label.split(' ')[0]}” as ${range.label}.` : '');
      return;
    }
    setPending(buildReportPending({ report: 'eop_cogs', awaiting: 'eop', reports, query, question: 'Which End of Period report should I use?' }));
    pushMessage({
      role: 'assistant',
      text: range
        ? `I found ${reports.length} saved End of Period reports for ${range.label} — which one should I use?`
        : `Which End of Period report should I use? Tap one below, or say “the latest”.`,
      choices: eopReportChips(reports),
    });
  };

  // Entry point: a parsed report query (from the local parser or the model's
  // route='report_query') → clarify, disambiguate, or answer.
  const offerReportQuery = async (query, storeId) => {
    if (!storeId) {
      pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' });
      return;
    }
    const q = query || {};

    // "Inventory total" is ambiguous — dollar value or counted quantities?
    if (q.needs_kind_clarification) {
      setPending(buildReportPending({ report: 'inventory_kind', awaiting: 'kind', query: q, question: 'Do you want the dollar value of that inventory, or the counted quantities?' }));
      pushMessage({
        role: 'assistant',
        text: 'Quick check — do you want the dollar value of that inventory, or the counted quantities?',
        choices: reportKindChips(),
      });
      return;
    }

    if (!q.report_type) {
      setPending(buildReportPending({ report: 'report_pick', awaiting: 'type', query: q, question: 'Which report do you want — inventory value, counted quantities, purchase history, End of Period, or price changes?' }));
      pushMessage({
        role: 'assistant',
        text: 'I can pull that from your reports — which one do you want?',
        choices: reportTypeChips(),
      });
      return;
    }

    if (q.report_type === 'price_changes') {
      const requestedRange = (q.ranges && q.ranges[0]) || null;
      // Diagnostic questions such as “why are my liquor costs high?” and
      // “what had the greatest increase?” should answer immediately. A plain
      // request to open the report still asks which period the user wants.
      const range = requestedRange || (q.priceInsight ? resolvePriceRangeReply('last 30 days') : null);
      // "What prices changed in August?" already names the range — answer it.
      if (range) {
        const result = await generatePriceChangeReport({
          supabase, storeId, startDate: range.startDate, endDate: range.endDate, rangeLabel: range.label,
          storeName: getReportStoreName(), createdBy: getReportCreatedBy(),
          scope: q.scope, priceScopeKind: q.priceScopeKind, priceDirection: q.priceDirection,
          priceInsight: q.priceInsight, priceSort: q.priceSort,
        });
        const note = requestedRange?.assumed
          ? `I read “${range.label.split(' ')[0]}” as ${range.label}.`
          : (!requestedRange && q.priceInsight ? 'I used the last 30 days.' : '');
        await renderReport(result, note);
        return;
      }
      setPending(buildReportPending({ report: 'price_changes', awaiting: 'range', query: q, question: 'Over what date range should I run the price change report?' }));
      pushMessage({
        role: 'assistant',
        text: 'To see what changed I’ll run the Item Price Change report — each item’s start and end price with the % change. Over what date range?',
        choices: priceRangeChips(),
      });
      return;
    }

    if (q.report_type === 'purchase_history') {
      const range = (q.ranges && q.ranges[0]) || null;
      const rangeB = (q.ranges && q.ranges[1]) || null;
      if (range) {
        // Two mentioned periods span into one window ("what did I buy in
        // August and September") rather than silently dropping the second.
        const bounds = [range, rangeB].filter(Boolean);
        const startDate = bounds.map((r) => r.startDate).sort()[0];
        const endDate = bounds.map((r) => r.endDate).sort().slice(-1)[0];
        const periodLabel = rangeB ? `${range.label} – ${rangeB.label}` : range.label;
        const result = await generatePurchaseReport({ query: q, storeId, startDate, endDate, periodLabel });
        await renderReport(result, range.assumed && !rangeB ? `I read “${range.label.split(' ')[0]}” as ${range.label}.` : '');
        return;
      }
      setPending(buildReportPending({ report: 'purchase_history', awaiting: 'period', query: q, question: 'Over what period should I pull purchase history?' }));
      pushMessage({
        role: 'assistant',
        text: q.vendorScope || q.scope
          ? `To answer that I’ll pull your purchase history${q.vendorScope ? ` from ${q.vendorScope}` : ''}${q.scope ? ` for “${q.scope}”` : ''}. Over what period?`
          : 'To review your purchase history I’ll pull your Purchase Orders report — every order you’ve sent, with vendor, status, and total. Over what period?',
        choices: purchaseHistoryChips(),
      });
      return;
    }

    if (q.report_type === 'eop_cogs') {
      await offerEopReport(q, storeId);
      return;
    }

    // Count-backed reports: inventory_value / inventory_totals / count_review.
    if (q.lastTwo) {
      await offerLastTwoComparison(q.report_type, q, storeId);
      return;
    }
    if (q.comparison && Array.isArray(q.ranges) && q.ranges.length === 2) {
      await offerCountComparison(q.report_type, q, storeId);
      return;
    }
    if (q.comparison && (!q.ranges || q.ranges.length < 2)) {
      pushMessage({
        role: 'assistant',
        text: 'Tell me the two periods to compare — like “compare August and September inventory” — or say “compare the last two counts”.',
      });
      return;
    }
    await offerCountReport(q.report_type, q, storeId);
  };

  // `note` states an assumption the answer rests on (e.g. which year a bare
  // month name was read as) so the user can correct it.
  const renderReport = async (result, note = '') => {
    if (result.empty) {
      pushMessage({ role: 'assistant', text: note ? `${result.summary} (${note})` : result.summary, link: result.link });
      return;
    }
    pushMessage({
      role: 'assistant',
      card: {
        kind: 'report',
        descriptor: result.descriptor,
        summary: result.summary,
        emailLines: result.emailLines,
        csvFilename: result.csvFilename,
        link: result.link,
        empty: false,
        status: 'done',
      },
    });
    if (result.link) {
      pushMessage({
        role: 'assistant',
        text: `${note ? `${note} ` : ''}Want the interactive version with filters and more detail?`,
        link: result.link,
      });
    } else if (note) {
      pushMessage({ role: 'assistant', text: note });
    }
  };

  // ── Routing: resolve follow-ups, else classify, then call the right preview ──
  // `explicitText` lets a chip / suggested reply send text directly without it
  // having to be typed into the box first.
  const handleSend = async (explicitText, sendOpts) => {
    const fromChip = typeof explicitText === 'string';
    const text = (fromChip ? explicitText : command).trim();
    if (!text || busy) return;
    const storeId = getStoreId();
    pushMessage({
      role: 'user',
      text,
      sourceLabel: sendOpts?.sourceLabel,
      helpTopicId: sendOpts?.helpTopicId,
    });
    // Structured tooltip-help context travels with this turn only; the support
    // answerer below forwards it as help_context and clears it.
    helpContextRef.current = sendOpts?.helpContext || null;
    if (!fromChip) setCommand('');
    setBusy(true);
    try {
      // Pasted tabular data (a vendor/pack/price list) rides the message
      // itself. Detected once, deterministically, and honored wherever the
      // routing below lands — the import always reads the full original rows
      // even though the classifier only sees the collapsed form.
      const pasted = detectPastedTableData(text);

      // ── Model-first turn-taking router ───────────────────────────────────────
      // When a thread is open (an offer or a question awaiting a reply), ONE classify
      // call decides — from the conversation + the open state — whether this message
      // CONTINUES that thread or starts a NEW request (dialog), and for a yes/no offer
      // how the user responded (offer_response). On "new" we drop the stale pending up
      // front so it can never hijack the fresh request (the bug behind the wrong
      // "P&L Categories" answers), and reuse the result for routing below so a new
      // request still costs a single call. If the call fails we fall back to the
      // deterministic follow-up handling that follows.
      const openPending = (() => { const p = pendingRef.current; return (p && !isPendingExpired(p)) ? p : null; })();
      let classification = null;
      let routerFailed = false;
      let offerResponse = null;
      let dialogContinue = false;
      if (openPending) {
        try {
          const r = await classifyMessage(text, openPending);
          offerResponse = r?.offer_response || null;
          dialogContinue = r?.dialog === 'continue';
          // The model says this is a new thread → clear the stale pending and reuse
          // its classification to route the new request (no second call).
          if (r && !dialogContinue) { clearPending(); classification = r; }
        } catch (_) {
          routerFailed = true; // fall back to deterministic gates below
        }
      }

      // In-chat item import follow-ups, resolved first so a "yes" to the import
      // offer (or to building templates) isn't re-classified into another flow.
      const importPending = pendingRef.current;
      if (importPending?.kind === 'pending_item_import' && !isPendingExpired(importPending)) {
        // Prefer the model's yes/no read (offer_response); fall back to keyword
        // matching only if the router didn't return one (e.g. an API hiccup).
        const declined = offerResponse === 'decline' || (offerResponse == null && isImportDecline(text));
        const accepted = offerResponse === 'accept' || (offerResponse == null && isImportAffirmative(text));
        if (importPending.awaiting === 'offer') {
          // The user answered the offer by pasting the list itself — that IS
          // the yes. Import the pasted rows; no file picker.
          if (pasted) {
            const directive = extractTemplateDirective(pasted.prose)
              || extractTemplateDirective(importPending.original_user_message || '');
            clearPending();
            await runItemImportFromText({ detection: pasted, directive });
            return;
          }
          if (declined) {
            clearPending();
            pushMessage({
              role: 'assistant',
              text: 'No problem — whenever you’re ready, just say “import my items”, or open Inventory → Items and use “Import a file” in the panel above the table.',
              link: { href: '/items.html', label: 'Open Item Catalog →' },
            });
            return;
          }
          if (accepted) {
            clearPending();
            // "Yes" to an offer made about a message that already carried the
            // table (see the clarify fallback below) imports THAT paste —
            // asking such a user to now pick a file would drop their data.
            const stashed = detectPastedTableData(importPending.original_user_message || '');
            if (stashed) {
              await runItemImportFromText({ detection: stashed, directive: extractTemplateDirective(stashed.prose) });
              return;
            }
            promptFileUpload();
            return;
          }
          // "What files can you take?" — answer it and keep the offer open so the
          // next "yes" still starts the upload. Gated on the message actually
          // being about files: offer_response 'other' just means "not a yes/no",
          // so on its own it turned every unrelated follow-up asked while this
          // offer was open into the file-format list. See isAcceptedFilesQuestion.
          if (isAcceptedFilesQuestion(text)) {
            pushMessage({ role: 'assistant', text: ACCEPTED_FILES_SENTENCE, choices: importOfferChips() });
            return;
          }
          // Some other message — drop the offer and let it classify normally.
          clearPending();
        } else if (importPending.awaiting === 'templates') {
          if (declined) {
            clearPending();
            finishImportWithoutTemplates(importPending.session);
            return;
          }
          if (accepted) {
            clearPending();
            openTemplateSetupFromImport(importPending);
            return;
          }
          clearPending();
        }
      }

      // In-chat price update follow-ups: a "yes" to the price-update offer starts
      // the invoice upload; "what files?" is answered without closing the offer.
      const pricePending = pendingRef.current;
      if (pricePending?.kind === 'pending_price_update' && !isPendingExpired(pricePending)) {
        if (pricePending.awaiting === 'offer') {
          if (offerResponse === 'decline' || (offerResponse == null && isImportDecline(text))) {
            clearPending();
            pushMessage({
              role: 'assistant',
              text: 'No problem — whenever you’re ready, just say “update my prices”, or open Catalog & Prices → Update Prices.',
              link: { href: '/items.html', label: 'Open Item Catalog →' },
            });
            return;
          }
          if (offerResponse === 'accept' || (offerResponse == null && isImportAffirmative(text))) {
            clearPending();
            promptPriceFileUpload();
            return;
          }
          // Same gate as the item-import offer above: only a genuine "what can
          // you read?" keeps this offer open with the file list. Anything else
          // drops the stale offer and gets classified on its own merits.
          if (isAcceptedFilesQuestion(text)) {
            pushMessage({ role: 'assistant', text: PRICE_UPDATE_FILES_SENTENCE, choices: priceUpdateOfferChips() });
            return;
          }
          clearPending();
        }
      }

      // After the "duplicates" explanation we offered to run the look-alike scan.
      // Resolve the reply here (before classify) so "Find duplicates now" / "find
      // duplicates in my entire catalog" / "clean up the blackberries" reliably
      // starts the scan instead of getting cold-classified into a dead end.
      const dedupPending = pendingRef.current;
      if (dedupPending?.kind === 'pending_dedup' && !isPendingExpired(dedupPending)) {
        if (offerResponse === 'decline' || (offerResponse == null && isDedupDecline(text))) {
          clearPending();
          pushMessage({
            role: 'assistant',
            text: 'No problem — whenever you want, just say “find my duplicates” and I’ll take a look.',
            link: { href: '/items.html', label: 'Open Item Catalog →' },
          });
          return;
        }
        if (offerResponse === 'accept' || (offerResponse == null && isDedupAffirmative(text))) {
          clearPending();
          if (!storeId) { pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' }); return; }
          // Pass the user's own words as the scan seed: "clean up the blackberries"
          // focuses on blackberries, while a bare "yes" / "find duplicates now"
          // matches no item name and falls back to a full-catalog scan.
          await previewItemDedup(text, storeId);
          return;
        }
        clearPending();
      }

      // Single-item price change follow-ups: candidate selection, effective date,
      // or past-date entry.
      const priceChangePending = pendingRef.current;
      if (priceChangePending?.kind === 'pending_item_price_change' && !isPendingExpired(priceChangePending)) {
        if (isSupportCancel(text)) { clearPending(); pushMessage({ role: 'assistant', text: 'Okay — no price change made.' }); return; }

        if (priceChangePending.awaiting === 'candidate_choice') {
          const chipMatch = text.match(/^pc_item:(\d+)$/);
          const chosenId = chipMatch ? Number(chipMatch[1]) : null;
          const chosen = chosenId ? priceChangePending.candidates.find((c) => Number(c.id) === chosenId) : null;
          if (chosen) {
            const preview = buildPriceChangePreview(chosen, priceChangePending.newPrice);
            setPending({
              kind: 'pending_item_price_change',
              route: 'item_price_change',
              awaiting: 'date_choice',
              itemId: chosen.id,
              itemName: preview.itemName,
              oldPrice: preview.oldPrice,
              newPrice: preview.newPrice,
              preview,
              question: `Change ${preview.itemName} from ${formatCurrency(preview.oldPrice || 0)} to ${formatCurrency(preview.newPrice)}?`,
              created_at: new Date().toISOString(),
              expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
            });
            pushMessage({
              role: 'assistant',
              text: `I’ll change ${preview.itemName} from ${formatCurrency(preview.oldPrice || 0)} to ${formatCurrency(preview.newPrice)}. When should this price change take effect?`,
              choices: priceChangeDateChips(),
            });
            return;
          }
          pushMessage({
            role: 'assistant',
            text: 'Tap one of the items above, or say “cancel”.',
            choices: priceChangePending.candidates.slice(0, 5).map((c) => ({ key: `pc_item:${c.id}`, label: `${c.description}${c.category ? ` · ${c.category}` : ''}` })),
          });
          return;
        }

        if (priceChangePending.awaiting === 'date_choice') {
          if (text === 'pc_date_today') {
            clearPending();
            setBusy(true);
            try {
              await applySinglePriceChange({ itemId: priceChangePending.itemId, newPrice: priceChangePending.newPrice, changedAt: null });
              pushMessage({ role: 'assistant', text: `Done — ${formatPriceChangeSummary(priceChangePending.preview)}. The change is effective today.`, link: { href: '/items.html', label: 'Open Item Catalog →' } });
            } catch (err) {
              pushMessage({ role: 'assistant', text: formatErrorMessage(err, 'I couldn’t update the price.'), tone: 'error' });
            } finally { setBusy(false); }
            return;
          }
          if (text === 'pc_date_past') {
            setPending({ ...priceChangePending, awaiting: 'past_date', question: 'What date should this price change be effective?' });
            pushMessage({ role: 'assistant', text: 'What date should this price change be effective? You can say something like “July 1st”, “yesterday”, or “last Monday”.' });
            return;
          }
          const parsed = parsePastDate(text);
          if (parsed) {
            clearPending();
            setBusy(true);
            try {
              await applySinglePriceChange({ itemId: priceChangePending.itemId, newPrice: priceChangePending.newPrice, changedAt: parsed });
              pushMessage({ role: 'assistant', text: `Done — ${formatPriceChangeSummary(priceChangePending.preview)}. The change is effective ${parsed}.`, link: { href: '/items.html', label: 'Open Item Catalog →' } });
            } catch (err) {
              pushMessage({ role: 'assistant', text: formatErrorMessage(err, 'I couldn’t update the price.'), tone: 'error' });
            } finally { setBusy(false); }
            return;
          }
          pushMessage({ role: 'assistant', text: 'Tap “Today” or “Set a past date”, or say “cancel”.', choices: priceChangeDateChips() });
          return;
        }

        if (priceChangePending.awaiting === 'past_date') {
          const parsed = parsePastDate(text);
          if (parsed) {
            clearPending();
            setBusy(true);
            try {
              await applySinglePriceChange({ itemId: priceChangePending.itemId, newPrice: priceChangePending.newPrice, changedAt: parsed });
              pushMessage({ role: 'assistant', text: `Done — ${formatPriceChangeSummary(priceChangePending.preview)}. The change is effective ${parsed}.`, link: { href: '/items.html', label: 'Open Item Catalog →' } });
            } catch (err) {
              pushMessage({ role: 'assistant', text: formatErrorMessage(err, 'I couldn’t update the price.'), tone: 'error' });
            } finally { setBusy(false); }
            return;
          }
          pushMessage({ role: 'assistant', text: 'I didn’t catch that date — try “July 1st”, “yesterday”, or “last Monday”, or say “cancel”.' });
          return;
        }
      }

      // Direct single-item creation: the form handles submission. Typing plain text
      // while the form is open cancels the form and starts a fresh classification.
      const itemCreatePending = pendingRef.current;
      if (itemCreatePending?.kind === 'pending_item_create' && !isPendingExpired(itemCreatePending)) {
        if (isSupportCancel(text)) { clearPending(); pushMessage({ role: 'assistant', text: 'Okay — I didn’t add the item. Just ask when you’re ready.' }); return; }
        // Fall through to normal classification so the user can change topics.
      }

      // General support-discussion follow-up: the previous support answer ended
      // with a question ("what kind of restaurant are you?"), so a plain reply
      // ("Quick service Mexican") is CONTINUING that discussion, not a new command.
      // Re-answer in context (history carries the thread) instead of cold-classifying
      // it into a clarify dead end. A reply that IS a clear command falls through.
      const supportPending = pendingRef.current;
      if (supportPending?.kind === 'pending_support_followup' && !isPendingExpired(supportPending)) {
        if (isSupportCancel(text)) {
          clearPending();
          pushMessage({ role: 'assistant', text: 'No problem — ask me anytime.' });
          return;
        }
        // The model decides continuation (dialog === 'continue'); we only reach here
        // when it said so (a "new" reply already cleared this pending up top). If the
        // router call failed, fall back to the short-plain-answer heuristic so a stale
        // discussion still can't hijack a new request ("Can you check for duplicates?").
        if (dialogContinue || (routerFailed && isPlainShortAnswer(text))) {
          clearPending();
          await answerSupport(text, storeId, {
            support_intent: supportPending.intent || 'general',
            needs_setup_context: !!supportPending.needs_setup_context,
          });
          return;
        }
        clearPending();
      }

      // PAR-suggestion follow-ups, resolved BEFORE the template-create resolver
      // so an affirmative isn't re-read as "create a template". Handled here (not
      // in ciFollowups) because it drives the in-place PAR flow.
      const parPending = pendingRef.current;

      // "Which order template?" → match the reply to one of the offered ones.
      if (parPending?.kind === 'pending_par_template_pick' && !isPendingExpired(parPending)) {
        if (/\b(no|not now|nope|nah|later|never ?mind|cancel|stop|forget it|none)\b/i.test(text)) {
          clearPending();
          pushMessage({ role: 'assistant', text: 'No problem — just tell me which template (or open one) when you’d like PARs set.' });
          return;
        }
        const candidates = Array.isArray(parPending.candidates) ? parPending.candidates : [];
        const matched = matchTemplatesToCommand(text, candidates.map((c) => ({ id: c.id, name: c.name })));
        const pick = matched.length === 1
          ? candidates.find((c) => String(c.id) === String(matched[0].id))
          : null;
        if (pick) {
          clearPending();
          await gateForTarget({ source: pick.source, templateId: pick.id, templateName: pick.name });
          return;
        }
        // Couldn't tell which one — re-offer the same choices.
        pushMessage({
          role: 'assistant',
          text: 'I didn’t catch which template. Tap one of these:',
          choices: candidates.slice(0, 8).map((c) => ({ key: `tpl:${c.id}`, label: c.name })),
        });
        return;
      }

      if (parPending?.kind === 'pending_par_suggest' && !isPendingExpired(parPending)) {
        if (/\b(no|not now|nope|nah|later|never ?mind|cancel|stop|forget it)\b/i.test(text)) {
          clearPending();
          pushMessage({ role: 'assistant', text: 'No problem — I’ll leave the PARs as they are. Ask any time and I’ll suggest starter PARs for the beverages in your order templates.' });
          return;
        }
        if (/\b(yes|yep|yeah|yup|sure|ok|okay|please|do it|go ahead|sounds good|suggest|set them|set the pars)\b/i.test(text)) {
          const target = parPending.target;
          clearPending();
          await presentParSuggestions(target);
          return;
        }
        // Anything else: drop the pending and fall through to normal handling.
        clearPending();
      }

      // Template-format conversion follow-ups (which template? convert or copy?),
      // resolved before the PAR / create resolvers so a "convert it" / "make a
      // copy" answer isn't re-read as another intent.
      const convertPending = pendingRef.current;
      if (convertPending?.kind === 'pending_template_convert' && !isPendingExpired(convertPending)) {
        // Still choosing which template (named one matched several).
        if (convertPending.awaiting === 'template_pick') {
          if (convertModeFromReply(text) === 'cancel') {
            clearPending();
            pushMessage({ role: 'assistant', text: 'Okay — no changes made. Tell me which template to convert whenever you like.' });
            return;
          }
          const candidates = Array.isArray(convertPending.candidates) ? convertPending.candidates : [];
          const matched = matchTemplatesToCommand(text, candidates.map((c) => ({ id: c.id, name: c.name })));
          const pick = matched.length === 1
            ? candidates.find((c) => String(c.id) === String(matched[0].id))
            : null;
          if (pick) {
            askConvertMode({
              templateId: pick.id,
              templateName: pick.name,
              currentFormat: pick.currentFormat || null,
              command: convertPending.command || text,
            });
            return;
          }
          pushMessage({
            role: 'assistant',
            text: 'I didn’t catch which template. Tap one of these:',
            choices: candidates.slice(0, 8).map((c) => ({ key: `cvt:${c.id}`, label: c.name })),
          });
          return;
        }
        // Awaiting the convert-or-copy choice.
        const mode = convertModeFromReply(text);
        if (mode === 'cancel') {
          clearPending();
          pushMessage({ role: 'assistant', text: 'Okay — I left the template as it is.' });
          return;
        }
        if (mode === 'convert_in_place' || mode === 'make_copy') {
          clearPending();
          if (!storeId) {
            pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' });
            return;
          }
          await previewTemplateConvert(convertPending, mode, storeId);
          return;
        }
        // Unrecognized — re-offer the two choices.
        pushMessage({
          role: 'assistant',
          text: 'Would you like me to convert the existing template, or make a copy in the other format?',
          choices: convertModeChips(),
        });
        return;
      }

      // "Set / suggest / add PARs" (or a PAR question) → the AI PAR reasoning flow.
      // It finds the template from the command (or the open editor, or asks which),
      // so it now works from anywhere, not only with the editor open.
      if (matchSetParsIntent(text)) {
        await startParSuggestionFlow(text, storeId);
        return;
      }

      // In-chat report follow-ups: resolve the detail we asked for (which count /
      // which report / what period / dollar-value-or-quantities), then render
      // the report. Resolved before generic follow-ups so "last 30 days" / a
      // count name isn't re-classified.
      const reportPending = pendingRef.current;
      if (reportPending?.kind === 'pending_report' && !isReportPendingExpired(reportPending)) {
        const storeName = getReportStoreName();
        const createdBy = getReportCreatedBy();
        const requireStore = () => {
          if (storeId) return true;
          pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' });
          return false;
        };
        // The resolver runs FIRST; cancel words only count when the reply
        // didn't resolve — "no, the November count" is a correction, not a
        // cancel, and chip labels like "Walk-in No. 2 · …" contain "no".
        const cancelOrReask = (reaskText, choices) => {
          if (isReportCancel(text)) {
            clearPending();
            pushMessage({ role: 'assistant', text: 'No problem — just ask whenever you’d like that report.' });
            return;
          }
          pushMessage({ role: 'assistant', text: reaskText, choices });
        };

        // "Dollar value or counted quantities?" for the ambiguous "inventory total".
        if (reportPending.awaiting === 'kind') {
          const kind = resolveReportKindReply(text);
          if (!kind) {
            cancelOrReask('Dollar value or counted quantities?', reportKindChips());
            return;
          }
          clearPending();
          if (!requireStore()) return;
          await offerReportQuery({ ...(reportPending.query || {}), report_type: kind, needs_kind_clarification: false }, storeId);
          return;
        }

        // "Which report?" when the classifier knew it was a data question but
        // not which report answers it.
        if (reportPending.awaiting === 'type') {
          const type = resolveReportTypeReply(text);
          if (!type) {
            cancelOrReask('Which report — tap one below?', reportTypeChips());
            return;
          }
          clearPending();
          if (!requireStore()) return;
          await offerReportQuery({ ...(reportPending.query || {}), report_type: type }, storeId);
          return;
        }

        // Which completed count to answer from (value / totals / review).
        if (reportPending.awaiting === 'count') {
          const session = resolveInventoryCountReply(text, reportPending.sessions);
          if (!session) {
            cancelOrReask('I didn’t catch which count — tap one below, or say “the latest”.', inventoryCountChips(reportPending.sessions));
            return;
          }
          clearPending();
          if (!requireStore()) return;
          // 'inventory_totals' was also the pre-report-query topic id for the
          // value report; treat legacy pendings (no query) as value requests.
          const type = (reportPending.report === 'inventory_totals' && !reportPending.query)
            ? 'inventory_value'
            : reportPending.report;
          const result = await generateCountReport(type, { session, query: reportPending.query, storeId });
          await renderReport(result);
          return;
        }

        // Comparison: pick a count for period A, then period B, then compare.
        if (reportPending.awaiting === 'compare_count') {
          const session = resolveInventoryCountReply(text, reportPending.sessions);
          if (!session) {
            cancelOrReask('I didn’t catch which count — tap one below, or say “the latest”.', inventoryCountChips(reportPending.sessions));
            return;
          }
          if (!requireStore()) return;
          const picked = { ...(reportPending.picked || {}), [reportPending.slot]: session };
          if (reportPending.slot === 'a' && (reportPending.sessionsB || []).length !== 1) {
            setPending({ ...reportPending, slot: 'b', sessions: reportPending.sessionsB, picked });
            pushMessage({
              role: 'assistant',
              text: `Got it — “${session.name}” for ${reportPending.rangeA.label}. Which count for ${reportPending.rangeB.label}?`,
              choices: inventoryCountChips(reportPending.sessionsB),
            });
            return;
          }
          if (reportPending.slot === 'a') picked.b = reportPending.sessionsB[0];
          clearPending();
          const result = await generateInventoryValueComparison({
            supabase, storeId, sessionA: picked.a, sessionB: picked.b,
            labelA: reportPending.rangeA?.label || '', labelB: reportPending.rangeB?.label || '',
            scope: reportPending.query?.scope || null, storeName, createdBy,
          });
          await renderReport(result, reportPending.report !== 'inventory_value'
            ? 'I compare periods by dollar value — counted quantities span mixed units, so value is the comparable measure.'
            : '');
          return;
        }

        // Which saved End of Period report to answer from.
        if (reportPending.awaiting === 'eop') {
          const eopRow = resolveEopReportReply(text, reportPending.reports);
          if (!eopRow) {
            cancelOrReask('I didn’t catch which report — tap one below, or say “the latest”.', eopReportChips(reportPending.reports));
            return;
          }
          clearPending();
          if (!requireStore()) return;
          const result = generateEopCogsReport({ eopRow, storeName, createdBy });
          await renderReport(result);
          return;
        }

        if (reportPending.report === 'purchase_history' && reportPending.awaiting === 'period') {
          const period = resolvePurchasePeriodReply(text);
          if (!period) {
            cancelOrReask('Pick a period below, or say something like “last 90 days”.', purchaseHistoryChips());
            return;
          }
          clearPending();
          if (!requireStore()) return;
          const result = await generatePurchaseReport({
            query: reportPending.query, storeId, sinceDays: period.days, periodLabel: period.label,
          });
          await renderReport(result);
          return;
        }
        if (reportPending.report === 'price_changes' && reportPending.awaiting === 'range') {
          // Relative presets first, then month names ("August") via the shared
          // date parser so a user can answer in their own words.
          const monthReply = parseDateRanges(text)[0];
          const range = resolvePriceRangeReply(text)
            || (monthReply ? { startDate: monthReply.startDate, endDate: monthReply.endDate, label: monthReply.label } : null);
          if (!range) {
            cancelOrReask('Pick a range below, or say something like “last 30 days” or “this year”.', priceRangeChips());
            return;
          }
          clearPending();
          if (!requireStore()) return;
          const q = reportPending.query || {};
          const result = await generatePriceChangeReport({
            supabase, storeId, startDate: range.startDate, endDate: range.endDate,
            rangeLabel: range.label, storeName, createdBy,
            scope: q.scope, priceScopeKind: q.priceScopeKind, priceDirection: q.priceDirection,
            priceInsight: q.priceInsight, priceSort: q.priceSort,
          });
          await renderReport(result);
          return;
        }
        // Unknown state — drop it and let the message classify normally.
        clearPending();
      }

      // Intent pass of an attribute-scoped delete ("delete all the freezer
      // items" — which group did I mean?). Resolved before generic follow-ups,
      // alongside the report pendings: the answer picks the reading to scan
      // (the list pass), backs out, or is re-asked. No expiry check — this
      // pending shape carries asked_at, not the ciFollowups expires_at.
      const deleteScopePending = pendingRef.current;
      if (deleteScopePending?.kind === 'pending_delete_scope') {
        const scopeReply = resolveDeleteScopeReply(text, deleteScopePending);
        if (scopeReply?.action === 'cancel') {
          clearPending();
          pushMessage({ role: 'assistant', text: 'No problem — nothing has been changed.' });
          return;
        }
        if (!scopeReply) {
          // Unrecognized — re-ask with the same readings and keep the pending.
          pushMessage({
            role: 'assistant',
            text: intentConfirmMessage(deleteScopePending.phrase, deleteScopePending.interpretations),
            choices: deleteScopeChips(deleteScopePending),
          });
          return;
        }
        clearPending();
        if (!storeId) {
          pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' });
          return;
        }
        const pickedReading = scopeReply.interpretation;
        // A description reading scans item NAMES for the phrase; the attribute
        // readings scan their own field. The scope label restates the confirmed
        // reading on the review card.
        const pickedScopeLabel = pickedReading.field === 'description'
          ? `with names containing “${pickedReading.value}”`
          : pickedReading.label;
        await scanAttributeScopedDelete({
          filters: [{ field: pickedReading.field, value: pickedReading.value }],
          scopeLabel: pickedScopeLabel,
          storeId,
        });
        return;
      }

      // A clarifying question from the previous turn left a pending context.
      // Resolve short answers ("yes" / "order template" / "combined" / "separate
      // by vendor" / "never mind") against it instead of re-classifying — this is
      // what stops the create/edit/learn re-clarification loop on follow-ups.
      const pending = pendingRef.current;
      if (pending && !isPendingExpired(pending)) {
        const resolved = resolveFollowup(text, pending);
        if (resolved.action === 'cancel') {
          clearPending();
          pushMessage({ role: 'assistant', text: resolved.reply });
          return;
        }
        if (resolved.action === 'ask') {
          let pend = resolved.pending;
          let choices = pend.choices;
          // When we're about to ask "which vendor?", offer the actual vendors
          // that still need an order template (captured now, while none exist —
          // setup-progress stops listing them once the first one is created).
          if (pend.awaiting === 'vendor_choice' && !Array.isArray(pend.vendorCandidates)) {
            const candidates = await fetchOrderVendorCandidates(storeId);
            if (candidates.length) {
              pend = { ...pend, vendorCandidates: candidates };
              choices = vendorChoiceChips(candidates);
            }
          }
          setPending(pend);
          pushMessage({ role: 'assistant', text: resolved.reply, choices });
          return;
        }
        if (resolved.action === 'create') {
          const prevPending = pendingRef.current;
          clearPending();
          if (!storeId) {
            pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' });
            return;
          }
          // Per-vendor order template: carry the remaining vendors so we can offer
          // the next one once this template is created (one vendor at a time).
          const meta = {};
          if (resolved.createdVendor) {
            meta.createdVendor = resolved.createdVendor;
            const candidates = Array.isArray(prevPending?.vendorCandidates) ? prevPending.vendorCandidates : [];
            meta.orderVendorQueue = candidates.filter((v) => v !== resolved.createdVendor);
          }
          await previewTemplateCreate(resolved.effectiveCommand, storeId, meta);
          return;
        }
        // passthrough → fall through to normal classification; terminal handlers
        // below set or clear the pending context as appropriate.
      }

      // Report questions are matched here, before the classifier, so "what
      // prices changed" reliably suggests the price-change *report* rather than
      // the price-*update* flow it sits beside. parseReportQuestion also pulls
      // out the date range / category / vendor so "inventory value for August"
      // can be answered directly; phrasings it misses come back from the model
      // classifier as route='report_query' below.
      const reportQuery = parseReportQuestion(text);
      if (reportQuery) {
        await offerReportQuery(reportQuery, storeId);
        return;
      }

      // A delete scoped by WHEN the items were added ("delete everything I added
      // today", "remove the items from July 28th") is matched before the dedup check
      // below, because it is a different question: the date is a hard filter and names
      // are never compared. Previously this fell through to the look-alike scan, which
      // matched no item name and then offered the whole catalog's duplicates instead.
      const scopedDelete = matchScopedItemDelete(text);
      if (scopedDelete) {
        clearPending();
        if (scopedDelete.kind === 'ambiguous_date') {
          pushMessage({ role: 'assistant', text: ambiguousDateMessage(scopedDelete.ranges) });
          return;
        }
        if (!storeId) { pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' }); return; }
        await previewScopedItemDelete(scopedDelete, storeId);
        return;
      }

      // A delete scoped by WHO the items belong to — one vendor, category, or
      // storage area ("delete all the Asia Imports items", "remove everything in
      // the Walk-In") — matched after the date scope above, for the same reason:
      // the attribute is a hard filter and names are never compared. The phrase
      // is resolved server-side against the store's actual values, so "Asia
      // Imports" lands on the vendor without the user saying the word "vendor",
      // and an ambiguous or unknown phrase gets a question, never a guessed list.
      const attributeDelete = matchAttributeScopedItemDelete(text);
      if (attributeDelete) {
        clearPending();
        if (!storeId) { pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' }); return; }
        await previewAttributeScopedItemDelete(attributeDelete, storeId);
        return;
      }

      // A delete pointing at the import EVENT itself — "delete the items from the
      // recent import", "undo my last upload", "get rid of what I just added". No
      // clock or calendar wording to parse (the attribute matcher above refuses
      // these texts so "recent import" is never searched as a vendor name); the
      // server resolves the store's actual most recent burst of item creations
      // and the reply SAYS what window that turned out to be.
      const latestImportDelete = matchLatestImportDelete(text);
      if (latestImportDelete) {
        clearPending();
        if (!storeId) { pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' }); return; }
        await previewLatestBatchDelete(storeId);
        return;
      }

      // Deduplication is matched deterministically here, before the classifier, so a
      // request to "find / check / clean up duplicates" reliably STARTS the review
      // scan instead of depending on the model routing it to the deduplicate action
      // (which was landing on the how-to explanation instead of running). How-to
      // questions ("why do I have duplicates?") don't match and still get explained.
      if (matchDedupIntent(text)) {
        clearPending();
        if (!storeId) { pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' }); return; }
        await previewItemDedup(text, storeId);
        return;
      }

      // Instant navigation answers: a clear how-to / "where do I" question whose
      // topic has NO in-chat action (instant: true) is answered straight from
      // SUPPORT_TOPICS — path, deep link, no model round-trip. Topics with an
      // in-chat offer (items, prices, …) still go through the support answerer
      // so their answers carry the actionable chips. A message carrying a
      // pasted table is data to import, never a navigation question — keyword
      // overlap with the rows must not swallow it.
      if (!classification && !pasted) {
        const instantTopic = matchSupportTopic(text);
        if (instantTopic?.instant) {
          clearPending();
          pushMessage({
            role: 'assistant',
            text: instantTopic.answer,
            link: instantTopic.href ? { href: instantTopic.href, label: instantTopic.label } : undefined,
          });
          return;
        }
      }

      // Route the (new) request. Reuse the router's classification when it already
      // ran up top (the message was a new thread while a pending was open) so a new
      // request still costs a single model call; otherwise classify now.
      if (!classification) classification = await classifyMessage(text, pendingRef.current);
      const route = classification?.route;

      // Support / how-to questions are routed to the support answerer, which teaches
      // the workflow using Countertop product knowledge — NOT a one-line canned link
      // and NOT Setup Review. Does not require a selected store.
      if (route === 'support') {
        await answerSupport(text, storeId, classification);
        return;
      }

      // Adding new items: when the data is already IN the message (a pasted
      // table), import it right now — offering an upload would drop what the
      // user just handed over. Otherwise offer the in-chat file import as
      // before. A multi-row paste also overrides a stray single-item read of
      // one of its rows (`item_create_direct`). `unsupported_item_create` is
      // the pre-deploy classifier's old label for the same intent.
      if (route === 'item_import' || route === 'unsupported_item_create'
          || (route === 'item_create_direct' && pasted)) {
        if (pasted) {
          await runItemImportFromText({ detection: pasted, directive: extractTemplateDirective(pasted.prose) });
          return;
        }
        offerItemImport(text);
        return;
      }

      if (!route || route === 'clarify' || route === 'unsupported_destroy'
          || route === 'unsupported_destroy_scope'
          || route === 'unsupported_vendor_create'
          || route === 'unsupported_po_create') {
        clearPending();
        // The message carried a table even though the intent didn't classify —
        // offer to import the pasted rows instead of dead-ending. The offer
        // pending keeps the original message, so a "yes" imports THIS paste.
        if (pasted && (!route || route === 'clarify')) {
          setPending(buildImportOfferPending({ text }));
          pushMessage({ role: 'assistant', text: pastedImportOfferText(pasted.rowCount), choices: importOfferChips() });
          return;
        }
        pushMessage({ role: 'assistant', text: classification?.clarification_message || 'Could you rephrase that? I can build templates, bulk-edit items, edit a vendor, or edit and check the status of orders.' });
        return;
      }

      // The remaining routes act on a store's data, so a store must be selected.
      if (!storeId) {
        pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' });
        return;
      }

      if (route === 'setup_review') {
        await reviewSetup(text, storeId);
      } else if (route === 'template_create') {
        await previewTemplateCreate(text, storeId);
      } else if (route === 'template_convert') {
        await startTemplateConvert({ text, storeId });
      } else if (route === 'item_edit') {
        await previewItemEdit(text, storeId);
      } else if (route === 'item_dedup') {
        await previewItemDedup(text, storeId, { intent: classification?.action === 'destroy' ? 'delete' : 'dedup' });
      } else if (route === 'item_delete_scoped') {
        if (classification?.destroy_scope && classification.destroy_scope !== 'created_date') {
          // A delete scoped by one vendor / category / storage area, in wording the
          // deterministic matcher above missed. The classifier put the scope NAME in
          // extracted_item_name — resolve it through the same server-side resolver
          // (against the store's actual values), never against item names. The name
          // can itself be a list ("Asia Imports and Shamrock Foods") — split it
          // exactly like the deterministic matcher does.
          const phrase = classification?.extracted_item_name;
          if (!phrase) {
            pushMessage({
              role: 'assistant',
              text: 'Tell me which vendor, category, or storage area you mean and I’ll pull up exactly that batch to confirm.',
            });
            return;
          }
          // A model that read "the recent import" as a vendor name would send it
          // here and the resolver would answer "no vendor called recent import".
          // That wording is a time reference — route it to the latest-batch scan.
          if (matchesLatestImportWording(phrase)) {
            await previewLatestBatchDelete(storeId);
            return;
          }
          await previewAttributeScopedItemDelete({ phrase, phrases: splitScopePhrases(phrase) }, storeId);
          return;
        }
        // The classifier saw a delete scoped by when the items were added, in wording
        // the deterministic matcher above missed. Resolve the date from the phrase it
        // extracted — never from the whole sentence, so a stray date can't widen it.
        const datePhrase = classification?.extracted_date_phrase;
        const scope = scopeFromDatePhrase(datePhrase);
        if (!scope) {
          // No clock or calendar in the phrase — but "the recent import" / "just
          // now" still names a batch: the store's latest one. Resolve it from data.
          if (matchesLatestImportWording(datePhrase) || matchesLatestImportWording(text)) {
            await previewLatestBatchDelete(storeId);
            return;
          }
          pushMessage({
            role: 'assistant',
            text: 'I can delete the items you added on a particular day, over a recent stretch, or within a time range, but I couldn’t pin down when. Tell me the day, range, or time (for example “delete the items I added on July 28”, “the items added in the past 3 days”, or “the items I added in the last hour”) — or say “delete my latest import” and I’ll pull up the most recent batch.',
          });
          return;
        }
        await previewScopedItemDelete(scope, storeId);
      } else if (route === 'vendor_edit') {
        await previewVendorEdit(text, storeId);
      } else if (route === 'template_edit') {
        await previewTemplateEdit(text, storeId);
      } else if (route === 'po_edit') {
        await previewPurchaseOrderEdit(text, storeId);
      } else if (route === 'po_status') {
        await answerPurchaseOrderStatus(text, storeId);
      } else if (route === 'item_price_change') {
        await previewSinglePriceChange(classification, storeId);
      } else if (route === 'item_create_direct') {
        await previewSingleItemCreate(classification);
      } else if (route === 'report_query') {
        // The model saw a report-data question the local triggers missed.
        // Re-parse for dates/scopes and lean on the model's report_type when
        // the wording alone didn't pin one down.
        const parsed = parseReportQuestion(text, { fallbackType: classification?.report_type || null })
          || { report_type: classification?.report_type || null, ranges: [], comparison: false };
        await offerReportQuery(parsed, storeId);
      }
    } catch (err) {
      pushMessage({ role: 'assistant', text: formatErrorMessage(err, 'Something went wrong.'), tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  handleSendRef.current = handleSend;

  // Read-only Setup Review: ask the setup-progress function to review the store's
  // setup-progress steps (the same data the dashboard shows) and explain what to
  // do next. Nothing is changed — this only surfaces guidance.
  const reviewSetup = async (text, storeId) => {
    const payload = await post(setupProgressEndpoint(), {
      action: 'review',
      store_id: storeId,
      question: text,
    });
    const reply = payload?.reply || 'I reviewed your setup.';
    const rec = payload?.recommended_next_step;
    // If the recommended next step is to create an order/EOP template, stash a
    // pending context so "yes" / "End of Period" / "the Sysco one" resolves into
    // the create flow instead of being re-classified.
    const pending = buildPendingFromSetup({ text, payload });
    setPending(pending);
    pushMessage({
      role: 'assistant',
      text: reply,
      link: rec?.href ? { href: rec.href, label: `${rec.cta || 'Open'} →` } : undefined,
      choices: pending?.choices,
    });
  };

  // Signup review chips are an explicit user decision, so they do not need a
  // classifier round-trip. Yes invokes the existing read-only review directly;
  // Not now closes only this invitation and leaves Counter Intelligence ready.
  const handleChoice = async (choice) => {
    const action = signupReviewChoiceAction(choice);
    if (!action) {
      await handleSend(choice?.label || '');
      return;
    }
    if (busy) return;

    pushMessage({ role: 'user', text: choice.label });
    clearPending();
    if (action === 'dismiss') {
      pushMessage({ role: 'assistant', text: SIGNUP_REVIEW_DISMISS_REPLY });
      return;
    }

    const storeId = getStoreId();
    if (!storeId) {
      pushMessage({
        role: 'assistant',
        text: 'I can’t tell which store you’re in — pick a store and try again.',
      });
      return;
    }

    setBusy(true);
    try {
      await reviewSetup(SIGNUP_REVIEW_PROMPT, storeId);
    } catch (err) {
      pushMessage({
        role: 'assistant',
        text: formatErrorMessage(err, 'I couldn’t review your setup just now.'),
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  // Support / how-to answer. Routes the question through the support answerer, which
  // teaches the workflow from Countertop product knowledge. When the classifier says
  // the answer benefits from setup context, pull the store's setup-progress and pass
  // it in so the answer can note a relevant blocker. Falls back to the hardcoded
  // navigation snippet only if the support call fails.
  const answerSupport = async (text, storeId, classification) => {
    let setupContext = null;
    if (classification?.needs_setup_context && storeId) {
      try {
        setupContext = await post(setupProgressEndpoint(), { action: 'progress', store_id: storeId });
      } catch (_) { /* setup context is optional enrichment */ }
    }
    // Billing answers need the store's real subscription status + current pricing
    // so Counter Intelligence can quote the actual plan, price, trial days left,
    // and renewal date instead of generic guidance. Both are read-only RPCs.
    let subscriptionContext = null;
    if (classification?.needs_subscription_context && storeId) {
      try {
        const [subRes, priceRes] = await Promise.all([
          supabase.rpc('get_subscription_status', { p_store_id: storeId }),
          supabase.rpc('get_current_pricing'),
        ]);
        subscriptionContext = {
          subscription: subRes?.data || null,
          pricing: priceRes?.data || null,
        };
      } catch (_) { /* subscription context is optional enrichment */ }
    }
    // Invoice support answers should reveal this store's actual permanent
    // receiving address. Read it through the same authenticated processor used
    // by the Invoices page so the assistant never guesses or exposes another
    // store's address.
    let invoiceContext = null;
    if ((classification?.support_intent || 'general') === 'invoices' && storeId) {
      try {
        const inbox = await post(invoiceProcessorEndpoint(), { action: 'get_address', storeId });
        invoiceContext = { address: inbox?.address || null };
      } catch (_) { /* invoice context is optional enrichment */ }
    }
    try {
      // Structured help-tooltip context for this turn (utils/helpTopics.js):
      // the authored question is `command`; the payload carries the topic id,
      // page/section and safe app context so the answerer can be specific
      // without inferring anything from tooltip prose.
      const helpContext = helpContextRef.current;
      helpContextRef.current = null;
      const res = await post(classifyEndpoint(), {
        action: 'support',
        command: text,
        support_intent: classification?.support_intent || 'general',
        setup_context: setupContext,
        subscription_context: subscriptionContext,
        invoice_context: invoiceContext,
        help_context: helpContext || undefined,
        // Where the user is. The answerer looks the surface up in the shared page
        // registry and answers from that page — naming its own controls, resolving
        // "these items" against what is on screen, and saying where a workflow
        // lives when it is somewhere else. On the Add Items setup surface it
        // teaches THIS page's tools (and the in-chat import) instead of routing a
        // locked new user to pages the onboarding gate bounces back to setup.
        surface: surfaceForApi(),
        viewport: viewportForApi(),
        // Live dashboard status published by the AI Command Center (structured
        // facts, not prose) so "what needs my attention?" answers from the same
        // data the page shows. Absent on every other surface.
        dashboard_context: (typeof window !== 'undefined' && window.__accDashboardStatus) || undefined,
        // Recent turns so a continued discussion is answered in context — e.g. after
        // "what kind of restaurant are you?", a reply of "Quick service Mexican" is
        // understood as answering that, not a fresh, contextless question.
        history: recentConversationForClassify(),
      });
      const reply = res?.reply || 'Here’s how that works in Countertop.';
      const intent = res?.support_intent || classification?.support_intent || 'general';
      // A ⓘ help ask is a request to UNDERSTAND one control, not to start a task.
      // Tacking "want me to import your items?" onto an explanation of what an
      // inventory item is answers a question nobody asked. So a tooltip-sourced
      // answer only carries an action offer when the control the user tapped is
      // itself about that action; a question typed into the chat box still gets
      // the offer as before.
      const helpTopicId = helpContext?.help_topic_id || null;
      // Help-tooltip analytics: metadata only (topic id + page), never the
      // conversation. Guarded so analytics can never break the chat.
      if (helpContext?.help_topic_id) {
        try {
          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'counterintelligence_help_answered', {
              help_topic_id: helpContext.help_topic_id,
              page: helpContext.page,
              template_type: helpContext.template_type,
            });
          }
        } catch (_) { /* non-fatal */ }
      }
      // "How do I add my items?" — the answer explains the workflow; follow it
      // with the actionable in-chat offer so the user can upload right here.
      //
      // Guarded, because this offer used to fire on EVERY 'items' answer. A
      // definition question routed here ("What is an inventory item, and how is
      // it different from a vendor product?") got the workflow answer buried
      // under "I can read PDFs, Excel and CSV spreadsheets…" — an import pitch
      // nobody asked for. Definitions now classify as 'item_model' instead.
      if (intent === 'items' && shouldOfferAction('items', helpTopicId)) {
        setPending(buildImportOfferPending({ text }));
        pushMessage({
          role: 'assistant',
          text: reply,
          link: res?.href ? { href: res.href, label: res.label || 'Open →' } : undefined,
          choices: importOfferChips(),
        });
        return;
      }
      // "How do I update my prices?" / "what happens when a price changes?" — the
      // answer explains the workflow; follow it with the actionable in-chat offer
      // so the user can update prices from an invoice right here, with approval.
      if (intent === 'prices' && shouldOfferAction('prices', helpTopicId)) {
        setPending(buildPriceUpdateOfferPending({ text }));
        pushMessage({
          role: 'assistant',
          text: reply,
          link: res?.href ? { href: res.href, label: res.label || 'Open →' } : undefined,
          choices: priceUpdateOfferChips(),
        });
        return;
      }
      // "Why do I have duplicates?" / "how does deleting work?" — the answer
      // explains the rules; follow it with the actionable offer so the user can run
      // the look-alike scan right here (they tap "Find duplicates now" or just say
      // "find my duplicates"), instead of the explanation dead-ending.
      if (intent === 'duplicates' && shouldOfferAction('duplicates', helpTopicId)) {
        setPending(buildDedupOfferPending({ text }));
        pushMessage({
          role: 'assistant',
          text: `${reply}\n\n${DEDUP_OFFER_TEXT}`,
          link: res?.href ? { href: res.href, label: res.label || 'Open →' } : undefined,
          choices: dedupOfferChips(),
        });
        return;
      }
      // If this how-to is about creating a template and the answer ended with a
      // question, stash a pending context so the user's next short reply ("yes" /
      // "order template" / "combined") goes straight to the create flow. Otherwise,
      // if the answer simply ended with a question, hold a general support-followup
      // pending so a plain reply ("Quick service Mexican") continues the discussion
      // in context instead of dead-ending in a re-classification.
      const pending = buildPendingFromSupport({ text, reply, supportIntent: intent })
        || buildSupportFollowupPending({ text, reply, intent, needsSetupContext: classification?.needs_setup_context });
      setPending(pending);
      pushMessage({
        role: 'assistant',
        text: reply,
        link: res?.href ? { href: res.href, label: res.label || 'Open →' } : undefined,
        choices: pending?.choices,
      });
    } catch (err) {
      // Emergency fallback to the canned navigation snippet from SUPPORT_TOPICS.
      const topic = matchSupportTopic(text);
      if (topic) {
        pushMessage({
          role: 'assistant',
          text: topic.answer,
          link: topic.href ? { href: topic.href, label: topic.label } : undefined,
        });
      } else {
        pushMessage({ role: 'assistant', text: formatErrorMessage(err, 'Something went wrong.'), tone: 'error' });
      }
    }
  };

  // Fetch the vendors that still need an order template. This data exists only
  // while the store has no order templates yet, so we capture it up front (at the
  // "which vendor?" step) to drive the one-vendor-at-a-time loop.
  const fetchOrderVendorCandidates = async (storeId) => {
    if (!storeId) return [];
    try {
      const p = await post(setupProgressEndpoint(), { action: 'progress', store_id: storeId });
      const step = (p?.steps || []).find((s) => s && s.key === 'missing_order_templates');
      return Array.isArray(step?.vendors) ? step.vendors.filter(Boolean) : [];
    } catch (_) {
      return [];
    }
  };

  // ── AI PAR suggestion (alcohol) ──
  // Counter Intelligence can only reason about PARs for items it recognises as
  // alcohol (wine, liquor, beer, draft, RTD). It finds the ordering template the
  // user means — named in the command, the one open in the editor, or chosen from
  // a list — runs the deterministic alcohol PAR rules over its rows, asks to
  // confirm, then proposes starter PARs in the same review console used for
  // template creation. Apply writes through the open editor when that template is
  // open, otherwise straight to its count_items. Nothing is saved until the user
  // reviews and applies.
  const parPendingExpiry = () => new Date(Date.now() + 30 * 60 * 1000).toISOString();

  // Classify the rows, keep only the alcohol, and de-duplicate by item — PARs
  // are shared across an item's location rows in this app, so the same bottle in
  // two spots should be proposed once. Counts are recomputed from the deduped set
  // so the "I found N" message matches the rows shown.
  const collectAlcohol = (rows) => {
    const { alcohol } = proposeParsForItems(rows || []);
    const seen = new Set();
    const distinct = [];
    const counts = { wine: 0, liquor: 0, beer: 0, draft_beer: 0, rtd: 0 };
    for (const row of alcohol) {
      const id = row.item.inventory_item_id ?? row.item.id;
      const key = id != null ? String(id) : `name:${row.proposal.item_name}::${row.item.location || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      distinct.push(row);
      counts[row.proposal.alcohol_category] = (counts[row.proposal.alcohol_category] || 0) + 1;
    }
    return { alcohol: distinct, counts };
  };

  // ── Target resolution: find the template to act on ──────────────────────────
  // A "target" is { source: 'editor' | 'db', templateId, templateName }. The flow
  // works from anywhere: if the command names a template we find it in the store's
  // ordering templates; otherwise we use the open editor, or ask which template.
  const fetchOrderingTemplates = async (storeId) => {
    if (!storeId) return [];
    try {
      const { data, error } = await supabase
        .from('count_sessions')
        .select('id, name, order_calculations_enabled, count_purpose')
        .eq('store_id', storeId)
        .eq('is_template', true)
        .not('status', 'in', '(cancelled,deleted)')
        .order('name', { ascending: true });
      if (error) return [];
      return (data || [])
        .filter((t) => t.order_calculations_enabled === true || t.count_purpose === 'ordering')
        .map((t) => ({ id: t.id, name: (t.name || 'Untitled template').trim() }));
    } catch (_) {
      return [];
    }
  };

  // Read a template's rows straight from count_items into the same shape the
  // editor exposes (getCountCreationAiRows), so the alcohol engine reasons over
  // them identically whether or not the editor is open.
  const loadDbTemplateRows = async (templateId) => {
    if (templateId == null) return [];
    try {
      const { data, error } = await supabase
        .from('count_items')
        .select('*')
        .eq('count_session_id', templateId)
        .order('count_position', { ascending: true });
      if (error) return [];
      return (data || []).map((ci) => {
        const snap = ci.item_snapshot || {};
        return {
          inventory_item_id: ci.inventory_item_id,
          id: ci.inventory_item_id,
          count_item_id: ci.id,
          description: snap.description || snap.name || '',
          name: snap.name || snap.description || '',
          category: snap.category || '',
          vendor: snap.vendor || '',
          brand: snap.brand || '',
          location: ci.location || snap.location || 'Imported Items',
          par_level: ci.par_level ?? null,
          par_unit: ci.par_unit || snap.unit || 'each',
          min_order_qty: ci.min_order_qty ?? null,
          min_order_unit: ci.min_order_unit || snap.unit || 'each',
          unit: snap.unit || 'each',
        };
      });
    } catch (_) {
      return [];
    }
  };

  const getTargetRows = async (target) => {
    if (!target) return [];
    if (target.source === 'editor') {
      const active = getActiveTemplate();
      return active?.rows || [];
    }
    return loadDbTemplateRows(target.templateId);
  };

  // Decide which ordering template(s) the user means: a named match wins, else the
  // open editor, else the store's templates (one → that one, many → ask).
  const resolveParTargets = async (command, storeId) => {
    const active = getActiveTemplate();
    const activeOrdering = (active && active.typeLabel === 'Ordering') ? active : null;
    const dbTemplates = await fetchOrderingTemplates(storeId);

    const universe = [];
    const seen = new Set();
    if (activeOrdering && activeOrdering.id != null) {
      universe.push({ source: 'editor', templateId: activeOrdering.id, templateName: activeOrdering.name });
      seen.add(String(activeOrdering.id));
    }
    for (const t of dbTemplates) {
      if (seen.has(String(t.id))) continue;
      universe.push({ source: 'db', templateId: t.id, templateName: t.name });
      seen.add(String(t.id));
    }

    const matched = matchTemplatesToCommand(command, universe.map((u) => ({ id: u.templateId, name: u.templateName })));
    if (matched.length) {
      const byId = new Map(universe.map((u) => [String(u.templateId), u]));
      const targets = matched.map((m) => byId.get(String(m.id))).filter(Boolean);
      if (targets.length === 1) return { status: 'one', target: targets[0] };
      if (targets.length > 1) return { status: 'many', candidates: targets };
    }

    // No name match — prefer the open ordering template (even an unsaved one).
    if (activeOrdering) {
      return { status: 'one', target: { source: 'editor', templateId: activeOrdering.id, templateName: activeOrdering.name } };
    }
    if (dbTemplates.length === 1) return { status: 'one', target: universe.find((u) => String(u.templateId) === String(dbTemplates[0].id)) };
    if (dbTemplates.length > 1) return { status: 'many', candidates: universe };
    return { status: 'none' };
  };

  // ── Entry point: resolve a target, then gate or ask which template ──────────
  const startParSuggestionFlow = async (command, storeId) => {
    clearPending();
    if (!storeId) {
      pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' });
      return;
    }
    const res = await resolveParTargets(command, storeId);
    if (res.status === 'none') {
      pushMessage({
        role: 'assistant',
        text: 'A PAR is your target on-hand level for an item. I can suggest starter PARs automatically — but only for beverages: liquor, wine, beer, and bottled/canned items. I couldn’t find an order template in this store to add them to. Create an order template first, then ask me again.',
        link: { href: '/count.html?view=overview', label: 'Open Counts →' },
      });
      return;
    }
    if (res.status === 'many') {
      setPending({
        kind: 'pending_par_template_pick',
        awaiting: 'template_pick',
        candidates: res.candidates.map((t) => ({ id: t.templateId, name: t.templateName, source: t.source })),
        created_at: new Date().toISOString(),
        expires_at: parPendingExpiry(),
      });
      pushMessage({
        role: 'assistant',
        text: 'I can suggest starter PARs (beverages only: liquor, wine, beer, bottled/canned). Which order template should I add them to?',
        choices: res.candidates.slice(0, 8).map((t) => ({ key: `tpl:${t.templateId}`, label: t.templateName })),
      });
      return;
    }
    await gateForTarget(res.target);
  };

  // Count the beverages in the chosen template and ask to proceed.
  const gateForTarget = async (target) => {
    const rows = await getTargetRows(target);
    const { alcohol, counts } = collectAlcohol(rows);
    if (!alcohol.length) {
      pushMessage({
        role: 'assistant',
        text: `I can only set PARs for liquor, wine, beer, and bottled items, and I didn’t find any of those in “${target.templateName}”. Add some beverages to that template, or pick a different one.`,
      });
      return;
    }
    const breakdown = describeCounts(counts);
    setPending({
      kind: 'pending_par_suggest',
      awaiting: 'par_confirm',
      target,
      templateName: target.templateName,
      created_at: new Date().toISOString(),
      expires_at: parPendingExpiry(),
    });
    pushMessage({
      role: 'assistant',
      text: `I can only set PARs for liquor, wine, beer, and bottled items. I found ${alcohol.length} of those in “${target.templateName}”${breakdown ? ` (${breakdown})` : ''}. Want me to suggest starter PARs you can review and edit?`,
      choices: [
        { key: 'par_yes', label: 'Yes, suggest PARs' },
        { key: 'par_no', label: 'Not now' },
      ],
    });
  };

  // Build and show the review-console card from the target template's CURRENT
  // alcohol rows (re-read live). starter_par → PAR, in the count
  // unit the rules chose (bottle / can / keg) so the editor and order math stay
  // consistent. The card carries the target so apply writes to the right place.
  const presentParSuggestions = async (target) => {
    if (!target) {
      pushMessage({ role: 'assistant', text: 'I lost track of which template to use — ask me to set PARs again.' });
      return;
    }
    const rows = await getTargetRows(target);
    if (target.source === 'editor' && !rows.length) {
      pushMessage({ role: 'assistant', text: 'The order template isn’t open anymore — reopen it (or name it) and ask me to set PARs again.' });
      return;
    }
    const { alcohol } = collectAlcohol(rows);
    if (!alcohol.length) {
      pushMessage({ role: 'assistant', text: `I couldn’t find beverages to set PARs for in “${target.templateName}” anymore.` });
      return;
    }
    const items = alcohol
      .map(({ item, proposal }) => ({
        inventory_item_id: item.inventory_item_id ?? item.id,
        item_name: proposal.item_name,
        vendor: item.vendor || '',
        category: item.category || '',
        location: item.location || 'Imported Items',
        par_level: proposal.starter_par == null ? '' : proposal.starter_par,
        par_unit: proposal.par_unit,
        sub_label: `${PRETTY_CATEGORY[proposal.alcohol_category]} · ${Math.round((proposal.confidence || 0) * 100)}% sure`,
        reason: proposal.reason,
        confidence: proposal.confidence,
      }))
      .sort((a, b) =>
        (a.location || '').localeCompare(b.location || '') ||
        (a.item_name || '').localeCompare(b.item_name || ''));
    pushMessage({
      role: 'assistant',
      text: 'Review these starter PARs — edit any value, remove anything you don’t want, then apply. You can change them again later in the editor.',
      card: {
        kind: 'par_suggest',
        templateName: target.templateName,
        templateId: target.templateId,
        source: target.source,
        items,
        status: 'pending',
      },
    });
  };

  // Persist reviewed PARs straight to a template's count_items (when the
  // editor is not the one open), mirroring the editor's own per-item write +
  // parent propagation, then nudge any open views to refresh. Returns the count
  // of items written.
  const applyParsToDb = async (templateId, items) => {
    let written = 0;
    for (const it of items || []) {
      const invId = it.inventory_item_id;
      if (invId == null) continue;
      const fields = {};
      if (it.par_level !== '' && it.par_level != null) {
        fields.par_level = Number(it.par_level);
        fields.par_unit = it.par_unit || 'each';
        // Applying the reviewed suggestion is an explicit PAR decision even
        // when the accepted number happens to equal the automatic default 1.
        // Clear its provenance so SET PAR does not keep asking afterward.
        fields.par_defaulted = false;
      }
      if (Object.keys(fields).length === 0) continue;
      const { error } = await supabase
        .from('count_items')
        .update({ ...fields, updated_at: new Date().toISOString() })
        .eq('count_session_id', templateId)
        .eq('inventory_item_id', invId);
      if (error) throw new Error(error.message || 'Failed to save PARs.');
      try {
        await propagateGlobalCountFieldsToParents({ sessionId: templateId, inventoryItemId: invId, ...fields });
      } catch (_) { /* propagation to the parent template/item is best-effort */ }
      written += 1;
    }
    try { document.dispatchEvent(new CustomEvent('refreshCountInterface', { detail: { sessionId: templateId } })); } catch (_) {}
    return written;
  };

  // ── Template format conversion (ordering ⇄ End of Period) ──────────────────
  // Reachable from the chat ("convert my End of Period template to ordering") and
  // from the editor's "Transition to order template" header button. We always ask
  // convert-vs-copy first, then preview the change, then apply. After creating an
  // ordering template we offer to help set starter PARs.

  // All of the store's templates we can convert, with their detected format.
  const fetchConvertibleTemplates = async (storeId) => {
    if (!storeId) return [];
    try {
      const { data, error } = await supabase
        .from('count_sessions')
        .select('id, name, order_calculations_enabled, count_purpose, ordering_cycle_config')
        .eq('store_id', storeId)
        .eq('is_template', true)
        .not('status', 'in', '(cancelled,deleted)')
        .order('name', { ascending: true });
      if (error) return [];
      return (data || []).map((t) => {
        const preset = t.ordering_cycle_config && t.ordering_cycle_config.preset;
        const currentFormat = (t.order_calculations_enabled === true || t.count_purpose === 'ordering')
          ? 'ordering'
          : (t.count_purpose === 'period_end' || preset === 'eop_454' || preset === 'eop_544')
            ? 'end_of_period'
            : 'count';
        return { id: t.id, name: (t.name || 'Untitled template').trim(), currentFormat };
      });
    } catch (_) {
      return [];
    }
  };

  // Ask the convert-or-copy question for a resolved target, stashing a pending
  // context so the next reply (or chip) drives the preview.
  const askConvertMode = ({ templateId = null, templateName = 'this template', currentFormat = null, targetFormat = null, command = '' }) => {
    const target = targetFormat
      || (currentFormat === 'ordering' ? 'end_of_period' : currentFormat ? 'ordering' : null);
    setPending(buildPendingTemplateConvert({ templateId, templateName, currentFormat, targetFormat: target, command }));
    const intoPhrase = target ? `into ${formatPhrase(target)}` : 'into the other format';
    pushMessage({
      role: 'assistant',
      text: `Do you want to convert “${templateName}” ${intoPhrase}, or make a copy of it in the other format? Converting changes the existing template; a copy leaves the original as-is.`,
      choices: convertModeChips(),
    });
  };

  const startTemplateConvert = async ({ text = '', storeId = getStoreId(), templateId = null, templateName = null, currentFormat = null, targetFormat = null } = {}) => {
    if (!storeId) {
      pushMessage({ role: 'assistant', text: 'I can’t tell which store you’re in — pick a store and try again.' });
      return;
    }
    // 1) Explicit target from the editor header button.
    if (templateId != null) {
      askConvertMode({ templateId, templateName: templateName || 'this template', currentFormat, targetFormat, command: text });
      return;
    }
    // 2) The template currently open in the editor.
    const active = getActiveTemplate();
    if (active && active.id != null) {
      askConvertMode({
        templateId: active.id,
        templateName: active.name,
        currentFormat: currentFormat || formatFromTypeLabel(active.typeLabel),
        targetFormat,
        command: text,
      });
      return;
    }
    // 3) A template named in the chat command.
    const templates = await fetchConvertibleTemplates(storeId);
    if (!templates.length) {
      pushMessage({
        role: 'assistant',
        text: 'I couldn’t find a template to convert. Create one first, or open the template you mean and ask again.',
        link: { href: '/count.html?view=overview', label: 'Open Counts →' },
      });
      return;
    }
    const matched = matchTemplatesToCommand(text, templates.map((t) => ({ id: t.id, name: t.name })));
    const pick = matched.length === 1 ? templates.find((t) => String(t.id) === String(matched[0].id)) : null;
    if (pick) {
      askConvertMode({ templateId: pick.id, templateName: pick.name, currentFormat: pick.currentFormat, targetFormat, command: text });
      return;
    }
    // Couldn't tell which one — ask, carrying the candidates for the next reply.
    setPending({
      kind: 'pending_template_convert',
      route: 'template_convert',
      awaiting: 'template_pick',
      candidates: templates.slice(0, 12),
      command: text,
      created_at: new Date().toISOString(),
      expires_at: parPendingExpiry(),
    });
    pushMessage({
      role: 'assistant',
      text: 'Which template should I convert?',
      choices: templates.slice(0, 8).map((t) => ({ key: `cvt:${t.id}`, label: t.name })),
    });
  };
  // Keep the ref fresh so the convert-template event always calls the latest one.
  startConvertRef.current = startTemplateConvert;

  // Call the deterministic convert endpoint and show the preview card.
  const previewTemplateConvert = async (pending, mode, storeId) => {
    const payload = await post(templatesConvertEndpoint(), {
      action: 'preview',
      store_id: storeId,
      mode,
      template_id: pending.templateId ?? null,
      template_name: (pending.templateName && pending.templateName !== 'this template') ? pending.templateName : null,
      target_format: pending.targetFormat || null,
      command: pending.command || '',
    });
    if (payload.clarification_needed) {
      // Multiple matches → let the user pick; otherwise just explain.
      if (Array.isArray(payload.candidates) && payload.candidates.length > 1) {
        setPending({
          kind: 'pending_template_convert',
          route: 'template_convert',
          awaiting: 'template_pick',
          candidates: payload.candidates.map((c) => ({ id: c.id, name: c.name })),
          command: pending.command || '',
          created_at: new Date().toISOString(),
          expires_at: parPendingExpiry(),
        });
        pushMessage({
          role: 'assistant',
          text: payload.clarification_message || 'Which template should I convert?',
          choices: payload.candidates.slice(0, 8).map((c) => ({ key: `cvt:${c.id}`, label: c.name })),
        });
        return;
      }
      pushMessage({ role: 'assistant', text: payload.clarification_message || 'I couldn’t tell which template to convert.' });
      return;
    }
    const r = payload.resolution || {};
    pushMessage({
      role: 'assistant',
      text: payload.summary || 'Here’s the change I can make.',
      card: {
        kind: 'template_convert',
        previewToken: payload.preview_token,
        templateId: r.template_id,
        templateName: r.template_name,
        currentFormat: r.current_format,
        newFormat: r.new_format,
        mode: r.mode || mode,
        itemCount: r.item_count ?? 0,
        storeId,
        status: 'pending',
      },
    });
  };

  // After a conversion produces an ordering template, offer starter PARs (reuses
  // the alcohol PAR flow's confirm step on the resulting template).
  const offerParsForConverted = async (templateId, templateName) => {
    try {
      await gateForTarget({ source: 'db', templateId, templateName });
    } catch (_) { /* offering PARs is best-effort */ }
  };

  const previewTemplateCreate = async (text, storeId, meta = {}) => {
    const payload = await post(templatesEndpoint(), {
      action: 'preview',
      intent: 'create',
      create_source: 'inventory',
      store_id: storeId,
      command: text,
    });
    if (payload.clarification_needed) {
      // The executor needs more detail (which items, which type, red or white…).
      // Keep the create thread alive so the next short reply is accumulated onto
      // this command instead of being re-classified into a generic "create /
      // edit / learn?" loop. Offer type-aware chips: ordering steers toward
      // dividing by vendor/category; End of Period offers "all active items".
      const clarifyPending = buildPendingTemplateClarify({ command: text, question: payload.clarification_message });
      setPending(clarifyPending);
      pushMessage({
        role: 'assistant',
        text: payload.clarification_message || 'Please refine that.',
        choices: clarifyScopeChoices(clarifyPending),
      });
      return;
    }
    const op = (payload.operations || [])[0];
    const r = op?.resolution || {};
    if (!op || !r.item_count) {
      // Nothing matched — still a refine-able moment, so keep the thread open.
      const clarifyPending = buildPendingTemplateClarify({ command: text, question: 'No items matched that description.' });
      setPending(clarifyPending);
      pushMessage({
        role: 'assistant',
        text: 'No items matched that description. Try naming a category, vendor, or specific items.',
        choices: clarifyScopeChoices(clarifyPending),
      });
      return;
    }
    clearPending();
    const typeLabel = r.template_type === 'end_of_period' ? 'End of Period' : 'Ordering';
    // The preview already returns the FULL resolved item set — render all of it
    // in the mini-editor, grouped by location (sorted by location then name) so
    // the displayed order becomes the template's count order on apply.
    const items = (r.resolved_items || [])
      .map((it) => ({
        inventory_item_id: it.inventory_item_id,
        item_name: it.item_name || 'Item',
        vendor: it.vendor || '',
        category: it.category || '',
        location: it.location || 'Imported Items',
        par_level: it.par_level == null ? '' : it.par_level,
        par_unit: it.par_unit || 'each',
      }))
      .sort((a, b) =>
        (a.location || '').localeCompare(b.location || '') ||
        (a.item_name || '').localeCompare(b.item_name || ''));
    // Run the new template through the shared naming protocol before showing the
    // card, so a proposed name is ready for the user to review/edit.
    const proposedName = await proposeTemplateName({ typeLabel, items, fallback: r.template_name });
    pushMessage({
      role: 'assistant',
      text: r.interpreted_request || `Here’s a template I can create.`,
      card: {
        kind: 'template_create',
        previewToken: payload.preview_token,
        name: proposedName,
        typeLabel,
        itemCount: items.length,
        items,
        warnings: r.selection_warnings || [],
        scheduleDescription: r.schedule_description || null,
        createdVendor: meta.createdVendor || null,
        orderVendorQueue: Array.isArray(meta.orderVendorQueue) ? meta.orderVendorQueue : [],
        status: 'pending',
      },
    });
  };

  const previewItemEdit = async (text, storeId) => {
    clearPending();
    const payload = await post(itemsEndpoint(), {
      action: 'preview',
      command: text,
      store_id: storeId,
      page_context: 'items_page',
    });
    if (payload.clarification_needed) {
      pushMessage({ role: 'assistant', text: payload.clarification_message || 'Please refine that.' });
      return;
    }
    const affected = payload.affected_count || 0;
    if (!affected) {
      pushMessage({ role: 'assistant', text: payload.summary || 'No matching items were found.' });
      return;
    }
    pushMessage({
      role: 'assistant',
      text: payload.summary || `This will update ${affected} item${affected === 1 ? '' : 's'}.`,
      card: {
        kind: 'item_edit',
        previewToken: payload.preview_token,
        storeId,
        affected,
        sample: (payload.sample_rows || payload.preview_rows || []).slice(0, 8),
        largeWarning: !!payload.large_update_warning,
        // New values the user is introducing (e.g. a category that doesn't exist
        // yet) — surfaced so confirming the edit doubles as confirming creation.
        pendingCreations: Array.isArray(payload.pending_creations) ? payload.pending_creations : [],
        pnlReminder: !!payload.pnl_mapping_reminder,
        status: 'pending',
      },
    });
  };

  // Deduplication / delete-item flow. Asks the cleanup function to find look-alike
  // items (grouped by same/very-similar name, ignoring unit/pack/number/spelling
  // differences). When the command names a specific item ("delete the old
  // blackberries"), the scan is seeded with it so that item is surfaced for a
  // cautious, confirm-first delete. Nothing is removed here — the card lets the user
  // pick which items to archive, then applyCard does the delete.
  const previewItemDedup = async (text, storeId, { intent = 'dedup' } = {}) => {
    clearPending();
    const payload = await post(itemsCleanupEndpoint(), {
      action: 'scan',
      command: text,
      store_id: storeId,
      // 'delete' tells the scan that the wording NAMES what to remove, so an unmatched
      // query must come back empty rather than falling back to the whole catalog's
      // look-alikes. Only an open-ended "find my duplicates" gets that fallback.
      intent,
    });
    const groups = (Array.isArray(payload.groups) ? payload.groups : [])
      .filter((g) => g && Array.isArray(g.items) && g.items.length);
    if (!groups.length) {
      pushMessage({
        role: 'assistant',
        text: payload.summary || (intent === 'delete'
          ? 'I couldn’t find an item matching that, so I haven’t touched anything. Tell me its exact name — or, if you meant a batch you added, say the day (“delete the items I added on July 28”).'
          : 'I didn’t find items that look like duplicates or close variations. If you want to delete a specific item, tell me its exact name and I’ll pull it up.'),
      });
      return;
    }
    pushMessage({
      role: 'assistant',
      text: payload.summary || 'Here are the look-alike items I found. Tick the ones to archive.',
      card: {
        kind: 'dedup',
        mode: 'lookalike',
        previewToken: payload.preview_token,
        storeId,
        groups,
        // Item ids the user has ticked for deletion (archive). Starts empty — the
        // user opts in per item, so a bulk delete is never the default.
        selectedIds: [],
        status: 'pending',
      },
    });
  };

  // Scoped delete: the items added inside a date window, grouped by vendor. The date
  // is applied as a HARD filter in the query — this flow never compares names and
  // never widens its own scope, so what comes back is that batch or nothing.
  const previewScopedItemDelete = async (scope, storeId) => {
    clearPending();
    const payload = await post(itemsCleanupEndpoint(), {
      action: 'scan',
      store_id: storeId,
      intent: 'delete',
      filters: { created_from: scope.createdFrom, created_to: scope.createdTo },
      scope_label: scope.scopeLabel,
    });
    const groups = (Array.isArray(payload.groups) ? payload.groups : [])
      .filter((g) => g && Array.isArray(g.items) && g.items.length);
    const note = assumedYearNote(scope);
    if (!groups.length) {
      pushMessage({
        role: 'assistant',
        text: `${payload.summary || `I didn’t find any items added ${scope.scopeLabel}. Nothing has been changed.`}${note}`,
      });
      return;
    }
    pushMessage({
      role: 'assistant',
      text: `${payload.summary || `Here are the items added ${scope.scopeLabel}. Tick the ones to archive.`}${note}`,
      card: {
        kind: 'dedup',
        mode: 'scoped',
        scopeLabel: scope.scopeLabel,
        // A sub-day window ("in the last hour") — review rows show clock times,
        // because "added Aug 14" on every row of an hour batch says nothing.
        timeScoped: !!scope.timeWindow,
        previewToken: payload.preview_token,
        storeId,
        groups,
        selectedIds: [],
        status: 'pending',
      },
    });
  };

  // Latest-batch delete: "delete the items from the recent import" — the scope is
  // the store's actual most recent burst of item creations, resolved server-side
  // (filters.latest_batch). The server reports the window it found (batch_from/
  // batch_to instants); only this client can say it in the user's local clock, so
  // the message is worded here — stating HOW the fuzzy reference was read before
  // anything can be ticked.
  const previewLatestBatchDelete = async (storeId) => {
    clearPending();
    const payload = await post(itemsCleanupEndpoint(), {
      action: 'scan',
      store_id: storeId,
      intent: 'delete',
      filters: { latest_batch: true },
    });
    const groups = (Array.isArray(payload.groups) ? payload.groups : [])
      .filter((g) => g && Array.isArray(g.items) && g.items.length);
    if (!groups.length) {
      pushMessage({
        role: 'assistant',
        text: 'I couldn’t find a recent batch — I don’t see any active items with creation dates. Nothing has been changed. Tell me a day or a time range instead (for example “the items I added on July 28” or “the items added in the last hour”).',
      });
      return;
    }
    const windowLabel = describeInstantWindow(payload.batch_from, payload.batch_to);
    const scopeLabel = windowLabel || 'in your most recent batch';
    const itemCount = groups.reduce((n, g) => n + g.items.length, 0);
    pushMessage({
      role: 'assistant',
      text: `I read that as your most recent batch of added items — ${itemCount} item${itemCount === 1 ? '' : 's'} added ${scopeLabel}. That’s everything in that batch, and nothing else. Tick the ones to archive (or Select all); nothing is removed until you confirm.`,
      card: {
        kind: 'dedup',
        mode: 'scoped',
        scopeLabel,
        timeScoped: true,
        previewToken: payload.preview_token,
        storeId,
        groups,
        selectedIds: [],
        status: 'pending',
      },
    });
  };

  // Scan + review card for an attribute scope — the LIST pass of a scoped
  // delete. Shared by the direct path (one confident reading resolves straight
  // to it) and by the intent-confirmed path in handleSend, so the card the user
  // finally ticks is identical either way.
  const scanAttributeScopedDelete = async ({ filters, scopeLabel, heard = '', storeId }) => {
    // Combine the resolved pairs into one UNION scan, one array per field.
    const scanFilters = {};
    for (const f of filters) {
      if (!scanFilters[f.field]) scanFilters[f.field] = [];
      scanFilters[f.field].push(f.value);
    }
    const payload = await post(itemsCleanupEndpoint(), {
      action: 'scan',
      store_id: storeId,
      intent: 'delete',
      filters: scanFilters,
      scope_label: scopeLabel,
    });
    const groups = (Array.isArray(payload.groups) ? payload.groups : [])
      .filter((g) => g && Array.isArray(g.items) && g.items.length);
    if (!groups.length) {
      pushMessage({
        role: 'assistant',
        text: `${heard}${payload.summary || `I didn’t find any items ${scopeLabel}. Nothing has been changed.`}`,
      });
      return;
    }
    pushMessage({
      role: 'assistant',
      text: `${heard}${payload.summary || `Here are the items ${scopeLabel}. Tick the ones to archive.`}`,
      card: {
        kind: 'dedup',
        mode: 'scoped',
        // 'attribute' (vs the default created-date scope) only changes how the
        // card TALKS about the batch — the review-and-confirm flow is identical.
        scopeKind: 'attribute',
        // A batch picked by vendor or by item name is grouped by category (and
        // vice versa), so each group still says something new about its items.
        groupNoun: (filters[0].field === 'vendor' || filters[0].field === 'description') ? 'category' : 'vendor',
        scopeLabel,
        previewToken: payload.preview_token,
        storeId,
        groups,
        selectedIds: [],
        status: 'pending',
      },
    });
  };

  // Scoped delete by attribute: every active item under one vendor, category, or
  // storage area — or SEVERAL of them ("Asia Imports and Shamrock Foods"). The
  // phrase(s) are resolved server-side first ("Asia Imports" → the vendor), so
  // the review card always names what was matched — and a phrase that matches
  // several kinds (a vendor AND a category) or none is answered with words,
  // never with a guessed scan.
  const previewAttributeScopedItemDelete = async (scope, storeId) => {
    clearPending();
    // scope: { phrase, phrases } from the matcher — or a bare phrase string from
    // the classifier path, which is split the same way here.
    const phrase = String(typeof scope === 'string' ? scope : scope?.phrase || '').trim();
    const phrases = (typeof scope === 'object' && Array.isArray(scope?.phrases) && scope.phrases.length)
      ? scope.phrases
      : splitScopePhrases(phrase);
    if (!phrases.length) {
      pushMessage({
        role: 'assistant',
        text: 'Tell me which vendor, category, or storage area you mean and I’ll pull up exactly that batch to confirm.',
      });
      return;
    }
    let resolution = await post(itemsCleanupEndpoint(), {
      action: 'resolve_scope',
      store_id: storeId,
      phrases,
    });
    let filters = (Array.isArray(resolution.filters) ? resolution.filters : [])
      .filter((f) => f && f.field && f.value);
    // A vendor literally named "X and Y" loses the split — so when a piece fails
    // to resolve, try the WHOLE phrase as one name before asking the user.
    if (!filters.length && phrases.length > 1 && phrase) {
      const whole = await post(itemsCleanupEndpoint(), {
        action: 'resolve_scope',
        store_id: storeId,
        phrase,
      });
      if (whole.filter && whole.filter.field && whole.filter.value) {
        resolution = { ...whole, filters: [whole.filter], scope_label: whole.scope_label };
        filters = [whole.filter];
      }
    }
    if (!filters.length) {
      pushMessage({
        role: 'assistant',
        text: resolution.summary
          || `I couldn’t pin down a single vendor, category, or storage area called “${phrase}” — nothing has been changed.`,
      });
      return;
    }
    const scopeLabel = String(resolution.scope_label || '').trim() || attributeScopeListLabel(filters);
    // The two-pass confirm: when the resolver reports the phrase's possible
    // readings (newer function), only a single EXACT vendor/category/location
    // reading goes straight to the list pass — anything ambiguous or fuzzy
    // ("freezer" = storage area AND a frozen category AND item names) asks what
    // was meant FIRST, and the scan waits for that answer. An absent
    // `interpretations` field means an older function: keep the old behavior.
    const interpretations = Array.isArray(resolution.interpretations) ? resolution.interpretations : null;
    if (interpretations) {
      if (!interpretations.length) {
        pushMessage({
          role: 'assistant',
          text: resolution.summary
            || `I couldn’t pin down a single vendor, category, or storage area called “${phrase}” — nothing has been changed.`,
        });
        return;
      }
      const singleExact = interpretations.length === 1
        && interpretations[0].matched === 'exact'
        && ['vendor', 'category', 'location'].includes(interpretations[0].field);
      if (!singleExact) {
        const deleteScopePending = buildDeleteScopePending({ phrase, phrases, interpretations, storeId });
        setPending(deleteScopePending);
        pushMessage({
          role: 'assistant',
          text: intentConfirmMessage(phrase, interpretations),
          choices: deleteScopeChips(deleteScopePending),
        });
        return;
      }
    }
    // The "I read X as Y" note rides ahead of the summary whenever a phrase
    // resolved fuzzily — a delete must never hide how the name was interpreted,
    // and the confirmation shows the FULL resolved name alongside the items.
    const interpretation = String(resolution.interpretation || '').trim();
    const heard = interpretation ? `${interpretation} — ` : '';
    await scanAttributeScopedDelete({ filters, scopeLabel, heard, storeId });
  };

  const previewVendorEdit = async (text, storeId) => {
    clearPending();
    const activeVendor = getActiveVendor();
    const payload = await post(vendorsEndpoint(), {
      action: 'preview',
      command: text,
      store_id: storeId,
      active_vendor: activeVendor,
    });
    if (payload.clarification_needed) {
      pushMessage({ role: 'assistant', text: payload.clarification_message || 'Please refine that.' });
      return;
    }
    const changes = payload.changes || [];
    if (!changes.length) {
      pushMessage({ role: 'assistant', text: payload.summary || 'No vendor changes were detected.' });
      return;
    }
    pushMessage({
      role: 'assistant',
      text: payload.summary || 'Here’s the vendor change I can make.',
      card: {
        kind: 'vendor_edit',
        previewToken: payload.preview_token,
        storeId,
        vendorName: payload.vendor?.display_name || 'Vendor',
        changes,
        willUpdateDrafts: !!payload.will_update_drafts,
        renaming: !!payload.renaming,
        status: 'pending',
      },
    });
  };

  // ── Purchase order: edit a draft order, or answer a status question ─────────
  // The executor resolves the target order from the vendor named in the command
  // (with a recency bias, draft-first for edits). Editing only ever touches a
  // draft; for a sent order it offers an amendment instead. Status is read-only.
  const previewPurchaseOrderEdit = async (text, storeId) => {
    clearPending();
    const payload = await post(purchaseOrdersEndpoint(), {
      action: 'preview',
      command: text,
      store_id: storeId,
    });
    if (payload.clarification_needed) {
      // Includes the "that order was already sent — send an amendment" case,
      // which carries a suggestion link to the Orders page.
      const suggestion = (payload.suggestions || []).find((s) => s && s.href);
      pushMessage({
        role: 'assistant',
        text: payload.clarification_message || 'Please tell me which order and what to change.',
        link: suggestion ? { href: suggestion.href, label: suggestion.label || 'Open →' } : undefined,
      });
      return;
    }
    const operations = Array.isArray(payload.changes) ? payload.changes : [];
    if (!operations.length) {
      pushMessage({ role: 'assistant', text: payload.clarification_message || 'I couldn’t find anything to change on that order.' });
      return;
    }
    const po = payload.purchase_order || {};
    pushMessage({
      role: 'assistant',
      text: `Here’s the change I can make to ${po.po_number || 'this order'}.`,
      card: {
        kind: 'po_edit',
        previewToken: payload.preview_token,
        storeId,
        // Carry the order's id so the apply-time refresh events can target the
        // exact order (an open Orders/Ordering card or draft review) instead of
        // reloading every PO on screen.
        poId: po.id ?? null,
        poNumber: po.po_number || 'this order',
        vendorName: po.vendor_name || '',
        changes: operations,
        affected: payload.affected_count || operations.length,
        netDeltaDisplay: payload.net_delta_display || '',
        warnings: Array.isArray(payload.warnings) ? payload.warnings.filter(Boolean) : [],
        status: 'pending',
      },
    });
  };

  const answerPurchaseOrderStatus = async (text, storeId) => {
    clearPending();
    const payload = await post(purchaseOrdersEndpoint(), {
      action: 'status',
      command: text,
      store_id: storeId,
    });
    const reply = payload?.reply || 'I couldn’t determine that order’s status.';
    // Surface the first actionable suggestion as the message link (revise the
    // vendor email, open the Orders page, or send an amendment). The "reach the
    // vendor another way" suggestion has no link — it's already in the reply.
    const linkSuggestion = (payload.suggestions || []).find((s) => s && s.href);
    pushMessage({
      role: 'assistant',
      text: reply,
      link: linkSuggestion ? { href: linkSuggestion.href, label: linkSuggestion.label || 'Open →' } : undefined,
    });
  };

  // Edit the template currently open in the count-creation editor. The command
  // is resolved into concrete operations server-side (against the editor's LIVE
  // rows, so it sees the user's unsaved edits), previewed for approval, then —
  // on Apply — handed to the same in-page hook the editor uses for its own AI
  // changes (window.applyAiPlanToCountCreation). That keeps the open editor and
  // the count_items table in sync, instead of a separate server write racing the
  // user's next save. When no template is open we point them to where editing
  // happens rather than acting blindly.
  const previewTemplateEdit = async (text, storeId) => {
    clearPending();
    const active = getActiveTemplate();
    if (!active) {
      pushMessage({
        role: 'assistant',
        text: 'Open the template first — Inventory → Counts → tap the template → Edit Template — then ask me right here and I’ll make the change in place. From any page I can also create new templates and bulk-edit your item catalog.',
        link: { href: '/count.html?view=overview', label: 'Open Counts →' },
      });
      return;
    }
    const payload = await post(templatesEndpoint(), {
      action: 'preview',
      intent: 'edit',
      store_id: storeId,
      template_id: active.id,
      template_rows: active.rows,
      command: text,
    });
    if (payload.clarification_needed) {
      pushMessage({ role: 'assistant', text: payload.clarification_message || 'Please refine that.' });
      return;
    }
    const operations = Array.isArray(payload.operations) ? payload.operations : [];
    const affected = payload.total_affected || 0;
    if (!operations.length) {
      pushMessage({ role: 'assistant', text: 'I couldn’t find anything to change for that in this template. Try naming the items, a location, or a PAR value.' });
      return;
    }
    pushMessage({
      role: 'assistant',
      text: `Here’s the change I can make to “${active.name}”.`,
      card: {
        kind: 'template_edit',
        operations,
        affected,
        templateName: active.name,
        status: 'pending',
      },
    });
  };

  // ── Apply actions ──
  const applyCard = async (messageId, card) => {
    updateMessage(messageId, { card: { ...card, status: 'applying' } });
    try {
      if (card.kind === 'template_create') {
        // Send the mini-editor's final rows (and edited name) so the created
        // template matches exactly what's shown. The server re-validates every
        // id against the signed selection before trusting these.
        const clientItems = (card.items || []).map((it) => ({
          inventory_item_id: it.inventory_item_id,
          location: it.location,
          par_level: it.par_level === '' ? null : it.par_level,
          par_unit: it.par_unit,
        }));
        // The chosen schedule is the same ordering_cycle_config the template
        // editor's persistScheduleToTemplate writes. We send it to the edge
        // function so it's stamped on the count_sessions row at insert time
        // (service role — reliable), instead of relying on a client-side update
        // that can silently no-op under RLS. Optional: no schedule still creates.
        const cycle = (card.cycle && card.cycle.preset) ? card.cycle : null;
        const res = await post(templatesEndpoint(), {
          action: 'apply',
          preview_token: card.previewToken,
          approved_operation_indexes: [0],
          template_name: card.name,
          client_items: clientItems,
          cycle,
        });
        const newTemplateId = res.new_template_id;
        const scheduleLabel = cycle ? cycleToFriendlyLabel(cycle) : null;
        // Belt-and-suspenders for the deploy window: if this build is talking to
        // an older edge function that doesn't persist the schedule on insert,
        // stamp it client-side too. Harmless (idempotent) once the new function
        // is live, and silently skipped if the client write isn't permitted.
        if (cycle && newTemplateId != null && res.schedule_saved !== true) {
          try {
            await supabase.from('count_sessions')
              .update({ ordering_cycle_config: cycle })
              .eq('id', newTemplateId);
          } catch (_) { /* schedule is optional — the template is already created */ }
        }
        updateMessage(messageId, { card: { ...card, status: 'done', newTemplateId, scheduleLabel } });
        // Show the new template on the Count & Order overview WITHOUT a manual
        // refresh. The overview list (CountInterface) reloads its sessions on
        // the document 'refreshCountInterface' event — the same one the drawer's
        // applyParsToDb already fires. Every other apply branch notifies its
        // surface; template_create was the one that didn't, so the user had to
        // reload to see the card. (Dispatched on document — CountInterface
        // listens there; window would be a silent no-op.)
        try { document.dispatchEvent(new CustomEvent('refreshCountInterface', { detail: { sessionId: newTemplateId } })); } catch (_) {}
        pushMessage({
          role: 'assistant',
          text: scheduleLabel ? `Created “${card.name}” · repeats ${scheduleLabel}.` : `Created “${card.name}”.`,
          link: { href: '/count.html?view=overview', label: 'Open it on the Counts page →' },
        });
        // One vendor at a time: if other vendors still need an order template,
        // offer the next one right here so the user can keep going.
        const nextPending = buildNextVendorPending(card.orderVendorQueue);
        if (nextPending) {
          setPending(nextPending);
          const queue = nextPending.vendorCandidates;
          pushMessage({
            role: 'assistant',
            text: `Want to set up the next vendor’s order template? ${queue.length} more ${queue.length === 1 ? 'vendor still needs' : 'vendors still need'} one.`,
            choices: vendorChoiceChips(queue),
          });
        }
      } else if (card.kind === 'par_suggest') {
        let n = 0;
        if (card.source === 'editor') {
          // The chosen template is the one open in the editor — write through the
          // same hook the template-edit flow uses (set_par ops carrying
          // units) so the change lands in memory AND persists to count_items.
          if (typeof window.applyAiPlanToCountCreation !== 'function') {
            throw new Error('The template editor isn’t open anymore — reopen the template (or name it) and ask me to set PARs again.');
          }
          const ops = buildSetParMoqOps(card.items || []);
          if (!ops.length) throw new Error('No PARs to apply — every row was cleared.');
          await window.applyAiPlanToCountCreation(ops);
          n = (card.items || []).length;
          updateMessage(messageId, { card: { ...card, status: 'done' } });
          pushMessage({
            role: 'assistant',
            text: `Set starter PARs on ${n} item${n === 1 ? '' : 's'} in “${card.templateName}”. They’re applied in the editor — fine-tune any of them there if you like.`,
          });
        } else {
          // The template isn't open — persist straight to its count_items (and
          // propagate to the parent template / item), exactly as the editor does.
          if (card.templateId == null) throw new Error('I lost track of which template to save to — ask me to set PARs again.');
          n = await applyParsToDb(card.templateId, card.items || []);
          if (!n) throw new Error('No PARs to apply — every row was cleared.');
          updateMessage(messageId, { card: { ...card, status: 'done' } });
          pushMessage({
            role: 'assistant',
            text: `Set and saved starter PARs on ${n} item${n === 1 ? '' : 's'} in “${card.templateName}”. Open it under Inventory → Counts if you’d like to fine-tune anything.`,
            link: { href: '/count.html?view=overview', label: 'Open Counts →' },
          });
        }
        try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'par_suggest' } })); } catch (_) {}
      } else if (card.kind === 'template_convert') {
        // Deterministic apply (service role): flip the format flags in place, or
        // build a copy in the other format, then refresh the Count & Order views.
        const res = await post(templatesConvertEndpoint(), {
          action: 'apply',
          preview_token: card.previewToken,
          store_id: card.storeId,
        });
        const newFormat = res.new_format || card.newFormat;
        const newId = res.new_template_id ?? card.templateId;
        const formatLabel = formatPhrase(newFormat);
        updateMessage(messageId, { card: { ...card, status: 'done' } });
        if (res.mode === 'copy') {
          pushMessage({
            role: 'assistant',
            text: `Created “${res.template_name || card.templateName}” as ${formatLabel}. Your original “${card.templateName}” is unchanged.`,
            link: { href: '/count.html?view=overview', label: 'Open it on the Counts page →' },
          });
        } else {
          pushMessage({
            role: 'assistant',
            text: `Converted “${card.templateName}” into ${formatLabel}.`,
            link: { href: '/count.html?view=overview', label: 'Open Counts →' },
          });
        }
        try { document.dispatchEvent(new CustomEvent('refreshCountInterface', { detail: { sessionId: newId } })); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'template_convert' } })); } catch (_) {}
        // Now that it's an ordering template, offer to help set starter PARs.
        if (newFormat === 'ordering' && newId != null && (card.itemCount || 0) > 0) {
          await offerParsForConverted(newId, res.template_name || card.templateName);
        }
      } else if (card.kind === 'item_edit') {
        const res = await post(itemsEndpoint(), {
          action: 'apply',
          preview_token: card.previewToken,
          store_id: card.storeId,
        });
        updateMessage(messageId, { card: { ...card, status: 'done' } });
        const updatedN = res.updated_count ?? card.affected;
        const createdCats = Array.isArray(res.created_categories) ? res.created_categories : [];
        let doneText = `Updated ${updatedN} item${updatedN === 1 ? '' : 's'}.`;
        if (createdCats.length) {
          doneText += ` Created new categor${createdCats.length === 1 ? 'y' : 'ies'}: ${createdCats.join(', ')}.`;
          if (res.pnl_mapping_reminder) {
            doneText += ` Remember to map ${createdCats.length === 1 ? 'it' : 'them'} under Settings → Store → P&L Categories.`;
          }
        }
        pushMessage({
          role: 'assistant',
          text: doneText,
          link: (createdCats.length && res.pnl_mapping_reminder)
            ? { href: '/store-settings.html#pnl', label: 'Open P&L Categories →' }
            : undefined,
        });
        try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'item_edit' } })); } catch (_) {}
      } else if (card.kind === 'vendor_edit') {
        const res = await post(vendorsEndpoint(), {
          action: 'apply',
          preview_token: card.previewToken,
          store_id: card.storeId,
        });
        updateMessage(messageId, { card: { ...card, status: 'done' } });
        pushMessage({ role: 'assistant', text: res.summary || `Updated ${card.vendorName}.` });
        // Let the Vendors page refresh its list/edit panel in real time.
        try { document.dispatchEvent(new CustomEvent('vendorDataChanged', { detail: { storeId: card.storeId } })); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'vendor_edit' } })); } catch (_) {}
      } else if (card.kind === 'template_edit') {
        // Apply through the open editor's own hook so the change lands in the
        // in-memory rows AND persists to count_items (handleApplyAiPlan). If the
        // user navigated away from the editor between preview and apply, the hook
        // is gone — fail loudly so the card resets to pending rather than silently
        // dropping the edit.
        if (typeof window.applyAiPlanToCountCreation !== 'function') {
          throw new Error('The template editor isn’t open anymore — reopen the template and ask again.');
        }
        await window.applyAiPlanToCountCreation(card.operations);
        updateMessage(messageId, { card: { ...card, status: 'done' } });
        pushMessage({ role: 'assistant', text: `Updated “${card.templateName}”. Review it in the editor and save when you’re ready.` });
        try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'template_edit' } })); } catch (_) {}
      } else if (card.kind === 'po_edit') {
        // The draft order is edited server-side (service role): the executor
        // re-validates it's still a draft, applies the previewed adds/removes/
        // quantity changes to purchase_order_items, and recomputes the totals.
        const res = await post(purchaseOrdersEndpoint(), {
          action: 'apply',
          preview_token: card.previewToken,
          store_id: card.storeId,
        });
        updateMessage(messageId, { card: { ...card, status: 'done' } });
        pushMessage({
          role: 'assistant',
          text: res.summary || `Updated ${card.poNumber}.`,
          link: { href: '/orders.html', label: 'Open the Orders page →' },
        });
        // Let any open Orders/Ordering views refresh in real time. The poId lets
        // a listener reload just the affected order (e.g. an expanded order card
        // or open draft review) so the changed quantity/total updates live.
        try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'po_edit', poId: card.poId } })); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent('purchaseOrdersChanged', { detail: { storeId: card.storeId, poId: card.poId } })); } catch (_) {}
      } else if (card.kind === 'item_import') {
        // The imported items are already in the catalog; saving keeps the ones
        // still shown and deletes the rows the user removed in the preview (same
        // as the Items page's reject-on-save). Then offer to build templates.
        const keptIds = (card.items || []).map((it) => it.id).filter((v) => v != null);
        const keptKeys = new Set(keptIds.map((v) => String(v)));
        const removedIds = (card.allIds || []).filter((id) => !keptKeys.has(String(id)));
        if (removedIds.length) {
          const { error } = await supabase
            .from('inventory_items')
            .delete()
            .in('id', removedIds)
            .eq('store_id', card.storeId);
          if (error) throw new Error(error.message || 'Could not remove the unselected items.');
        }
        updateMessage(messageId, { card: { ...card, status: 'done' } });
        // `count` lets page listeners (e.g. add-items.html's chat-saved alert)
        // ignore an all-removed save; existing listeners only read `kind`.
        try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'item_import', count: keptIds.length } })); } catch (_) {}
        if (!keptIds.length) {
          pushMessage({ role: 'assistant', text: 'Okay — I removed all of those, so nothing was added to your catalog.' });
          return;
        }
        // Real items just landed in the catalog — the onboarding gate is open,
        // so link buttons from here on (Count & Order etc.) genuinely work.
        setupUnlockedRef.current = true;
        const session = { ...(card.session || {}), itemsImported: keptIds.length };
        // The import message already said what to do with templates ("…and
        // make it all into one ordering template") — carry it forward instead
        // of re-asking: open the graphical template review with the directive
        // riding the handoff. Creation still waits for approval there.
        if (session.directive) {
          openTemplateSetupFromImport({ session, kept_item_ids: keptIds });
          return;
        }
        setPending(buildTemplatesPending({ session, keptItemIds: keptIds }));
        pushMessage({
          role: 'assistant',
          text: `Saved ${keptIds.length} item${keptIds.length === 1 ? '' : 's'} to your Item Catalog. Want me to organize them into count and order templates? I’ll open a graphical proposal first, and you can review schedules, move items, combine groups, or add a special template before anything is created.`,
          choices: templateOfferChips(),
        });
      } else if (card.kind === 'dedup') {
        // Archive the ticked items: the executor removes them from every template
        // (drops them from upcoming counts) and soft-deactivates the catalog row,
        // while count history / orders / reports keep them. Only ids from this
        // preview are honored (the token binds the reviewed set).
        const selected = Array.isArray(card.selectedIds) ? [...new Set(card.selectedIds)] : [];
        if (!selected.length) {
          updateMessage(messageId, { card: { ...card, status: 'pending' } });
          return;
        }
        // A card restored from an old session can carry a missing/expired token.
        // Guard here so the user gets a clear "scan again" nudge instead of a raw
        // backend error (the server also rejects a bad token defensively).
        if (!card.previewToken) {
          updateMessage(messageId, { card: { ...card, status: 'pending' } });
          pushMessage({
            role: 'assistant',
            text: card.mode === 'scoped'
              ? (card.scopeKind === 'attribute'
                ? `That review is stale — ask me again for the items ${card.scopeLabel || 'in that group'} and I’ll pull a fresh list.`
                : `That review is stale — ask me again for the items added ${card.scopeLabel || 'that day'} and I’ll pull a fresh list.`)
              : 'That review is stale — ask me to look for duplicates again and I’ll pull a fresh list.',
          });
          return;
        }
        const res = await post(itemsCleanupEndpoint(), {
          action: 'delete_apply',
          preview_token: card.previewToken,
          store_id: card.storeId,
          selected_item_ids: selected,
        });
        updateMessage(messageId, { card: { ...card, status: 'done' } });
        pushMessage({
          role: 'assistant',
          text: res.summary || `Archived ${res.archived_count ?? selected.length} item${(res.archived_count ?? selected.length) === 1 ? '' : 's'}.`,
        });
        // Let the Item Catalog / Count & Order pages drop the archived items live.
        try { window.dispatchEvent(new CustomEvent('counter-intelligence:applied', { detail: { kind: 'item_dedup', storeId: card.storeId } })); } catch (_) {}
      } else if (card.kind === 'price_update') {
        // Update changed prices, add new items, record price history, then recap.
        // applyPriceUpdate sets the card to 'done' and posts the summary itself.
        await applyPriceUpdate(messageId, card);
      }
    } catch (err) {
      updateMessage(messageId, { card: { ...card, status: 'pending' } });
      pushMessage({ role: 'assistant', text: formatErrorMessage(err, 'Apply failed.'), tone: 'error' });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <>
      <style>{`
        .ci-drawer-overlay { position: fixed; inset: 0; background: rgba(45,35,28,0.35); z-index: 4000; opacity: 0; pointer-events: none; transition: opacity .18s ease; }
        .ci-drawer-overlay.open { opacity: 1; pointer-events: auto; }
        /* 100dvh (100vh fallback for older WebKit) tracks the visible viewport
           on phones so the footer input isn't pushed under the browser chrome. */
        .ci-drawer { position: fixed; top: 0; right: 0; height: 100vh; height: 100dvh; width: min(440px, 92vw); background: #FBF7F1; box-shadow: -4px 0 24px rgba(45,35,28,0.18); z-index: 4001; display: flex; flex-direction: column; transform: translateX(100%); transition: transform .2s ease; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; overflow: hidden; }
        .ci-drawer.open { transform: translateX(0); }
        /* Reverse of template-setup's panel-to-launcher close. The one-time
           completed-signup handoff grows the real drawer out of the CI badge;
           every ordinary open keeps the established side-slide. */
        .ci-drawer.open.ci-signup-opening { animation: ci-signup-drawer-open .32s ease both; }
        @keyframes ci-signup-drawer-open {
          from { transform: translate(var(--ci-signup-open-x, 0), var(--ci-signup-open-y, 0)) scale(.12); opacity: 0; }
          to { transform: translate(0, 0) scale(1); opacity: 1; }
        }
        /* On phones the drawer should be a true full-screen panel. 100vw avoids
           any sub-pixel rounding that can make the drawer feel wider than the
           screen, and -webkit-fill-available catches iOS WebViews where dvh
           can initially report the full document height instead of the visible
           viewport (especially when the keyboard is opening). */
        @media (max-width: 768px) {
          .ci-drawer { width: 100vw; max-width: 100vw; right: 0; height: 100%; height: -webkit-fill-available; height: 100dvh; }
        }
        /* The drawer runs edge-to-edge (viewport-fit=cover), so the header owns
           the notch inset: the gradient fills the status-bar area while the
           title and the Clear/× buttons start below the phone's clock and
           battery icons. The right inset keeps them off a landscape notch. */
        .ci-head { display: flex; align-items: center; gap: 8px; padding: 12px 14px; padding-top: calc(12px + env(safe-area-inset-top, 0px)); padding-right: calc(14px + env(safe-area-inset-right, 0px)); border-bottom: 1px solid #E3D6C9; background: linear-gradient(135deg,#F5E2D0 0%,#E8DDD4 100%); }
        /* !important: mobile.css bumps bare h3 to calc(1.5rem * scale) !important,
           which blew the title up over the Clear/× buttons and the status bar. */
        .ci-head h3 { margin: 0; font-size: 1rem !important; font-weight: 800; color: #3D2518; }
        .ci-head .ci-sub { font-size: .72rem; color: #8B7B73; }
        .ci-clear { background: transparent; border: 1px solid #D8C3AE; color: #6D4C41; font: inherit; font-size: .68rem; font-weight: 700; cursor: pointer; padding: 3px 9px; border-radius: 999px; flex-shrink: 0; }
        .ci-clear:hover { background: rgba(109,76,65,0.08); }
        .ci-close { background: transparent; border: none; font-size: 1.3rem; line-height: 1; color: #6D4C41; cursor: pointer; padding: 2px 6px; border-radius: 6px; flex-shrink: 0; }
        .ci-close:hover { background: rgba(109,76,65,0.1); }
        /* Finger-sized Clear/× targets on touch screens (44px HIG minimum). */
        @media (max-width: 768px) {
          .ci-clear { min-height: 44px; padding: 6px 14px; font-size: .74rem; }
          .ci-close { display: inline-flex; align-items: center; justify-content: center; min-width: 44px; min-height: 44px; font-size: 1.5rem; padding: 0; }
        }
        .ci-list { flex: 1; min-height: 0; overflow-y: auto; padding: 14px; padding-right: calc(14px + env(safe-area-inset-right, 0px)); display: flex; flex-direction: column; gap: 10px; }
        .ci-empty { color: #8B7B73; font-size: .85rem; line-height: 1.5; }
        .ci-empty-copy { margin: 0; }
        .ci-empty-copy + .ci-empty-copy { margin-top: 8px; }
        .ci-examples-label { margin-top: 10px; font-size: .8rem; font-weight: 800; color: #3D2518; }
        .ci-examples { list-style: none; padding-left: 0; margin: 4px 0 0; }
        .ci-examples li { cursor: pointer; color: #2E5E86; padding: 1px 0; }
        .ci-examples li:hover { text-decoration: underline; }
        .ci-msg { max-width: 92%; padding: 8px 11px; border-radius: 12px; font-size: .87rem; line-height: 1.4; white-space: pre-wrap; }
        /* Origin tag for messages sent from a UI help tooltip ("Asked from
           Templates help") — sits tight above the user bubble it belongs to. */
        .ci-msg-src { align-self: flex-end; font-size: .68rem; font-weight: 600; color: #A89890; margin: -4px 4px -6px 0; }
        .ci-msg.user { align-self: flex-end; background: #2E5E86; color: #fff; border-bottom-right-radius: 4px; }
        .ci-msg.assistant { align-self: flex-start; background: #fff; color: #3A2E28; border: 1px solid #E8DFD7; border-bottom-left-radius: 4px; }
        .ci-msg.error { border-color: #E0A9A0; background: #FDF1EF; color: #8A3B2E; }
        .ci-link { display: inline-block; margin-top: 4px; color: #2E5E86; font-weight: 600; }
        .ci-chips { align-self: flex-start; display: flex; flex-wrap: wrap; gap: 6px; margin: -2px 0 2px; max-width: 92%; }
        .ci-chip { font: inherit; font-size: .8rem; font-weight: 600; color: #2E5E86; background: #fff; border: 1px solid #C6D5E2; border-radius: 999px; padding: 5px 12px; cursor: pointer; transition: background .12s, border-color .12s; }
        .ci-chip:hover { background: #EFF4F9; border-color: #2E5E86; }
        .ci-chip:disabled { opacity: .55; cursor: default; }
        /* Setup coaching card (kind: setup_coach) */
        .ci-coach-head { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-bottom: 7px; }
        .ci-coach-flag { font-weight: 800; color: #3D2518; font-size: .88rem; }
        .ci-coach-pill { background: #F5E2D0; color: #5B4A42; font-weight: 700; font-size: .69rem; border-radius: 999px; padding: 3px 9px; white-space: nowrap; }
        .ci-coach-pill-warn { background: #FEF0C0; color: #7A5A0A; }
        .ci-coach-bar { height: 6px; border-radius: 999px; background: #EFE1D2; overflow: hidden; margin: 0 0 8px; }
        .ci-coach-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #7EA64E 0%, #5E8C3A 100%); transition: width 300ms ease; }
        .ci-coach-step { border-top: 1px solid #F0E7DC; padding: 8px 0 4px; }
        .ci-coach-step-title { font-weight: 700; font-size: .82rem; color: #3A2E28; display: flex; gap: 7px; align-items: center; }
        .ci-coach-dot { width: 8px; height: 8px; border-radius: 999px; background: #D49A45; flex: 0 0 auto; }
        .ci-coach-step-desc { font-size: .76rem; color: #6E5C52; margin: 3px 0 1px 15px; line-height: 1.45; }
        .ci-coach-step .ci-link { margin-left: 15px; font-size: .78rem; }
        .ci-coach-offer { margin-top: 10px; font-size: .8rem; color: #3A2E28; line-height: 1.45; }
        .ci-card .ci-chips { margin-top: 8px; max-width: 100%; }
        .ci-form { align-self: flex-start; width: 92%; background: #fff; border: 1px solid #D8C8B7; border-radius: 10px; padding: 10px 12px; margin: -2px 0 2px; }
        .ci-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .ci-form-field { display: flex; flex-direction: column; gap: 3px; }
        .ci-form-field.required .ci-form-label { font-weight: 700; }
        .ci-form-label { font-size: .76rem; color: #5B4A42; }
        .ci-form-required { color: #c62828; margin-left: 2px; }
        .ci-form-input { font: inherit; font-size: .85rem; padding: 6px 8px; border: 1px solid #D8C8B7; border-radius: 6px; background: #fff; color: #3A2E28; }
        .ci-form-input:focus { outline: none; border-color: #2E5E86; box-shadow: 0 0 0 2px rgba(46,94,134,.12); }
        .ci-form-input:disabled { opacity: .6; background: #F5F1ED; }
        select.ci-form-input { cursor: pointer; width: 100%; box-sizing: border-box; }
        .ci-form-field .vendor-cell input { width: 100%; box-sizing: border-box; }
        .ci-form-link { align-self: flex-start; background: none; border: none; padding: 2px 0 0; margin: 0; color: #2E5E86; font-size: .72rem; cursor: pointer; text-decoration: underline; }
        .ci-form-link:disabled { opacity: .6; cursor: default; }
        .ci-form-actions { display: flex; gap: 8px; margin-top: 12px; }
        .ci-card { align-self: flex-start; width: 92%; background: #fff; border: 1px solid #D8C8B7; border-left: 4px solid #2E5E86; border-radius: 10px; padding: 10px 12px; }
        .ci-card .ci-card-title { font-weight: 700; color: #3D2518; font-size: .9rem; }
        .ci-card .ci-card-meta { font-size: .78rem; color: #6D4C41; margin-top: 2px; }
        .ci-card ul.ci-card-items { list-style: none; padding: 0; margin: 6px 0 0; max-height: 160px; overflow-y: auto; }
        .ci-card ul.ci-card-items li { font-size: .8rem; color: #3A2E28; padding: 1px 0; }
        .ci-card ul.ci-vendor-changes li { display: flex; align-items: baseline; flex-wrap: wrap; gap: 5px; padding: 3px 0; }
        .ci-vc-label { font-weight: 700; color: #5B4A42; min-width: 110px; }
        .ci-vc-old { color: #9A8B83; text-decoration: line-through; max-width: 45%; overflow-wrap: anywhere; }
        .ci-vc-arrow { color: #8B7B73; }
        .ci-vc-new { color: #2e7d32; font-weight: 600; overflow-wrap: anywhere; }
        .ci-card .ci-warn { color: #9A6A33; font-size: .76rem; margin-top: 6px; }
        .ci-card .ci-create-note { color: #2e7d32; font-size: .78rem; margin-top: 7px; line-height: 1.4; }
        .ci-card .ci-create-note b { color: #1f5d23; }

        /* ── In-chat item import (upload progress + reviewable item list) ──── */
        .ci-import-progress { margin-top: 8px; color: #5B4A42; font-size: .82rem; line-height: 1.4; display: flex; align-items: center; gap: 7px; }
        .ci-import-progress::before { content: ''; width: 13px; height: 13px; flex: 0 0 auto; border: 2px solid #D8C8B7; border-top-color: #2E5E86; border-radius: 50%; animation: ci-spin .7s linear infinite; }
        @keyframes ci-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .ci-import-progress::before { animation: none; } }
        .ci-import-ok { margin-top: 6px; color: #2e7d32; font-size: .8rem; font-weight: 600; }
        .ci-import-list { margin-top: 8px; border: 1px solid #E3D6C9; border-radius: 8px; overflow-y: auto; max-height: 244px; background: #fff; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
        .ci-import-row { display: flex; align-items: center; gap: 4px; padding: 4px 6px; border-bottom: 1px solid #F2EAE1; }
        .ci-import-row:last-child { border-bottom: none; }
        .ci-import-item { min-width: 0; flex: 1; }

        /* ── Deduplication / delete review (grouped look-alikes, tick to delete) ─ */
        .ci-dedup-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 8px; }
        .ci-dedup-selectall { display: inline-flex; align-items: center; gap: 6px; font-size: .76rem; font-weight: 700; color: #5B4A42; cursor: pointer; user-select: none; }
        .ci-dedup-selectall input[type="checkbox"] { accent-color: #B0746A; width: 15px; height: 15px; margin: 0; flex: 0 0 auto; }
        .ci-dedup-clear { border: none; background: transparent; color: #B0746A; font-size: .74rem; font-weight: 600; cursor: pointer; padding: 2px 6px; border-radius: 5px; }
        .ci-dedup-clear:hover { background: #FBE9E6; color: #8A3B2E; }
        .ci-dedup-groups { margin-top: 8px; display: flex; flex-direction: column; gap: 8px; max-height: 300px; overflow-y: auto; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
        .ci-dedup-group { flex: 0 0 auto; border: 1px solid #E3D6C9; border-radius: 8px; overflow: hidden; background: #fff; }
        .ci-dedup-group-label { background: #F5E2D0; color: #5B4A42; font-weight: 700; font-size: .74rem; padding: 4px 8px; border-bottom: 1px solid #E3D6C9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ci-dedup-row { display: flex; align-items: flex-start; gap: 8px; padding: 6px 8px; border-bottom: 1px solid #F2EAE1; cursor: pointer; }
        .ci-dedup-row:last-child { border-bottom: none; }
        .ci-dedup-row.selected { background: #FBEFE9; }
        .ci-dedup-row input[type="checkbox"] { margin-top: 3px; flex: 0 0 auto; accent-color: #B0746A; width: 15px; height: 15px; }
        .ci-dedup-usage { color: #8B7B73; font-size: .68rem; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        /* ── In-chat price update review ─────────────────────────────────────── */
        .ci-price-group { margin-top: 8px; border: 1px solid #E3D6C9; border-radius: 8px; overflow: hidden; background: #fff; }
        .ci-price-group-label { background: #F5E2D0; color: #5B4A42; font-weight: 700; font-size: .74rem; padding: 4px 8px; border-bottom: 1px solid #E3D6C9; }
        .ci-price-group:last-of-type .ci-price-group-label { border-bottom: none; }
        .ci-price-delta { flex: 0 0 auto; font-size: .78rem; font-weight: 700; color: #8B6135; white-space: nowrap; padding-left: 6px; }
        .ci-price-fuzzy { margin-left: 5px; font-size: .64rem; font-weight: 700; color: #8B6135; background: #FCF4EA; border: 1px solid #E8CEAF; border-radius: 4px; padding: 0 5px; }

        /* ── In-chat template mini-editor ───────────────────────────────────
           A compact, horizontally-scrollable table of every selected item.
           The container scrolls both axes so a wide row (item · location · PAR)
           stays reachable inside the narrow drawer on phones; the header sticks
           while the body scrolls vertically. */
        .ci-tpl-editor { margin-top: 8px; border: 1px solid #E3D6C9; border-radius: 8px; overflow: auto; max-height: 244px; background: #fff; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
        .ci-tpl-table { border-collapse: collapse; table-layout: fixed; width: 100%; min-width: 488px; font-size: .78rem; }
        .ci-tpl-table thead th { position: sticky; top: 0; z-index: 1; background: #F5E2D0; color: #5B4A42; font-weight: 700; text-align: left; padding: 5px 8px; white-space: nowrap; border-bottom: 1px solid #E3D6C9; }
        /* The PAR column is left-aligned so the input boxes line up on a common
           left edge no matter how many digits the value has or how long the unit
           label is (bottle vs can vs keg). Right-aligning pushed the fixed-width
           input left as the unit grew, so the boxes drifted out of column. */
        .ci-tpl-table thead th.ci-tpl-th-num { text-align: left; padding-left: 6px; padding-right: 6px; }
        .ci-tpl-table thead th.ci-tpl-th-rm { padding: 5px 2px; }
        .ci-tpl-table td { padding: 4px 8px; border-bottom: 1px solid #F2EAE1; vertical-align: middle; }
        .ci-tpl-table tbody tr:last-child td { border-bottom: none; }
        .ci-tpl-rm-cell { text-align: center; padding-left: 4px; padding-right: 2px; }
        .ci-tpl-rm { border: none; background: transparent; color: #B0746A; font-size: 1.05rem; line-height: 1; cursor: pointer; padding: 1px 5px; border-radius: 5px; }
        .ci-tpl-rm:hover { background: #FBE9E6; color: #8A3B2E; }
        .ci-tpl-item-cell { overflow: hidden; }
        .ci-tpl-name { color: #3A2E28; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ci-tpl-sub { color: #9A8B83; font-size: .7rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ci-tpl-static { color: #5B4A42; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
        .ci-tpl-loc { width: 100%; box-sizing: border-box; border: 1px solid #D8C8B7; border-radius: 6px; padding: 3px 6px; font: inherit; font-size: .76rem; color: #3A2E28; background: #fff; }
        .ci-tpl-num { text-align: left; white-space: nowrap; padding-left: 6px; padding-right: 6px; }
        .ci-tpl-par-wrap { display: inline-flex; align-items: baseline; gap: 4px; max-width: 100%; }
        /* Fixed-width input sized for 2–3 digits, with the native number spinner
           removed so its variable width can't shift the layout. */
        .ci-tpl-par { width: 50px; flex: 0 0 auto; box-sizing: border-box; border: 1px solid #D8C8B7; border-radius: 6px; padding: 3px 5px; font: inherit; font-size: .76rem; text-align: right; color: #3A2E28; background: #fff; -moz-appearance: textfield; appearance: textfield; }
        .ci-tpl-par::-webkit-outer-spin-button, .ci-tpl-par::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .ci-tpl-unit { color: #8B7B73; font-size: .68rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 0 1 auto; min-width: 0; }
        .ci-tpl-loc:focus, .ci-tpl-par:focus { outline: none; border-color: #2E5E86; box-shadow: 0 0 0 .12rem rgba(46,94,134,.15); }
        .ci-tpl-empty { margin-top: 8px; color: #8A3B2E; background: #FDF1EF; border: 1px solid #E0A9A0; border-radius: 8px; padding: 8px 10px; font-size: .78rem; }
        .ci-tpl-hint { font-size: .72rem; color: #8B7B73; margin: 6px 0 0; line-height: 1.4; }
        .ci-name-edit { margin-top: 11px; }
        .ci-name-label { font-size: .76rem; font-weight: 700; color: #5B4A42; margin-bottom: 4px; }
        .ci-name-input { width: 100%; border: 1px solid #C9B9A9; border-radius: 8px; padding: 7px 9px; font: inherit; font-size: .86rem; color: #3A2E28; background: #fff; }
        .ci-name-input:focus { outline: none; border-color: #2E5E86; box-shadow: 0 0 0 .15rem rgba(46,94,134,.15); }
        .ci-name-input:disabled { background: #F3EEE7; color: #8B7B73; }

        .ci-sched { margin-top: 9px; padding-top: 9px; border-top: 1px dashed #E3D6C9; }
        .ci-sched-label { font-size: .76rem; font-weight: 700; color: #5B4A42; margin-bottom: 5px; }
        .ci-sched-opt { font-weight: 600; color: #A89890; }
        .ci-sched-when { font-size: .74rem; color: #2e7d32; margin-top: 5px; }
        .ci-sched-hint { font-size: .72rem; color: #8B7B73; margin-top: 5px; line-height: 1.4; }
        .ci-card-actions { display: flex; gap: 8px; margin-top: 10px; }
        .ci-btn { font: inherit; font-size: .82rem; font-weight: 700; border-radius: 8px; padding: 6px 12px; cursor: pointer; border: 1px solid transparent; }
        .ci-btn-primary { background: #2e7d32; color: #fff; border-color: #2e7d32; }
        .ci-btn-primary:disabled { opacity: .6; cursor: default; }
        .ci-btn-secondary { background: #fff; color: #2E5E86; border-color: #C6D5E2; }
        .ci-btn-secondary:hover { background: #EFF4F9; border-color: #2E5E86; }

        /* ── In-chat rendered report ─────────────────────────────────────────── */
        .ci-report-summary { font-size: .82rem; color: #3A2E28; margin-top: 7px; line-height: 1.45; }
        .ci-report-stats { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 9px; }
        .ci-report-stat { display: flex; flex-direction: column; gap: 1px; border: 1px solid #E3D6C9; border-radius: 8px; padding: 5px 10px; background: #FBF7F1; min-width: 70px; }
        .ci-report-stat-label { font-size: .64rem; text-transform: uppercase; letter-spacing: .05em; color: #8B7B73; font-weight: 700; }
        .ci-report-stat-value { font-size: .92rem; font-weight: 800; color: #3D2518; }
        .ci-report-table-wrap { margin-top: 9px; border: 1px solid #E3D6C9; border-radius: 8px; overflow: auto; max-height: 252px; background: #fff; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
        .ci-report-table { border-collapse: collapse; width: 100%; min-width: 420px; font-size: .76rem; }
        .ci-report-table thead th { position: sticky; top: 0; z-index: 1; background: #F5E2D0; color: #5B4A42; font-weight: 700; text-align: left; padding: 5px 8px; white-space: nowrap; border-bottom: 1px solid #E3D6C9; }
        .ci-report-table td { padding: 4px 8px; border-bottom: 1px solid #F2EAE1; color: #3A2E28; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; }
        .ci-report-table tbody tr:last-child td { border-bottom: none; }
        .ci-report-table .ci-report-num { text-align: right; font-variant-numeric: tabular-nums; }
        .ci-report-more { font-size: .72rem; color: #8B7B73; margin-top: 6px; }
        .ci-report-actions { flex-wrap: wrap; }
        .ci-done { color: #2e7d32; font-weight: 700; font-size: .82rem; margin-top: 8px; }
        /* Bottom inset keeps the textarea/Send row above the home indicator. */
        .ci-foot { border-top: 1px solid #E3D6C9; padding: 10px 12px; padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)); padding-right: calc(12px + env(safe-area-inset-right, 0px)); background: #FFFDF8; }
        .ci-textarea { width: 100%; resize: none; border: 1px solid #C9B9A9; border-radius: 8px; padding: 8px 10px; font: inherit; font-size: .88rem; color: #3A2E28; min-height: 44px; }
        .ci-textarea:focus { outline: none; border-color: #2E5E86; box-shadow: 0 0 0 .15rem rgba(46,94,134,.15); }
        .ci-foot-row { display: flex; gap: 8px; align-items: flex-end; margin-top: 6px; }
        .ci-send { flex: 0 0 auto; background: #5B4A42; color: #FFF8EF; border: none; border-radius: 8px; padding: 8px 14px; font-weight: 700; cursor: pointer; }
        .ci-send:disabled { background: #C2B5AE; cursor: default; }
        .ci-hint { font-size: .72rem; color: #8B7B73; }
        .ci-typing { align-self: flex-start; color: #8B7B73; font-size: .8rem; font-style: italic; }

        /* ── Persistent launcher (floating badge) ───────────────────────────
           A fixed badge pinned to the upper-right corner, clear of the left
           sidebar (desktop) and the bottom nav (mobile), so Counter
           Intelligence is reachable on every screen. Primary entry point
           (sidebar launcher removed). Opens the same drawer.
           Hides while the drawer (or the mobile "More" sheet) is open. */
        .ci-fab { position: fixed; top: max(16px, env(safe-area-inset-top, 0px)); right: max(16px, env(safe-area-inset-right, 0px)); z-index: 3500; display: inline-flex; flex-direction: row; align-items: center; padding: 0; border: none; background: transparent; cursor: pointer; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; -webkit-tap-highlight-color: transparent; transition: opacity .2s ease, transform .2s ease; }
        .ci-fab.ci-hidden { opacity: 0; transform: scale(.85) translateY(-6px); pointer-events: none; }
        .ci-fab:focus { outline: none; }
        .ci-fab-label { max-width: 0; overflow: hidden; white-space: nowrap; opacity: 0; margin-right: 0; padding: 0; background: #FBF7F1; color: #3D2518; font-size: .82rem; font-weight: 800; line-height: 1; border: 1px solid #E3D6C9; border-radius: 999px; box-shadow: 0 3px 12px rgba(45,35,28,.18); transition: max-width .28s ease, opacity .2s ease, padding .28s ease, margin .28s ease; }
        .ci-fab:hover .ci-fab-label, .ci-fab:focus-visible .ci-fab-label, .ci-fab.ci-fab-intro .ci-fab-label { max-width: 220px; opacity: 1; margin-right: 10px; padding: 9px 14px; }
        .ci-fab-badge { width: 58px; height: 58px; border-radius: 50%; flex: 0 0 auto; display: block; transition: transform .15s ease, box-shadow .15s ease; }
        .ci-fab-badge img { width: 100%; height: 100%; display: block; filter: drop-shadow(0 4px 10px rgba(45,35,28,.30)); transition: filter .15s ease; }
        .ci-fab:hover .ci-fab-badge { transform: scale(1.07); }
        .ci-fab:hover .ci-fab-badge img { filter: drop-shadow(0 7px 16px rgba(45,35,28,.36)); }
        .ci-fab:active .ci-fab-badge { transform: scale(.95); }
        .ci-fab:focus-visible .ci-fab-badge { box-shadow: 0 0 0 3px #FBF7F1, 0 0 0 6px #2E5E86; }
        @media (max-width: 768px) { .ci-fab-badge { width: 52px; height: 52px; } .ci-fab-label { font-size: .8rem; } }
        body.mobile-more-open .ci-fab { opacity: 0; pointer-events: none; }
        /* The post-import template-setup overlay owns the single CI badge while
           it is active — hide this generic fab so two assistants never stack. */
        body.tsc-setup-active .ci-fab { opacity: 0; pointer-events: none; }
        body.tsc-setup-active .ci-drawer-overlay, body.tsc-setup-active .ci-drawer { display: none !important; }
        @media (prefers-reduced-motion: reduce) { .ci-drawer-overlay, .ci-drawer, .ci-fab, .ci-fab-badge, .ci-fab-badge img, .ci-fab-label { transition: none; } .ci-drawer.open.ci-signup-opening { animation: none; } .ci-fab:hover .ci-fab-badge, .ci-fab:active .ci-fab-badge { transform: none; } }
      `}</style>

      {/* Always-on launcher in the upper-right corner. Reachable on every
          screen (web + mobile app), out of the way of the navigation. */}
      <button
        ref={launcherRef}
        type="button"
        className={`ci-fab ${open || suppressed || docked ? 'ci-hidden' : ''} ${introVisible ? 'ci-fab-intro' : ''}`}
        aria-label="Open Counter Intelligence"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="ci-drawer-panel"
        aria-hidden={suppressed || docked}
        tabIndex={open || suppressed || docked ? -1 : 0}
        title="Counter Intelligence — ask, review, or edit in plain English"
        onClick={() => { setIntroVisible(false); setOpen(true); }}
      >
        <span className="ci-fab-label">Counter Intelligence</span>
        <span className="ci-fab-badge">
          <img src="/icons/counter_intel_button_chat_ci_badge.png" alt="" aria-hidden="true" />
        </span>
      </button>

      {/* Hidden picker for the in-chat item import — triggered by the upload card. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={IMPORT_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={onImportFilesChosen}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Hidden picker for the in-chat price update — invoices / price lists. */}
      <input
        ref={priceFileInputRef}
        type="file"
        accept={PRICE_UPDATE_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={onPriceFilesChosen}
        aria-hidden="true"
        tabIndex={-1}
      />

      {!docked && (
        <div className={`ci-drawer-overlay ${open ? 'open' : ''}`} onClick={closeDrawer} />
      )}
      <aside
        ref={drawerRef}
        id="ci-drawer-panel"
        className={`ci-drawer ${open || docked ? 'open' : ''} ${signupOpening ? 'ci-signup-opening' : ''}`}
        role={docked ? 'region' : 'dialog'}
        aria-label="Counter Intelligence"
        aria-hidden={!(open || docked)}
        tabIndex={-1}
        onAnimationEnd={(event) => {
          if (event.animationName === 'ci-signup-drawer-open') finishSignupOpening();
        }}
        onAnimationCancel={finishSignupOpening}
      >
        <div className="ci-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3>Counter Intelligence</h3>
            <div className="ci-sub">Ask, review, or edit in plain English</div>
          </div>
          {messages.length > 0 && (
            <button type="button" className="ci-clear" onClick={clearChat} title="Clear this conversation">Clear</button>
          )}
          {!docked && (
            <button type="button" className="ci-close" aria-label="Close" onClick={closeDrawer}>×</button>
          )}
        </div>

        <div className="ci-list" ref={listRef}>
          {/* Docked, the host surface shows suggestion pills directly beneath
              this window, so the idle state is a short greeting rather than a
              second copy of the "Try:" prompts. */}
          {messages.length === 0 && docked && (
            <div className="ci-msg assistant">What would you like help with?</div>
          )}
          {messages.length === 0 && !docked && (
            <div className="ci-empty">
              {/* On the Add Items setup surface the user is brand new and locked
                  to this step, so the welcome speaks to the one job in front of
                  them — getting items in — and the prompts match. */}
              {isSetupSurface() ? (
                SETUP_EMPTY_COPY.map((copy, i) => (
                  <p key={i} className="ci-empty-copy">{copy}</p>
                ))
              ) : (
                <>
                  <p className="ci-empty-copy">
                    Ask Countertop to explain, review, import, or update your setup in plain English.
                  </p>
                  <p className="ci-empty-copy">
                    It can help with inventory items, templates, PARs, vendors, prices, orders, End of Period, and P&amp;L mapping. Any bulk change shows a preview before it saves.
                  </p>
                </>
              )}
              <div className="ci-examples-label">Try:</div>
              <ul className="ci-examples">
                {(isSetupSurface() ? SETUP_TRY_PROMPTS : TRY_PROMPTS).map((p, i) => (
                  <li key={i} onClick={() => { setCommand(p); inputRef.current?.focus(); }}>“{p}”</li>
                ))}
              </ul>
            </div>
          )}

          {messages.map((m, idx) => {
            const isLastMessage = idx === messages.length - 1;
            // The file-picker card has its own layout (a Choose-file button +
            // live progress), not the standard preview-then-apply shape.
            if (m.card && m.card.kind === 'item_import_upload') {
              const c = m.card;
              return (
                <div key={m.id} className="ci-card">
                  <div className="ci-card-title">Import items from a file</div>
                  <div className="ci-card-meta">PDF, Excel/CSV, text, or a photo · item lists, not menus</div>
                  {c.status === 'processing' ? (
                    <div className="ci-import-progress">{c.note || 'Working…'}</div>
                  ) : c.status === 'done' ? (
                    <div className="ci-import-ok">{c.note}</div>
                  ) : (
                    <>
                      {c.status === 'error' && <div className="ci-warn">{c.note}</div>}
                      <div className="ci-card-actions">
                        <button
                          type="button"
                          className="ci-btn ci-btn-primary"
                          disabled={busy}
                          onClick={() => {
                            // The clicked card owns the run, so a second import
                            // later in the chat can't steal this one's progress.
                            uploadCardIdRef.current = m.id;
                            if (fileInputRef.current) fileInputRef.current.click();
                          }}
                        >
                          {c.status === 'error' ? 'Choose another file' : 'Choose a file'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            }
            // The price-update file picker — same shape as the item-import picker,
            // but it drives the invoice/price-list upload.
            if (m.card && m.card.kind === 'price_update_upload') {
              const c = m.card;
              return (
                <div key={m.id} className="ci-card">
                  <div className="ci-card-title">Update prices from an invoice</div>
                  <div className="ci-card-meta">PDF, Excel/CSV, or a photo · a vendor invoice or price list</div>
                  {c.status === 'processing' ? (
                    <div className="ci-import-progress">{c.note || 'Working…'}</div>
                  ) : c.status === 'done' ? (
                    <div className="ci-import-ok">{c.note}</div>
                  ) : (
                    <>
                      {c.status === 'error' && <div className="ci-warn">{c.note}</div>}
                      <div className="ci-card-actions">
                        <button
                          type="button"
                          className="ci-btn ci-btn-primary"
                          disabled={busy}
                          onClick={() => {
                            priceUploadCardIdRef.current = m.id;
                            if (priceFileInputRef.current) priceFileInputRef.current.click();
                          }}
                        >
                          {c.status === 'error' ? 'Choose another file' : 'Choose a file'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            }
            // Setup coaching card — the dashboard's "Setup progress" translated
            // into the chat: progress pills, the remaining steps with their
            // explanations and deep links, and chips that route into the
            // assistant's own flows. Pushed by 'counter-intelligence:setup-coach'.
            if (m.card && m.card.kind === 'setup_coach') {
              const c = m.card;
              const pct = c.total > 0 ? Math.max(4, Math.round((c.completed / c.total) * 100)) : 0;
              return (
                <div key={m.id} className="ci-card">
                  <div className="ci-coach-head">
                    <span className="ci-coach-flag">⚙️ Setup progress</span>
                    <span className="ci-coach-pill">{c.completed} of {c.total} complete</span>
                    <span className="ci-coach-pill ci-coach-pill-warn">{c.remaining} remaining</span>
                  </div>
                  <div className="ci-coach-bar" role="progressbar" aria-valuenow={c.completed} aria-valuemin={0} aria-valuemax={c.total} aria-label="Setup progress">
                    <div className="ci-coach-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="ci-card-meta">Get the most out of Countertop — here’s what’s left, and I can help with each one.</div>
                  {c.steps.map((s) => (
                    <div key={s.key} className="ci-coach-step">
                      <div className="ci-coach-step-title"><span className="ci-coach-dot" aria-hidden="true" />{s.title}</div>
                      {s.description && <div className="ci-coach-step-desc">{s.description}</div>}
                      {s.href && <a className="ci-link" href={s.href}>{s.cta} →</a>}
                    </div>
                  ))}
                  <div className="ci-coach-offer">
                    What can I help you with next? Tap one, or just tell me — e.g. “assign vendors to my items” or “build my Sysco order template”.
                  </div>
                  <div className="ci-chips">
                    {(c.chips || []).map((ch) => (
                      <button key={ch.label} type="button" className="ci-chip" disabled={busy} onClick={() => handleSend(ch.label)}>
                        {ch.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }
            // A rendered report: a self-contained card (summary + preview +
            // download/email/print), no preview-then-apply footer.
            if (m.card && m.card.kind === 'report') {
              return (
                <div key={m.id} className="ci-card ci-report-card">
                  <ReportCardBody card={m.card} />
                </div>
              );
            }
            if (m.card) {
              const c = m.card;
              return (
                <div key={m.id} className="ci-card">
                  {m.text && <div className="ci-card-meta" style={{ marginBottom: 4, fontStyle: 'italic' }}>{m.text}</div>}
                  {c.kind === 'template_create' ? (
                    <TemplateCreateCardBody card={c} messageId={m.id} onPatch={updateMessage} />
                  ) : c.kind === 'par_suggest' ? (
                    <ParSuggestCardBody card={c} messageId={m.id} onPatch={updateMessage} />
                  ) : c.kind === 'template_edit' ? (
                    <>
                      <div className="ci-card-title">Edit template · {c.templateName}</div>
                      <div className="ci-card-meta">{c.affected} item{c.affected === 1 ? '' : 's'} affected</div>
                      {Array.isArray(c.operations) && c.operations.length > 0 && (
                        <ul className="ci-card-items">
                          {c.operations.map((op, i) => (
                            <li key={i}>{op.description || op.type}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : c.kind === 'template_convert' ? (
                    <>
                      <div className="ci-card-title">{c.mode === 'copy' ? 'Copy template' : 'Convert template'} · {c.templateName}</div>
                      <div className="ci-card-meta">
                        {FORMAT_LABEL[c.currentFormat] || 'Template'} → {FORMAT_LABEL[c.newFormat] || 'Template'} · {c.itemCount} item{c.itemCount === 1 ? '' : 's'}
                      </div>
                      <div className="ci-card-meta" style={{ marginTop: 2 }}>
                        {c.newFormat === 'ordering'
                          ? 'Turns on PAR-based ordering (PARs start empty — I can help set them).'
                          : 'Counts totals to value inventory; PARs are removed (no ordering).'}
                      </div>
                    </>
                  ) : c.kind === 'po_edit' ? (
                    <>
                      <div className="ci-card-title">Edit order · {c.poNumber}{c.vendorName ? ` · ${c.vendorName}` : ''}</div>
                      <div className="ci-card-meta">
                        {c.affected} change{c.affected === 1 ? '' : 's'}{c.netDeltaDisplay ? ` · ${c.netDeltaDisplay} to the order total` : ''}
                      </div>
                      {Array.isArray(c.changes) && c.changes.length > 0 && (
                        <ul className="ci-card-items">
                          {c.changes.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : c.kind === 'vendor_edit' ? (
                    <>
                      <div className="ci-card-title">{c.renaming ? 'Rename & update vendor' : 'Edit vendor'} · {c.vendorName}</div>
                      <div className="ci-card-meta">{c.changes.length} field{c.changes.length === 1 ? '' : 's'}{c.willUpdateDrafts ? ' · also updates open drafts' : ''}</div>
                      <ul className="ci-card-items ci-vendor-changes">
                        {c.changes.map((ch, i) => (
                          <li key={ch.field || i}>
                            <span className="ci-vc-label">{ch.label}</span>
                            <span className="ci-vc-old">{ch.old}</span>
                            <span className="ci-vc-arrow">→</span>
                            <span className="ci-vc-new">{ch.new}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : c.kind === 'item_import' ? (
                    <ItemImportCardBody card={c} messageId={m.id} onPatch={updateMessage} />
                  ) : c.kind === 'dedup' ? (
                    <DedupCardBody card={c} messageId={m.id} onPatch={updateMessage} />
                  ) : c.kind === 'price_update' ? (
                    <PriceUpdateCardBody card={c} messageId={m.id} onPatch={updateMessage} />
                  ) : (
                    <>
                      <div className="ci-card-title">Bulk item edit</div>
                      <div className="ci-card-meta">{c.affected} item{c.affected === 1 ? '' : 's'} affected{c.largeWarning ? ' · large change' : ''}</div>
                      {c.sample.length > 0 && (
                        <ul className="ci-card-items">
                          {c.sample.map((row, i) => (
                            <li key={row.id || i}>{row.description || row.item_name || row.name}</li>
                          ))}
                        </ul>
                      )}
                      {Array.isArray(c.pendingCreations) && c.pendingCreations.length > 0 && (
                        <div className="ci-create-note">
                          {c.pendingCreations.map((pc, i) => (
                            <div key={i}>＋ New {pc.type === 'category' ? 'category' : pc.type} <b>“{pc.value}”</b> will be created when you confirm.</div>
                          ))}
                        </div>
                      )}
                      {c.pnlReminder && (
                        <div className="ci-warn">
                          You map P&amp;L categories, so map this new category to a P&amp;L bucket afterward — otherwise it won’t roll up in your Cost of Goods report.
                          <a className="ci-link" href="/store-settings.html#pnl">Open P&amp;L Categories →</a>
                        </div>
                      )}
                    </>
                  )}
                  {Array.isArray(c.warnings) && c.warnings.map((w, i) => <div key={i} className="ci-warn">{w}</div>)}
                  {c.status === 'done' ? (
                    <div className="ci-done">
                      {(c.kind === 'template_create' || (c.kind === 'template_convert' && c.mode === 'copy'))
                        ? 'Created ✓'
                        : c.kind === 'template_convert'
                          ? 'Converted ✓'
                          : c.kind === 'price_update'
                            ? 'Updated ✓'
                            : c.kind === 'dedup'
                              ? 'Archived ✓'
                              : 'Saved ✓'}
                    </div>
                  ) : (
                    <div className="ci-card-actions">
                      <button
                        type="button"
                        className="ci-btn ci-btn-primary"
                        disabled={c.status === 'applying'
                          || ((c.kind === 'template_create' || c.kind === 'par_suggest' || c.kind === 'item_import')
                              && !(Array.isArray(c.items) && c.items.length))
                          || (c.kind === 'price_update' && !actionableRows(c.rows).length)
                          || (c.kind === 'dedup' && !(Array.isArray(c.selectedIds) && c.selectedIds.length))
                          || (c.kind === 'template_create' && !(c.name && c.name.trim()))}
                        onClick={() => applyCard(m.id, c)}
                      >
                        {c.status === 'applying'
                          ? (c.kind === 'template_create' ? 'Creating…' : c.kind === 'template_convert' ? 'Working…' : c.kind === 'price_update' ? 'Updating…' : c.kind === 'dedup' ? 'Deleting…' : 'Saving…')
                          : c.kind === 'template_create'
                            ? 'Create template'
                            : c.kind === 'template_convert'
                              ? (c.mode === 'copy' ? 'Create the copy' : 'Convert template')
                            : c.kind === 'par_suggest'
                              ? `Apply PARs to ${(c.items || []).length} item${(c.items || []).length === 1 ? '' : 's'}`
                              : c.kind === 'vendor_edit'
                                ? 'Save vendor changes'
                                : c.kind === 'po_edit'
                                  ? `Apply ${c.affected} change${c.affected === 1 ? '' : 's'} to the order`
                                  : c.kind === 'item_import'
                                    ? `Save ${(c.items || []).length} item${(c.items || []).length === 1 ? '' : 's'}`
                                    : c.kind === 'dedup'
                                      ? `Delete ${(Array.isArray(c.selectedIds) ? c.selectedIds : []).length} selected item${(Array.isArray(c.selectedIds) ? c.selectedIds : []).length === 1 ? '' : 's'}`
                                    : c.kind === 'price_update'
                                      ? `Approve ${actionableRows(c.rows).length} change${actionableRows(c.rows).length === 1 ? '' : 's'}`
                                      : `Apply to ${c.affected} item${c.affected === 1 ? '' : 's'}`}
                      </button>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <React.Fragment key={m.id}>
                {m.sourceLabel && <div className="ci-msg-src">{m.sourceLabel}</div>}
                <div className={`ci-msg ${m.role} ${m.tone === 'error' ? 'error' : ''}`}>
                  {m.text}
                  {m.link && <a className="ci-link" href={m.link.href}>{m.link.label}</a>}
                </div>
                {/* Quick-reply chips for a clarifying question — only on the latest
                    message so old prompts can't be answered out of order. */}
                {Array.isArray(m.choices) && m.choices.length > 0 && isLastMessage && (
                  <div className="ci-chips">
                    {m.choices.map((ch) => (
                      <button
                        key={ch.key || ch.label}
                        type="button"
                        className="ci-chip"
                        disabled={busy}
                        onClick={() => handleChoice(ch)}
                      >
                        {ch.label}
                      </button>
                    ))}
                  </div>
                )}
                {/* Inline form for collecting structured item details. Category,
                    area, and vendor reuse the store's existing values (dropdown +
                    add-new) so the assistant's guess can be corrected without
                    creating near-duplicates. */}
                {m.form && m.form.kind === 'item_create' && isLastMessage && (
                  <ItemCreateForm
                    initialValues={m.form.fields}
                    facets={m.form.facets}
                    storeId={getStoreId()}
                    busy={busy}
                    onSubmit={submitItemCreateForm}
                    onCancel={cancelItemCreateForm}
                  />
                )}
              </React.Fragment>
            );
          })}

          {busy && <div className="ci-typing">Thinking…</div>}
        </div>

        <div className="ci-foot">
          <textarea
            ref={inputRef}
            className="ci-textarea"
            rows={2}
            placeholder={isVendorSurface() ? 'e.g. Add the contact email sales@vendor.com to this vendor' : 'e.g. Create an ordering template with all the wine'}
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
            aria-label="Counter Intelligence command"
          />
          <div className="ci-foot-row">
            <span className="ci-hint">Enter to send · review before anything saves</span>
            <button type="button" className="ci-send" style={{ marginLeft: 'auto' }} onClick={() => handleSend()} disabled={busy || !command.trim()}>Send</button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default CounterIntelligenceDrawer;
